import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Dimensions,
  PanResponder,
  findNodeHandle,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  UIManager,
  View,
} from 'react-native';
import { crossAlert as Alert_alert } from '../lib/crossAlert';
import { supabase } from '../lib/supabase';
import { useAuth } from '../auth/AuthProvider';
import { useTheme, makeThemedStyles, DISPLAY_FONT, SECTOR_COLOR, radii } from '../v10/theme';
import { Field, Chip, GoldButton, Atmos } from '../v10/ui';
import { LinearGradient } from 'expo-linear-gradient';
import type { ToolProps } from '../olmps/ToolHost';
import { registerDeityForm, unregisterDeityForm } from '../olmps/deityFormRegistry';
import { SECTORS, SECTOR_LABELS, type Sector } from '../domain/olmps';
import { exportOrderToPdf, exportOrderToWord, type OrderExportRow } from '../lib/orderExport';
import { TknePanel } from './tkne/TknePanel';
import { KrnosSchedule } from './KrnosTool';
import { ProjectPipeline } from '../olmps/orderWidgets/ProjectPipeline';
import { STEP_STATUS_META, STEP_STATUS_ORDER, effectiveStatus, type StepStatus } from '../olmps/stepStatus';
import { RichTextEditor } from '../components/RichTextEditor';
import { readNav, writeNav } from '../lib/navPersist';
import { ensureProtocolForStep, openThemisProtocol, consumeTkneFocus } from '../olmps/links/tkneThemis';

// ============================================================================
// FULL-FIDELITY native replica of the web TKNE (D.E.S.I.G.N.) page.
// Schema verified against:
//   - src/app/design/page.tsx           (list, create defaults, delete)
//   - src/app/design/[projectId]/page.tsx (editor: every section + JSON shapes)
//   - src/interfaces/settings.ts        (DesignDocument + nested types)
//
// Column names are snake_case (Postgres); JSON *content* inside each jsonb
// column keeps the exact camelCase field names the web app reads/writes, so
// web and mobile can edit the same `designs` row safely.
//
// EXCLUSIONS (see final report for rationale):
//   1. Storm matrix (`storm_columns`/`storm_rows`) — rendered read-only.
//      Allowed by task spec ("2-D spreadsheet... render read-only").
//   2. TKNE AI panel — allowed by task spec ("AI-assistant integration").
//   3. Embedded full Calendar page (Implementation > Calendar) — this embeds
//      an entire unrelated top-level route/feature with no design-specific
//      data of its own; reproducing it here would require pulling in the
//      whole calendar feature (new deps, new scope). Replaced with a note;
//      `section_toggles.calendar` and `sync_calendar` still round-trip.
//   4. Inline Task-list CRUD (Implementation > Task, "Add as List"/"Add as
//      Cat") — this embeds the separate Tasks feature (its own tables +
//      screens). Replaced with a read-only status line; `sync_list_id` /
//      `sync_category_list_id` / `sync_category_name` still round-trip.
//   5. "Export to Word" — a real .docx encoder isn't in the allowed
//      dependency list. PDF export (same content/section order as web's
//      Word export) IS implemented via expo-print + expo-sharing — see
//      "Export" button in the detail header, backed by src/lib/orderExport.ts.
// ============================================================================

// ---------------------------------------------------------------------------
// Types (mirrors src/interfaces/settings.ts DesignDocument + nested shapes)
// ---------------------------------------------------------------------------

interface DesignObjective {
  id: string;
  text: string;
  how: string;
  howStage: string;
  proof: string;
  proofStage: string;
}

interface DesignCheckItem {
  id: string;
  text: string;
  checked: boolean;
  /** Optional nested sub-steps (used by the Directions section). */
  substeps?: DesignCheckItem[];
  /** Optional due date (yyyy-MM-dd), used by the Directions section. */
  dueDate?: string;
  /** Free-form notes, opened by tapping the row's outline number. */
  notes?: string;
  /** Pending/Started/Completed status (Directions section). Absent → 'P'. */
  status?: StepStatus;
  /** Join key linking this step/substep to its synced KYRS `tasks` row (Directions → KYRS one-way sync). */
  taskId?: string;
  /** THEMIS protocol this step is run as ("Run as protocol"); the protocol's notes carry the back-link. */
  protocolId?: string;
}

interface DesignConstraintItem {
  id: string;
  text: string;
  stageComponent: string;
}

interface ForecastPoint {
  id: string;
  label: string;
  description: string;
}

interface ForecastRewardExposure {
  id: string;
  title: string;
  description: string;
}

interface ForecastOption {
  id: string;
  title: string;
  rewards: ForecastRewardExposure[];
  exposures: ForecastRewardExposure[];
}

interface ForecastConcern {
  id: string;
  title: string;
  options: ForecastOption[];
}

type ForecastStepType = 'start' | 'step' | 'end';

interface ForecastStep {
  id: string;
  type: ForecastStepType;
  title: string;
  description: string;
  concerns: ForecastConcern[];
  points: ForecastPoint[];
  checked?: boolean;
  date?: string;
  status?: string;
  stage?: number;
  notes?: string;
  /** Manual override for this step's drift on ASTRA (0–8 segments). Blank/undefined = auto (see src/olmps/tkneDrift.ts). */
  driftOverride?: number | null;
}

interface EssentialTableCell {
  id: string;
  text: string;
  checked: boolean;
}

interface EssentialTableColumn {
  id: string;
  title: string;
  items: EssentialTableCell[];
}

interface DesignStageTask {
  id: string;
  date: string;
  task: string;
  before: boolean;
  beforeText?: string;
  after: boolean;
  afterText?: string;
}

interface DesignStage {
  id: string;
  title: string;
  objectiveStage: string;
  objectiveProjectId: string;
  tasks: DesignStageTask[];
}

interface StormTableCell {
  id: string;
  text: string;
}

interface StormTableRow {
  id: string;
  cells: StormTableCell[];
}

interface DesignListItem {
  id: string;
  text: string;
  done: boolean;
}

/** A live row from the KYRS `tasks` table, as rendered inline in the Task section. */
interface LinkedTask {
  id: string;
  title: string;
  done: boolean;
  parent_task_id: string | null;
  order: number | null;
}


interface GuideImplementationRow {
  id: string;
  task: string;
  time: string;
}

type GuideResult = '' | 'success' | 'unsuccessful' | 'not_applicable';

interface DesignGuideEntry {
  id: string;
  date: string;
  sessionGoal: string;
  deliverable: string;
  essentials: string;
  forecastStep: number;
  forecastSubStep: number;
  workplanTask: number;
  workplanSubTask: number;
  implementationRows: GuideImplementationRow[];
  guide: string;
  result: GuideResult;
  resultText: string;
  indexUpdate: string;
  upgrades: string;
  techDebt: string;
  concerns: string;
  nextStep: string;
  other: string;
}

interface DesignNoteItem {
  id: string;
  text: string;
  fromGuideEntryId?: string;
}

interface BasicStep {
  id: string;
  text: string;
  checked: boolean;
}

type Toggles = Record<string, boolean>;

// Matches web's DesignGroup (src/interfaces/settings.ts), persisted in
// user_settings.design_groups (jsonb array of { id, name }).
interface DesignGroup {
  id: string;
  name: string;
}

// Group column tints on the TKNE list — cycled by the group's position, so
// neighbouring columns always read as different colours.
const GROUP_TINTS = [
  { bg: '#2563EB', ink: '#ffffff' },
  { bg: '#16A34A', ink: '#ffffff' },
  { bg: '#EA580C', ink: '#ffffff' },
  { bg: '#9333EA', ink: '#ffffff' },
  { bg: '#DC2626', ink: '#ffffff' },
  { bg: '#0D9488', ink: '#ffffff' },
];
const UNGROUPED_TINT = { bg: '#475569', ink: '#ffffff' };
const groupTint = (index: number) => GROUP_TINTS[((index % GROUP_TINTS.length) + GROUP_TINTS.length) % GROUP_TINTS.length];

interface DesignRow {
  id: string;
  project_id: string;
  user_id: string;
  design_group_id: string | null;
  is_active: boolean;
  sector: Sector | null;
  // DB column: designs.drift_gap_days (numeric, default 2). Named to match the
  // snake_case DB column directly (like every other field on this row) so the
  // generic toUpdatePayload()/setField() spread-to-columns save path works
  // without special-casing this field.
  drift_gap_days: number;
  section_toggles: Toggles;
  project_title: string;
  start_date: string;
  end_date: string;
  problem: string;
  user: string;
  objectives: DesignObjective[];
  one_core_feature: string;
  success_looks_like: string;
  exclusions: string;
  definition_of_done: DesignCheckItem[];
  mvp: DesignCheckItem[];
  challenges: DesignConstraintItem[];
  constraints: DesignConstraintItem[];
  risks: DesignConstraintItem[];
  forecast_steps: ForecastStep[];
  essential_columns: EssentialTableColumn[];
  component_columns: EssentialTableColumn[];
  stages: DesignStage[];
  workplans: unknown;
  storm_columns: string[];
  storm_rows: StormTableRow[];
  design_list: DesignListItem[];
  sync_list_id: string;
  sync_category_list_id: string;
  sync_category_name: string;
  sync_calendar: boolean;
  sync_calendar_id: string;
  guide_entries: DesignGuideEntry[];
  process_negatives: DesignNoteItem[];
  index_items: DesignNoteItem[];
  upgrades: DesignNoteItem[];
  additional_notes: string;
  basic_destination: string;
  /** Free-text status note shown under the pipeline diagram at the top. */
  status_update: string;
  basic_essentials: DesignCheckItem[];
  basic_stages: DesignCheckItem[];
  basic_implementation: DesignCheckItem[];
  basic_guide: string;
  basic_notes: string;
  basic_steps: BasicStep[];
  view_mode: 'basic' | 'advanced';
  created_at: string;
  updated_at: string;
}

type CheckField = 'definition_of_done' | 'mvp' | 'basic_essentials' | 'basic_stages' | 'basic_implementation';
type ConstraintField = 'challenges' | 'constraints' | 'risks';
type NoteField = 'process_negatives' | 'index_items' | 'upgrades';
type ColumnsField = 'essential_columns' | 'component_columns';

const SECTION_KEYS = ['destination', 'essentials', 'stages', 'implementation', 'guide', 'notes'] as const;
type SectionKey = (typeof SECTION_KEYS)[number];
// "implementation" and "guide" are presented together as a single "Execution"
// tab/section (purely presentational regroup — same underlying fields/writes).
// ORDER section labels (O·R·D·E·R): Orient · Resources · Directions · Execute
// · Revise. Underlying field keys stay 'destination'/'essentials'/etc — this is
// a presentational rename only.
const SECTION_LABELS: Record<SectionKey, string> = {
  destination: 'Orient',
  essentials: 'Resources',
  stages: 'Directions',
  implementation: 'Execute',
  guide: 'Execute',
  notes: 'Revise',
};

// Each tab/segment carries its ORDER stage color (Orient red → Revise blue),
// matching the Basic-view band palette (ORDER_SECTIONS.color). Values are RGB
// triples so we can derive alternating translucent shades for the accordion.
const SECTION_RGB: Record<SectionKey, [number, number, number]> = {
  destination: [232, 72, 61],   // Orient — red
  essentials: [240, 169, 78],   // Resources — orange
  stages: [233, 205, 70],       // Directions — yellow
  implementation: [143, 165, 50], // Execute — green
  guide: [143, 165, 50],
  notes: [74, 163, 232],        // Revise — blue
};
const SECTION_COLORS: Record<SectionKey, string> = {
  destination: '#E8483D',
  essentials: '#F0A94E',
  stages: '#F4D64E',
  implementation: '#8FA532',
  guide: '#8FA532',
  notes: '#4AA3E8',
};
/** Alternating translucent shade of a stage color for nested accordion rows. */
function stageShade(key: SectionKey, index: number): string {
  const [r, g, b] = SECTION_RGB[key];
  const alpha = index % 2 === 0 ? 0.55 : 0.32; // alternate stronger / lighter
  return `rgba(${r},${g},${b},${alpha})`;
}

// Maps an ORDER_SECTIONS.key (orient/resources/directions/execute/revise —
// the Basic-view band keys) onto the underlying SectionKey used by
// stageShade/SECTION_RGB, so the new "Basic" collapsible bar inside each
// band can reuse the same tint logic as the Advanced sub-section bars.
const SECTION_KEY_BY_ORDER: Record<string, SectionKey> = {
  orient: 'destination',
  resources: 'essentials',
  directions: 'stages',
  execute: 'implementation',
  revise: 'notes',
};

// ORDER staircase — the Basic view's five sections, each a colored bar matching
// the ORACLE ORDER palette (Orient red → Revise blue). `checkField`/`textField`
// map to the same underlying row columns the Advanced view writes.
type BasicTextField = 'basic_destination' | 'basic_guide' | 'basic_notes' | 'status_update';
type OrderSection = {
  key: string;
  label: string;
  color: string;
  /** Translucent wash laid over the frosted panel. */
  tint: string;
  /** Hairline accent rim for the band. */
  rim: string;
  /** Saturated hue for the collapse arrow / add glyph. */
  accent: string;
  textColor: string;
  checkField?: CheckField;
  itemPlaceholder?: string;
  emptyHint?: string;
  textField?: BasicTextField;
  textPlaceholder?: string;
};
// Each ORDER stage keeps its signature hue, but rendered as translucent
// frosted glass: `tint` is a soft wash laid over the atmosphere, `rim` a
// hairline accent, `accent` the saturated hue for the collapse-arrow / +.
// Labels are dark ink on the light tint (readable, not shouty).
// Default columns for the Resources "Essential Table" (the 5 P's).
const DEFAULT_ESSENTIAL_COLUMNS = ['People', 'Places', 'Processes', 'Products', 'Predictions'];

const ORDER_SECTIONS: OrderSection[] = [
  {
    key: 'orient', label: 'Orient',
    color: '#E8483D', tint: 'rgba(232,72,61,0.72)', rim: 'rgba(232,72,61,0.6)', accent: '#c23b31', textColor: '#070b17',
    textField: 'basic_destination',
    textPlaceholder: 'Where are you going? Describe the outcome / destination you’re aiming for and why it matters.',
  },
  {
    key: 'resources', label: 'Resources',
    color: '#F0A94E', tint: 'rgba(240,169,78,0.8)', rim: 'rgba(214,140,46,0.65)', accent: '#c8892f', textColor: '#070b17',
    checkField: 'basic_essentials',
    itemPlaceholder: 'A resource you’ll need — tool, person, skill, info, money, or time…',
    emptyHint: 'Tap + to add a resource you’ll need to reach the destination.',
  },
  {
    key: 'directions', label: 'Directions',
    color: '#F4D64E', tint: 'rgba(233,205,70,0.82)', rim: 'rgba(190,160,40,0.65)', accent: '#a88f28', textColor: '#070b17',
    checkField: 'basic_stages',
    itemPlaceholder: 'A step or stage on the path — the route from here to the destination…',
    emptyHint: 'Tap + to add a step on the path to the destination.',
  },
  {
    key: 'execute', label: 'Execute',
    color: '#8FA532', tint: 'rgba(143,165,50,0.76)', rim: 'rgba(120,140,40,0.62)', accent: '#6f8a2c', textColor: '#070b17',
    checkField: 'basic_implementation',
    itemPlaceholder: 'A concrete task to actually do next…',
    emptyHint: 'Tap + to add a concrete task to work on.',
    textField: 'basic_guide',
    textPlaceholder: 'How the work actually gets done — schedule, method, or notes on execution.',
  },
  {
    key: 'revise', label: 'Revise',
    color: '#4AA3E8', tint: 'rgba(74,163,232,0.72)', rim: 'rgba(47,99,240,0.6)', accent: '#2f63f0', textColor: '#070b17',
    textField: 'basic_notes',
    textPlaceholder: 'What changed? What did you learn, adjust, or need to rethink along the way?',
  },
];

// Info-popup copy for every section/sub-section header bar's "i" icon, keyed
// by the same string used for the section's toggle/collapse key: the five
// ORDER_SECTIONS.key values, each ADVANCED_FEATURES feature key, and a single
// generic 'basic' entry reused by every section's Basic block (title is built
// per-section at the call site, e.g. "Basic — Orient").
const SECTION_INFO: Record<string, { title: string; body: string }> = {
  orient: {
    title: 'Orient — Where am I going?',
    body: 'Name the problem, who it\'s for, and what you\'re actually trying to achieve. Set objectives, success criteria, an MVP, and what\'s explicitly out of scope. This is also where the forecast timeline (START → steps → FINISH) lives.',
  },
  resources: {
    title: 'Resources — What do I need?',
    body: 'List what has to be in place to get there — people, places, products, processes, predictions. The Essential Table holds the structured 5-P breakdown; Components hold anything else you\'re assembling.',
  },
  directions: {
    title: 'Directions — How will I get there?',
    body: 'Break the path into stages/phases, each with its own tasks. This section also drives the Calendar (date-linked view of stages), Storm (free brainstorming grid), and List (flat task checklist synced to the tasks table).',
  },
  execute: {
    title: 'Execute — Getting there',
    body: 'The working checklist of concrete tasks to actually do next, plus the Metis-Order guide — a per-session log of goal / result / next-step that keeps recursive planning honest as you go.',
  },
  revise: {
    title: 'Revise — What changed?',
    body: 'Capture what you learned along the way: process negatives, an index of reference items, upgrades to fold back in, and any additional freeform notes. Logs shows the activity history for the project.',
  },
  generalInfo: {
    title: 'General Information',
    body: 'Free-form context that supports Orient — background, notes, anything that doesn\'t fit the structured objective/MVP fields but is still worth capturing about where this project is headed.',
  },
  constraints: {
    title: 'Constraints',
    body: 'Hard limits and blockers — things you can\'t change and have to plan around: budget, time, dependencies, rules. Keep this distinct from risks or challenges; it\'s the stuff that\'s fixed.',
  },
  forecast: {
    title: 'Forecast',
    body: 'The START → steps → FINISH timeline for the project. Each middle step can carry its own description, concerns, and status, and the current step is auto-detected from what\'s checked off.',
  },
  essentialTable: {
    title: 'Essential Table',
    body: 'The structured 5-P table — People, Places, Products, Processes, Predictions — for what you need to reach the destination. This is the advanced, spreadsheet-style version of the Resources checklist.',
  },
  calendar: {
    title: 'Calendar',
    body: 'A date-linked view of the Directions stages — see where each stage falls on the calendar. Syncs one-way against `sync_calendar_id` so the dates stay consistent with the rest of the app.',
  },
  storm: {
    title: 'Storm',
    body: 'A free brainstorming grid — no fixed structure, just a spreadsheet for dumping and organizing ideas before they become formal stages or tasks.',
  },
  list: {
    title: 'List',
    body: 'A flat task checklist that syncs to the shared tasks table (`sync_list_id`). Use this when you want the Directions steps to show up alongside your other lists, not just inside this project.',
  },
  task: {
    title: 'Task',
    body: 'The structured task list for this section — a more detailed view than the flat List, for tracking individual items with their own status as work gets done.',
  },
  guide: {
    title: 'Metis-Order',
    body: 'A per-session recursive planning log: each entry records the goal for that session, what actually happened, and the next step. It\'s how Execute stays a living plan instead of a one-time checklist.',
  },
  notesDetail: {
    title: 'Notes (Process / Index / Upgrades)',
    body: 'Three lists that capture what changed: process negatives (what didn\'t work), index items (things worth remembering/referencing later), and upgrades (improvements to carry into the next pass).',
  },
  logs: {
    title: 'Logs',
    body: 'A read-only activity history for this project — a running record of what happened and when, useful for reviewing progress without digging through every section.',
  },
  basic: {
    title: 'Basic',
    body: 'The simplified version of this section\'s fields — just the core checklist or text field, with none of the advanced sub-features switched on. Turn on advanced features from the section\'s "⋯" menu when you need more structure.',
  },
  processNeg: {
    title: 'Process Negative',
    body: 'What didn\'t work — mistakes, dead ends, or friction in how the project ran. Part of Revise; capturing these is what keeps the same problems from repeating next time.',
  },
  index: {
    title: 'Index',
    body: 'A running list of things worth remembering or referencing later — decisions, definitions, links. Also auto-populated from Metis-Order\'s "Index Update" field.',
  },
  upgradesSection: {
    title: 'Upgrades',
    body: 'Improvements identified along the way that are worth carrying into the next pass — better tools, processes, or approaches. Also auto-populated from Metis-Order\'s "Upgrades" field.',
  },
  additionalNotes: {
    title: 'Additional Notes',
    body: 'A free-form catch-all for anything about what changed that doesn\'t fit Process Negative, Index, or Upgrades.',
  },
};

// ---------------------------------------------------------------------------
// ID / defaults helpers
// ---------------------------------------------------------------------------

function uuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function generateId(): string {
  return `d-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
}

function nowIso(): string {
  return new Date().toISOString();
}

function asArray<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

function defaultForecastSteps(): ForecastStep[] {
  return [
    { id: generateId(), type: 'start', title: 'START', description: '', concerns: [], points: [], checked: false, date: '' },
    { id: generateId(), type: 'step', title: 'Step 1', description: '', concerns: [], points: [], checked: false, date: '' },
    { id: generateId(), type: 'step', title: 'Step 2', description: '', concerns: [], points: [], checked: false, date: '' },
    { id: generateId(), type: 'step', title: 'Step 3', description: '', concerns: [], points: [], checked: false, date: '' },
    { id: generateId(), type: 'step', title: 'Step 4', description: '', concerns: [], points: [], checked: false, date: '' },
    { id: generateId(), type: 'step', title: 'Step 5', description: '', concerns: [], points: [], checked: false, date: '' },
    { id: generateId(), type: 'end', title: 'FINISH', description: '', concerns: [], points: [], checked: false, date: '' },
  ];
}

/** Matches the insert payload built by the web list page's handleAddProject('DESIGN', ...). */
export function buildCreatePayload(userId: string) {
  const id = uuid();
  const now = nowIso();
  return {
    id,
    project_id: id,
    user_id: userId,
    design_group_id: null as string | null,
    is_active: true,
    sector: null as Sector | null,
    drift_gap_days: 2,
    section_toggles: {
      destination: true, essentials: true, stages: true,
      implementation: true, guide: true, notes: true,
      generalInfo: true, constraints: true,
      forecast: true, essentialTable: true, components: true,
    },
    project_title: '',
    start_date: '',
    end_date: '',
    problem: '',
    user: '',
    objectives: [{ id: generateId(), text: '', how: '', howStage: '', proof: '', proofStage: '' }],
    one_core_feature: '',
    success_looks_like: '',
    exclusions: '',
    definition_of_done: Array.from({ length: 3 }, () => ({ id: generateId(), text: '', checked: false })),
    mvp: Array.from({ length: 3 }, () => ({ id: generateId(), text: '', checked: false })),
    challenges: [{ id: generateId(), text: '', stageComponent: '' }],
    constraints: [{ id: generateId(), text: '', stageComponent: '' }],
    risks: [{ id: generateId(), text: '', stageComponent: '' }],
    forecast_steps: defaultForecastSteps(),
    essential_columns: [] as EssentialTableColumn[],
    component_columns: [] as EssentialTableColumn[],
    stages: [] as DesignStage[],
    workplans: [] as unknown[],
    storm_columns: [] as string[],
    storm_rows: [] as StormTableRow[],
    design_list: [] as DesignListItem[],
    sync_list_id: '',
    sync_calendar: false,
    sync_calendar_id: '',
    guide_entries: [] as DesignGuideEntry[],
    process_negatives: [] as DesignNoteItem[],
    index_items: [] as DesignNoteItem[],
    upgrades: [] as DesignNoteItem[],
    additional_notes: '',
    basic_destination: '',
    status_update: '',
    basic_essentials: Array.from({ length: 3 }, () => ({ id: generateId(), text: '', checked: false })),
    basic_stages: Array.from({ length: 3 }, () => ({ id: generateId(), text: '', checked: false })),
    basic_implementation: Array.from({ length: 3 }, () => ({ id: generateId(), text: '', checked: false })),
    basic_guide: '',
    basic_notes: '',
    created_at: now,
    updated_at: now,
  };
}

function normalizeForecastSteps(raw: unknown): ForecastStep[] {
  return asArray<any>(raw).map((s) => ({
    id: s?.id ?? generateId(),
    type: (s?.type === 'start' || s?.type === 'end' ? s.type : 'step') as ForecastStepType,
    title: s?.title ?? '',
    description: s?.description ?? '',
    checked: !!s?.checked,
    date: s?.date ?? '',
    status: s?.status,
    stage: typeof s?.stage === 'number' ? s.stage : undefined,
    notes: s?.notes,
    driftOverride: typeof s?.driftOverride === 'number' ? s.driftOverride : null,
    points: asArray<any>(s?.points).map((p) =>
      typeof p === 'string'
        ? { id: generateId(), label: '', description: p }
        : { id: p?.id ?? generateId(), label: p?.label ?? '', description: p?.description ?? '' }
    ),
    concerns: asArray<any>(s?.concerns).map((c) => ({
      id: c?.id ?? generateId(),
      title: c?.title ?? '',
      options: asArray<any>(c?.options).map((o) => ({
        id: o?.id ?? generateId(),
        title: o?.title ?? '',
        rewards: asArray<any>(o?.rewards).map((r) => ({ id: r?.id ?? generateId(), title: r?.title ?? 'Reward', description: r?.description ?? '' })),
        exposures: asArray<any>(o?.exposures).map((r) => ({ id: r?.id ?? generateId(), title: r?.title ?? 'Exposure', description: r?.description ?? '' })),
      })),
    })),
  }));
}

function normalizeRow(raw: any): DesignRow {
  return {
    id: raw.id,
    project_id: raw.project_id,
    user_id: raw.user_id,
    design_group_id: raw.design_group_id ?? null,
    is_active: raw.is_active ?? true,
    sector: SECTORS.includes(raw.sector) ? (raw.sector as Sector) : null,
    drift_gap_days: typeof raw.drift_gap_days === 'number' ? raw.drift_gap_days : 2,
    section_toggles: raw.section_toggles && typeof raw.section_toggles === 'object' ? raw.section_toggles : {},
    project_title: raw.project_title ?? '',
    start_date: raw.start_date ?? '',
    end_date: raw.end_date ?? '',
    problem: raw.problem ?? '',
    user: raw.user ?? '',
    objectives: asArray<DesignObjective>(raw.objectives),
    one_core_feature: raw.one_core_feature ?? '',
    success_looks_like: raw.success_looks_like ?? '',
    exclusions: raw.exclusions ?? '',
    definition_of_done: asArray<DesignCheckItem>(raw.definition_of_done),
    mvp: asArray<DesignCheckItem>(raw.mvp),
    challenges: asArray<DesignConstraintItem>(raw.challenges),
    constraints: asArray<DesignConstraintItem>(raw.constraints),
    risks: asArray<DesignConstraintItem>(raw.risks),
    forecast_steps: normalizeForecastSteps(raw.forecast_steps),
    essential_columns: asArray<EssentialTableColumn>(raw.essential_columns),
    component_columns: asArray<EssentialTableColumn>(raw.component_columns),
    stages: asArray<DesignStage>(raw.stages),
    workplans: raw.workplans ?? [],
    storm_columns: asArray<string>(raw.storm_columns),
    storm_rows: asArray<StormTableRow>(raw.storm_rows),
    design_list: asArray<DesignListItem>(raw.design_list),
    sync_list_id: raw.sync_list_id ?? '',
    sync_category_list_id: raw.sync_category_list_id ?? '',
    sync_category_name: raw.sync_category_name ?? '',
    sync_calendar: raw.sync_calendar ?? false,
    sync_calendar_id: raw.sync_calendar_id ?? '',
    guide_entries: asArray<DesignGuideEntry>(raw.guide_entries),
    process_negatives: asArray<DesignNoteItem>(raw.process_negatives),
    index_items: asArray<DesignNoteItem>(raw.index_items),
    upgrades: asArray<DesignNoteItem>(raw.upgrades),
    additional_notes: raw.additional_notes ?? '',
    basic_destination: raw.basic_destination ?? '',
    status_update: raw.status_update ?? '',
    basic_essentials: asArray<DesignCheckItem>(raw.basic_essentials),
    basic_stages: asArray<DesignCheckItem>(raw.basic_stages),
    basic_implementation: asArray<DesignCheckItem>(raw.basic_implementation),
    basic_guide: raw.basic_guide ?? '',
    basic_notes: raw.basic_notes ?? '',
    basic_steps: asArray<BasicStep>(raw.basic_steps),
    view_mode: raw.view_mode === 'advanced' ? 'advanced' : 'basic',
    created_at: raw.created_at,
    updated_at: raw.updated_at,
  };
}

function toUpdatePayload(row: DesignRow): Record<string, any> {
  const { id, ...rest } = row;
  return { ...rest, updated_at: nowIso() };
}

function withArrayField<T = any>(row: DesignRow, field: keyof DesignRow, fn: (arr: T[]) => T[]): DesignRow {
  return { ...row, [field]: fn(row[field] as unknown as T[]) } as DesignRow;
}

/**
 * One-way reconciliation: Directions steps (basic_stages) → KYRS `tasks` rows
 * in the currently-selected list (`listId`). Creates/updates/deletes tasks so
 * the list mirrors the steps exactly, then writes new task ids back onto the
 * steps/substeps via the supplied mutators (which re-triggers the caller's
 * effect — the SyncGuard collapses those re-entrant fires into one drain loop).
 */
/** Mutable flag object for the sync re-entrancy guard. */
interface SyncGuard { running: boolean; pending: boolean }

async function syncDirectionsToKyrs(
  userId: string,
  listId: string,
  steps: DesignCheckItem[],
  updateCheckItem: (field: CheckField, id: string, patch: Partial<DesignCheckItem>) => void,
  updateSubstep: (field: CheckField, parentId: string, subId: string, patch: Partial<DesignCheckItem>) => void,
  guard: SyncGuard,
  link?: { designId: string | null; sector: string | null },
): Promise<void> {
  if (!listId) return;
  // Cross-tool link columns (design_id + sector) stamped on every mirrored task.
  const linkCols: Record<string, any> = {};
  if (link?.designId) linkCols.design_id = link.designId;
  if (link?.sector) linkCols.sector = link.sector;
  // Re-entrancy guard. A sync pass writes taskIds back into basic_stages, which
  // re-triggers the effect. If a pass is already running we DON'T drop the new
  // request (that could lose the user's latest edit) — we mark it pending so the
  // running pass re-runs once when it finishes with the freshest state.
  if (guard.running) {
    guard.pending = true;
    return;
  }
  guard.running = true;
  guard.pending = false;
  try {
    const nowIso = new Date().toISOString();

    // Existing task ids in the target list — used both to validate stale
    // taskIds (e.g. after switching lists) and to compute orphans afterward.
    const { data: existingRows, error: fetchErr } = await supabase
      .from('tasks')
      .select('id')
      .eq('list_id', listId)
      .eq('user_id', userId);
    if (fetchErr) {
      console.warn('[TKNE] sync: failed to fetch existing tasks', fetchErr);
      return;
    }
    const existingIds = new Set((existingRows ?? []).map((r: { id: string }) => r.id));

    const referencedIds = new Set<string>();

    for (let index = 0; index < steps.length; index++) {
      const step = steps[index];
      const stepDone = step.status ? step.status === 'C' : !!step.checked;
      // A taskId only "counts" if it actually exists in the target list
      // (handles switching sync_list_id to a different list).
      const validTaskId = step.taskId && existingIds.has(step.taskId) ? step.taskId : undefined;

      let stepTaskId = validTaskId;
      if (!stepTaskId) {
        const { data: inserted, error: insErr } = await supabase
          .from('tasks')
          .insert({
            user_id: userId,
            list_id: listId,
            title: step.text,
            done: stepDone,
            scheduled: step.dueDate ?? null,
            scheduled_manual: !!step.dueDate,
            parent_task_id: null,
            order: index,
            ...linkCols,
            created_at: nowIso,
            updated_at: nowIso,
          })
          .select('id')
          .single();
        if (insErr || !inserted) {
          console.warn('[TKNE] sync: failed to insert step task', insErr);
          continue;
        }
        stepTaskId = (inserted as { id: string }).id;
        updateCheckItem('basic_stages', step.id, { taskId: stepTaskId });
      } else {
        const { error: updErr } = await supabase
          .from('tasks')
          .update({
            title: step.text,
            done: stepDone,
            scheduled: step.dueDate ?? null,
            scheduled_manual: !!step.dueDate,
            order: index,
            ...linkCols,
            updated_at: nowIso,
          })
          .eq('id', stepTaskId)
          .eq('user_id', userId);
        if (updErr) console.warn('[TKNE] sync: failed to update step task', updErr);
        if (step.taskId !== stepTaskId) updateCheckItem('basic_stages', step.id, { taskId: stepTaskId });
      }
      referencedIds.add(stepTaskId);

      const substeps = step.substeps ?? [];
      for (let subIndex = 0; subIndex < substeps.length; subIndex++) {
        const sub = substeps[subIndex];
        const validSubTaskId = sub.taskId && existingIds.has(sub.taskId) ? sub.taskId : undefined;

        let subTaskId = validSubTaskId;
        if (!subTaskId) {
          const { data: insertedSub, error: insSubErr } = await supabase
            .from('tasks')
            .insert({
              user_id: userId,
              list_id: listId,
              title: sub.text,
              done: !!sub.checked,
              scheduled: null,
              scheduled_manual: false,
              parent_task_id: stepTaskId,
              order: subIndex,
              ...linkCols,
              created_at: nowIso,
              updated_at: nowIso,
            })
            .select('id')
            .single();
          if (insSubErr || !insertedSub) {
            console.warn('[TKNE] sync: failed to insert substep task', insSubErr);
            continue;
          }
          subTaskId = (insertedSub as { id: string }).id;
          updateSubstep('basic_stages', step.id, sub.id, { taskId: subTaskId });
        } else {
          const { error: updSubErr } = await supabase
            .from('tasks')
            .update({
              title: sub.text,
              done: !!sub.checked,
              scheduled: null,
              scheduled_manual: false,
              parent_task_id: stepTaskId,
              order: subIndex,
              ...linkCols,
              updated_at: nowIso,
            })
            .eq('id', subTaskId)
            .eq('user_id', userId);
          if (updSubErr) console.warn('[TKNE] sync: failed to update substep task', updSubErr);
          if (sub.taskId !== subTaskId) updateSubstep('basic_stages', step.id, sub.id, { taskId: subTaskId });
        }
        referencedIds.add(subTaskId);
      }
    }

    // NOTE: we intentionally do NOT delete "orphan" tasks (rows in the list not
    // referenced by any current step). The linked list is a shared surface —
    // the user can add their own standalone tasks in the Task section and they
    // must survive Directions syncs. Deleting a Directions *step* removes its
    // specific mirrored task at removal time (see deleteLinkedTasks wiring in
    // removeCheckItem/removeSubstep), so a step deletion still cleans up.
    void referencedIds;
  } catch (e) {
    console.warn('[TKNE] sync: unexpected error', e);
  } finally {
    guard.running = false;
  }
}

function getMiddleSteps(steps: ForecastStep[]): ForecastStep[] {
  return steps.filter((s) => s.type === 'step');
}

/** Clamp a manual drift override into the valid 0–8 segment range (see src/olmps/tkneDrift.ts). */
function clampDriftOverride(value: number): number {
  if (Number.isNaN(value)) return 0;
  return Math.max(0, Math.min(8, value));
}

function getCurrentMiddleStepIndex(mid: ForecastStep[]): number {
  for (let i = mid.length - 1; i >= 0; i--) {
    if (mid[i].checked) return i;
  }
  return 0;
}

function statusLabel(row: DesignRow): string {
  const mid = getMiddleSteps(row.forecast_steps);
  if (mid.length === 0) return 'No forecast steps yet';
  const hasChecked = mid.some((s) => s.checked);
  const allDone = mid.every((s) => s.checked);
  if (allDone) return `All ${mid.length} steps complete`;
  const idx = hasChecked ? getCurrentMiddleStepIndex(mid) : 0;
  const step = mid[idx];
  return `Step ${idx + 1}/${mid.length}: ${step.title || `Step ${idx + 1}`}`;
}

// ---------------------------------------------------------------------------
// Small reusable presentational pieces
// ---------------------------------------------------------------------------

// Small "i"-in-a-circle icon that opens the info popup for the section/
// sub-section it sits next to. Always its own Pressable (never nested text)
// so tapping it never also fires the bar's collapse/expand toggle.
/** Depth for hand-rolled panels: gradient glass fill + inner top highlight
 *  (the host View supplies radius/overflow/hairline). */
function GlassFill({ strong = false }: { strong?: boolean }) {
  const { t } = useTheme();
  return (
    <>
      <LinearGradient
        colors={strong ? [t.glassStrong, t.glassStrong] : t.panelGradient}
        start={{ x: 0.2, y: 0 }}
        end={{ x: 0.8, y: 1 }}
        style={StyleSheet.absoluteFill}
        pointerEvents="none"
      />
      <View pointerEvents="none" style={{ position: 'absolute', top: 0, left: 8, right: 8, height: 1, backgroundColor: t.panelHighlight }} />
    </>
  );
}

function InfoIcon({ onPress, tinted }: { onPress: () => void; tinted?: boolean }) {
  const styles = useStyles();
  return (
    <Pressable
      onPress={(e) => { e.stopPropagation(); onPress(); }}
      hitSlop={10}
      style={[styles.infoIconBtn, tinted ? styles.infoIconBtnTinted : null]}
    >
      <Text style={[styles.infoIconText, tinted ? styles.infoIconTextTinted : null]}>i</Text>
    </Pressable>
  );
}

// Content-only popup (no modal chrome — just the card + a floating close
// pill), used by every info icon in this screen.
function InfoPopup({ info, onClose }: { info: { title: string; body: string } | null; onClose: () => void }) {
  const styles = useStyles();
  return (
    <Modal visible={!!info} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.infoBackdrop} onPress={onClose}>
        <Pressable style={styles.infoCard} onPress={() => {}}>
          <GlassFill strong />
          <Pressable style={styles.infoCloseBtn} onPress={onClose} hitSlop={8}>
            <Text style={styles.infoCloseText}>×</Text>
          </Pressable>
          {info ? (
            <>
              <Text style={styles.infoCardTitle}>{info.title}</Text>
              <Text style={styles.infoCardBody}>{info.body}</Text>
            </>
          ) : null}
        </Pressable>
      </Pressable>
    </Modal>
  );
}

/** A row's outline number ("1", "1.1", "1.4A"). Tapping it opens that row's
 *  notes; once notes exist the number goes bold + underlined so a list shows at
 *  a glance which rows carry them. */
function OutlineNumber({
  label, hasNotes, onPress, textStyle,
}: {
  label: string; hasNotes: boolean; onPress: () => void; textStyle?: any;
}) {
  const styles = useStyles();
  return (
    <Pressable onPress={onPress} hitSlop={8}>
      <Text style={[textStyle, hasNotes ? styles.outlineNumNoted : null]}>{label}</Text>
    </Pressable>
  );
}

/** Notes editor for one row. Content only — no header bar, just the field and a
 *  floating close button, matching InfoPopup. */
function NotesPopup({
  visible, title, value, onChangeText, onClose,
}: {
  visible: boolean; title: string; value: string; onChangeText: (v: string) => void; onClose: () => void;
}) {
  const { c } = useTheme();
  const styles = useStyles();
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.infoBackdrop} onPress={onClose}>
        <Pressable style={styles.infoCard} onPress={() => {}}>
          <GlassFill strong />
          <Pressable style={styles.infoCloseBtn} onPress={onClose} hitSlop={8}>
            <Text style={styles.infoCloseText}>×</Text>
          </Pressable>
          <Text style={styles.infoCardTitle}>{title}</Text>
          <TextInput
            style={styles.notesInput}
            value={value}
            onChangeText={onChangeText}
            placeholder="Notes for this item…"
            placeholderTextColor={c.slate}
            multiline
            autoFocus
          />
        </Pressable>
      </Pressable>
    </Modal>
  );
}

function SectionHeaderBar({ number, title, isOpen, onToggle, onInfoPress }: { number?: number; title: string; isOpen: boolean; onToggle: () => void; onInfoPress?: () => void }) {
  const styles = useStyles();
  return (
    <View style={styles.sectionHeaderBar}>
      <View style={styles.sectionHeaderTitleRow}>
        <Text style={styles.sectionHeaderText}>{number ? `${number}. ${title}` : title}</Text>
        {onInfoPress ? <InfoIcon onPress={onInfoPress} /> : null}
      </View>
      <Switch value={isOpen} onValueChange={onToggle} />
    </View>
  );
}

// Accordion sub-section row. On mobile there's no toggle switch — the whole bar
// is tap-to-expand with a caret. `tint` is the stage color shade for this row
// (callers alternate shades down the section); dark ink text sits on it.
function SubHeaderBar({
  title, isOpen, onToggle, tint, onInfoPress,
}: { title: string; isOpen: boolean; onToggle: () => void; tint?: string; onInfoPress?: () => void }) {
  const styles = useStyles();
  return (
    <Pressable
      onPress={onToggle}
      style={[styles.subHeaderBar, tint ? { backgroundColor: tint } : null]}
    >
      <View style={styles.subHeaderTitleRow}>
        <Text style={[styles.subHeaderText, tint ? styles.subHeaderTextTinted : null]}>{title}</Text>
        {onInfoPress ? <InfoIcon onPress={onInfoPress} tinted={!!tint} /> : null}
      </View>
      <Text style={[styles.subHeaderCaret, tint ? styles.subHeaderTextTinted : null, isOpen ? styles.subHeaderCaretOpen : null]}>◀</Text>
    </Pressable>
  );
}

function SmallButton({ label, onPress, danger }: { label: string; onPress: () => void; danger?: boolean }) {
  const styles = useStyles();
  return (
    <Pressable onPress={onPress} style={styles.smallButton} hitSlop={6}>
      <Text style={[styles.smallButtonText, danger ? styles.dangerText : null]}>{label}</Text>
    </Pressable>
  );
}

function RemoveMark({ onPress }: { onPress: () => void }) {
  const styles = useStyles();
  return (
    <Pressable onPress={onPress} hitSlop={8} style={styles.removeButton}>
      <Text style={styles.removeMark}>×</Text>
    </Pressable>
  );
}

function LabeledInput({
  label, value, onChangeText, placeholder, multiline, keyboardType, onBlur,
}: {
  label: string; value: string; onChangeText: (v: string) => void; placeholder?: string;
  multiline?: boolean; keyboardType?: 'default' | 'number-pad'; onBlur?: () => void;
}) {
  const { c } = useTheme();
  const styles = useStyles();
  return (
    <View style={styles.fieldBlock}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <TextInput
        style={[styles.fieldInput, multiline ? styles.fieldInputMultiline : null]}
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={c.slate}
        multiline={multiline}
        keyboardType={keyboardType}
        onBlur={onBlur}
      />
    </View>
  );
}

/** Metis-Order section header — the O.R.D.E.R word in large bold black, so it
 *  stands out from its normal-weight description. */
function OrderHeader({ word, rest }: { word: string; rest?: string }) {
  const styles = useStyles();
  return (
    <Text style={styles.orderHeaderText}>
      <Text style={styles.orderWord}>{word}</Text>
      {rest ? <Text style={styles.orderRest}>{`  ${rest}`}</Text> : null}
    </Text>
  );
}

/** Anchored dropdown for picking a Directions step (or its sub-step) inside the
 *  Metis-Order session form. Stores a 1-based index (0 = none). */
function StepDropdown({
  placeholder, options, selectedIndex, onSelect,
}: {
  placeholder: string;
  options: string[];
  selectedIndex: number;
  onSelect: (idx: number) => void;
}) {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ x: number; y: number; height: number } | null>(null);
  const boxRef = useRef<View>(null);
  const selectedLabel = selectedIndex >= 1 && selectedIndex <= options.length
    ? `${selectedIndex}. ${options[selectedIndex - 1] || ''}`
    : placeholder;
  const openMenu = () => {
    boxRef.current?.measureInWindow((x, y, _w, height) => { setAnchor({ x, y, height }); setOpen(true); });
  };
  return (
    <>
      <Pressable ref={boxRef} style={styles.stepDdBox} onPress={openMenu} hitSlop={4}>
        <Text style={styles.stepDdText} numberOfLines={1}>{selectedLabel}</Text>
        <Text style={styles.listBoxCaret}>▾</Text>
      </Pressable>
      <Modal visible={open} transparent animationType="none" onRequestClose={() => setOpen(false)}>
        <Pressable style={styles.statusOverlay} onPress={() => setOpen(false)}>
          {anchor ? (
            <View style={[styles.listDropdown, { top: anchor.y + anchor.height + 4, left: anchor.x }]}>
              <Pressable style={styles.listOption} onPress={() => { onSelect(0); setOpen(false); }}>
                <Text style={styles.listOptionText}>None</Text>
              </Pressable>
              {options.map((label, i) => (
                <Pressable
                  key={i}
                  style={[styles.listOption, selectedIndex === i + 1 && styles.statusOptionActive]}
                  onPress={() => { onSelect(i + 1); setOpen(false); }}
                >
                  <Text style={styles.listOptionText} numberOfLines={1}>{i + 1}. {label || `Step ${i + 1}`}</Text>
                </Pressable>
              ))}
            </View>
          ) : null}
        </Pressable>
      </Modal>
    </>
  );
}

/**
 * A compact P/S/C status selector. The menu is rendered in a Modal (so it
 * always paints above sibling rows — plain zIndex loses to later rows in RN)
 * but positioned right under the box via the box's measured screen rect, so it
 * reads as a real anchored dropdown rather than a centered popout.
 */
function StepStatusSelect({ value, onChange }: { value: StepStatus; onChange: (s: StepStatus) => void }) {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ x: number; y: number; height: number } | null>(null);
  const boxRef = useRef<View>(null);
  const meta = STEP_STATUS_META[value];

  const openMenu = () => {
    boxRef.current?.measureInWindow((x, y, _w, height) => {
      setAnchor({ x, y, height });
      setOpen(true);
    });
  };

  return (
    <>
      <Pressable ref={boxRef} style={[styles.statusBox, { backgroundColor: meta.color }]} onPress={openMenu} hitSlop={4}>
        <Text style={styles.statusBoxText}>{value}</Text>
        <Text style={styles.statusBoxCaret}>▾</Text>
      </Pressable>
      <Modal visible={open} transparent animationType="none" onRequestClose={() => setOpen(false)}>
        <Pressable style={styles.statusOverlay} onPress={() => setOpen(false)}>
          {anchor ? (
            <View style={[styles.statusDropdown, { top: anchor.y + anchor.height + 4, left: anchor.x }]}>
              {STEP_STATUS_ORDER.map((s) => {
                const m = STEP_STATUS_META[s];
                return (
                  <Pressable
                    key={s}
                    style={[styles.statusOption, s === value && styles.statusOptionActive]}
                    onPress={() => { onChange(s); setOpen(false); }}
                  >
                    <View style={[styles.statusDot, { backgroundColor: m.color }]}>
                      <Text style={styles.statusDotText}>{s}</Text>
                    </View>
                    <Text style={styles.statusOptionText}>{m.label}</Text>
                  </Pressable>
                );
              })}
            </View>
          ) : null}
        </Pressable>
      </Modal>
    </>
  );
}

/**
 * "List" dropdown for the Directions section — picks which KYRS task list the
 * Directions steps one-way-sync into. Same Modal + measureInWindow anchored-
 * dropdown pattern as StepStatusSelect above.
 */
function ListPicker({
  lists,
  selectedId,
  onSelect,
  onAddList,
}: {
  lists: { id: string; name: string }[];
  selectedId: string;
  onSelect: (id: string) => void;
  onAddList: () => void;
}) {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ x: number; y: number; height: number } | null>(null);
  const boxRef = useRef<View>(null);
  const selectedName = selectedId ? (lists.find((l) => l.id === selectedId)?.name ?? 'None') : 'None';

  const openMenu = () => {
    boxRef.current?.measureInWindow((x, y, _w, height) => {
      setAnchor({ x, y, height });
      setOpen(true);
    });
  };

  return (
    <>
      <Pressable ref={boxRef} style={styles.listBox} onPress={openMenu} hitSlop={4}>
        <Text style={styles.listBoxText}>List: {selectedName}</Text>
        <Text style={styles.listBoxCaret}>▾</Text>
      </Pressable>
      <Modal visible={open} transparent animationType="none" onRequestClose={() => setOpen(false)}>
        <Pressable style={styles.statusOverlay} onPress={() => setOpen(false)}>
          {anchor ? (
            <View style={[styles.listDropdown, { top: anchor.y + anchor.height + 4, left: anchor.x }]}>
              <Pressable
                style={[styles.listOption, selectedId === '' && styles.statusOptionActive]}
                onPress={() => { onSelect(''); setOpen(false); }}
              >
                <Text style={styles.listOptionText}>No list</Text>
              </Pressable>
              {lists.map((l) => (
                <Pressable
                  key={l.id}
                  style={[styles.listOption, selectedId === l.id && styles.statusOptionActive]}
                  onPress={() => { onSelect(l.id); setOpen(false); }}
                >
                  <Text style={styles.listOptionText}>{l.name}</Text>
                </Pressable>
              ))}
              <Pressable
                style={styles.listOption}
                onPress={() => { setOpen(false); onAddList(); }}
              >
                <Text style={styles.listOptionAddText}>＋ Add list</Text>
              </Pressable>
            </View>
          ) : null}
        </Pressable>
      </Modal>
    </>
  );
}

/**
 * Calendar picker for the Task/Calendar section — same anchored-dropdown
 * pattern as ListPicker, but sourced from KRNOS `calendars`. Picks which
 * calendar this design's events live in, or creates a new one inline.
 */
function CalendarPicker({
  calendars,
  selectedId,
  onSelect,
  onAddCalendar,
}: {
  calendars: { id: string; name: string; color: string }[];
  selectedId: string;
  onSelect: (id: string) => void;
  onAddCalendar: () => void;
}) {
  const { c } = useTheme();
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ x: number; y: number; height: number } | null>(null);
  const boxRef = useRef<View>(null);
  const selectedName = selectedId ? (calendars.find((c) => c.id === selectedId)?.name ?? 'None') : 'None';

  const openMenu = () => {
    boxRef.current?.measureInWindow((x, y, _w, height) => {
      setAnchor({ x, y, height });
      setOpen(true);
    });
  };

  return (
    <>
      <Pressable ref={boxRef} style={styles.listBox} onPress={openMenu} hitSlop={4}>
        <Text style={styles.listBoxText}>Calendar: {selectedName}</Text>
        <Text style={styles.listBoxCaret}>▾</Text>
      </Pressable>
      <Modal visible={open} transparent animationType="none" onRequestClose={() => setOpen(false)}>
        <Pressable style={styles.statusOverlay} onPress={() => setOpen(false)}>
          {anchor ? (
            <View style={[styles.listDropdown, { top: anchor.y + anchor.height + 4, left: anchor.x }]}>
              <Pressable
                style={[styles.listOption, selectedId === '' && styles.statusOptionActive]}
                onPress={() => { onSelect(''); setOpen(false); }}
              >
                <Text style={styles.listOptionText}>No calendar</Text>
              </Pressable>
              {calendars.map((c) => (
                <Pressable
                  key={c.id}
                  style={[styles.listOption, selectedId === c.id && styles.statusOptionActive]}
                  onPress={() => { onSelect(c.id); setOpen(false); }}
                >
                  <Text style={styles.listOptionText}>{c.name}</Text>
                </Pressable>
              ))}
              <Pressable
                style={styles.listOption}
                onPress={() => { setOpen(false); onAddCalendar(); }}
              >
                <Text style={styles.listOptionAddText}>＋ Add calendar</Text>
              </Pressable>
            </View>
          ) : null}
        </Pressable>
      </Modal>
    </>
  );
}

// ── Mini month calendar (self-contained; no date-fns) ────────────────────────
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const WEEKDAY_ABBR = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function ymdToDate(ymd?: string): Date | null {
  if (!ymd) return null;
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}
function dateToYmd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function sameLocalDate(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
/** Compact "Jul 3" style label for the due-date chip. */
/** Outline letters for odd sub-step depths: 0→A, 25→Z, 26→AA. Sub-steps
 *  alternate number/letter by depth so the level is readable from the label
 *  alone (1.1 → 1.1A → 1.1A.1 → 1.1A.1A) now that rows are no longer indented. */
function letterLabel(i: number): string {
  let n = i;
  let out = '';
  do {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
}

function shortDateLabel(ymd: string): string {
  const d = ymdToDate(ymd);
  if (!d) return '';
  return `${MONTH_NAMES[d.getMonth()].slice(0, 3)} ${d.getDate()}`;
}

/** A month-grid date picker inside a centered modal. */
function DueDateModal({
  visible, initial, onSelect, onClear, onClose,
}: {
  visible: boolean;
  initial?: string;
  onSelect: (ymd: string) => void;
  onClear: () => void;
  onClose: () => void;
}) {
  const styles = useStyles();
  const initialDate = ymdToDate(initial) ?? new Date();
  const [month, setMonth] = useState<Date>(new Date(initialDate.getFullYear(), initialDate.getMonth(), 1));
  // Re-anchor the visible month whenever the picker is (re)opened.
  useEffect(() => {
    if (visible) {
      const d = ymdToDate(initial) ?? new Date();
      setMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    }
  }, [visible, initial]);

  const year = month.getFullYear();
  const monthIdx = month.getMonth();
  const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
  const leadBlanks = new Date(year, monthIdx, 1).getDay();
  const today = new Date();
  const selected = ymdToDate(initial);
  const cells: (Date | null)[] = [
    ...Array.from({ length: leadBlanks }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => new Date(year, monthIdx, i + 1)),
  ];

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.dueBackdrop} onPress={onClose}>
        <Pressable style={styles.dueCard} onPress={() => {}}>
          <GlassFill strong />
          <View style={styles.calHeaderRow}>
            <Pressable style={styles.calNavBtn} hitSlop={6} onPress={() => setMonth(new Date(year, monthIdx - 1, 1))}>
              <Text style={styles.calNavText}>‹</Text>
            </Pressable>
            <Text style={styles.calMonthLabel}>{MONTH_NAMES[monthIdx]} {year}</Text>
            <Pressable style={styles.calNavBtn} hitSlop={6} onPress={() => setMonth(new Date(year, monthIdx + 1, 1))}>
              <Text style={styles.calNavText}>›</Text>
            </Pressable>
          </View>
          <View style={styles.calWeekdayRow}>
            {WEEKDAY_ABBR.map((d, i) => (
              <Text key={`wd-${i}`} style={styles.calWeekdayText}>{d}</Text>
            ))}
          </View>
          <View style={styles.calGrid}>
            {cells.map((d, i) => {
              if (!d) return <View key={`blank-${i}`} style={styles.calCell} />;
              const isToday = sameLocalDate(d, today);
              const isSelected = selected ? sameLocalDate(d, selected) : false;
              return (
                <Pressable key={d.getTime()} style={styles.calCell} onPress={() => { onSelect(dateToYmd(d)); onClose(); }}>
                  <View style={[styles.calDayCircle, isToday && styles.calDayToday, isSelected && styles.calDaySelected]}>
                    <Text style={[styles.calDayText, isSelected && styles.calDayTextSelected]}>{d.getDate()}</Text>
                  </View>
                </Pressable>
              );
            })}
          </View>
          <View style={styles.dueFooter}>
            <Pressable onPress={() => { onClear(); onClose(); }} hitSlop={6}>
              <Text style={styles.dueClearText}>Clear</Text>
            </Pressable>
            <Pressable onPress={onClose} hitSlop={6}>
              <Text style={styles.dueDoneText}>Done</Text>
            </Pressable>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

/** One option in a sub-step dropdown: `label` is what the menu shows
 *  ("2.1  Build Student section"), `text` is what gets written into the field. */
type SubstepOption = { id: string; label: string; text: string };

/** Text field with a dropdown of every Directions sub-step hanging off its right
 *  edge. The first option keeps whatever the user typed (write your own); the
 *  rest fill the field with that sub-step's text, so an Execute item (or a
 *  Metis-Order session goal) can point at the sub-step it's actually doing. */
function SubstepPickerInput({
  value, onChangeText, options, placeholder, fillStyle,
}: {
  value: string; onChangeText: (v: string) => void; options: SubstepOption[];
  placeholder?: string; fillStyle?: { backgroundColor: string; color: string };
}) {
  const { c } = useTheme();
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ x: number; y: number; height: number; width: number } | null>(null);
  const boxRef = useRef<View>(null);

  const openMenu = () => {
    boxRef.current?.measureInWindow((x, y, width, height) => {
      setAnchor({ x, y, width, height });
      setOpen(true);
    });
  };

  return (
    <View ref={boxRef} style={styles.pickerWrap}>
      <TextInput
        style={[styles.checkRowInput, styles.pickerField, fillStyle]}
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder ?? 'Type here, or pick a sub-step →'}
        placeholderTextColor={c.slate}
      />
      <Pressable onPress={openMenu} hitSlop={10} style={styles.pickerCaretBtn}>
        <Text style={[styles.pickerCaretText, fillStyle ? { color: fillStyle.color } : null]}>▾</Text>
      </Pressable>
      <Modal visible={open} transparent animationType="none" onRequestClose={() => setOpen(false)}>
        <Pressable style={styles.statusOverlay} onPress={() => setOpen(false)}>
          {anchor ? (
            <View
              style={[
                styles.pickerDropdown,
                {
                  top: anchor.y + anchor.height + 4,
                  left: anchor.x,
                  width: anchor.width,
                  // Never run past the bottom of the window — whatever room is
                  // left below the field becomes the scroll area's height.
                  maxHeight: Math.max(
                    140,
                    Dimensions.get('window').height - (anchor.y + anchor.height) - 24,
                  ),
                },
              ]}
            >
              <GlassFill strong />
              <Pressable style={styles.pickerOption} onPress={() => setOpen(false)}>
                <Text style={styles.pickerOptionOwn}>✎  Type your own…</Text>
              </Pressable>
              {options.length === 0 ? (
                <Text style={styles.pickerEmpty}>No sub-steps in Directions yet.</Text>
              ) : (
                <ScrollView
                  style={styles.pickerScroll}
                  nestedScrollEnabled
                  keyboardShouldPersistTaps="handled"
                  showsVerticalScrollIndicator
                >
                  {options.map((o) => (
                    <Pressable
                      key={o.id}
                      style={styles.pickerOption}
                      onPress={() => { onChangeText(o.text); setOpen(false); }}
                    >
                      <Text style={styles.pickerOptionText} numberOfLines={1}>{o.label}</Text>
                    </Pressable>
                  ))}
                </ScrollView>
              )}
            </View>
          ) : null}
        </Pressable>
      </Modal>
    </View>
  );
}

/** A Basic-section item row: status dropdown (Active/Started/Completed) instead
 *  of a checkbox, a text field whose fill reflects that status, optional
 *  sub-step dropdown, and optional reorder arrows. */
function CheckRow({
  status, text, onStatusChange, onChangeText, onRemove, placeholder, index,
  onMove, canMoveUp, canMoveDown, substepOptions, onPressIndex, hasNotes,
}: {
  status: StepStatus; text: string; onStatusChange: (s: StepStatus) => void; onChangeText: (v: string) => void;
  onRemove?: () => void; placeholder?: string; index?: number;
  onMove?: (dir: -1 | 1) => void; canMoveUp?: boolean; canMoveDown?: boolean;
  substepOptions?: SubstepOption[];
  /** Supplied when the row's number should open a notes popup. */
  onPressIndex?: () => void; hasNotes?: boolean;
}) {
  const { c } = useTheme();
  const styles = useStyles();
  const meta = STEP_STATUS_META[status];
  const fillStyle = { backgroundColor: meta.inputFill, color: meta.inputText };
  return (
    <View style={styles.checkRow}>
      <StepStatusSelect value={status} onChange={onStatusChange} />
      {index !== undefined ? (
        onPressIndex ? (
          <OutlineNumber
            label={`${index + 1}.`}
            hasNotes={!!hasNotes}
            onPress={onPressIndex}
            textStyle={styles.indexLabel}
          />
        ) : (
          <Text style={styles.indexLabel}>{index + 1}.</Text>
        )
      ) : null}
      {substepOptions ? (
        <SubstepPickerInput
          value={text}
          onChangeText={onChangeText}
          options={substepOptions}
          placeholder={placeholder}
          fillStyle={fillStyle}
        />
      ) : (
        <TextInput
          style={[styles.checkRowInput, fillStyle]}
          value={text}
          onChangeText={onChangeText}
          placeholder={placeholder}
          placeholderTextColor={c.slate}
        />
      )}
      {onMove ? (
        <View style={styles.reorderCol}>
          <Pressable onPress={() => onMove(-1)} hitSlop={4} disabled={!canMoveUp} style={styles.reorderBtn}>
            <Text style={[styles.reorderText, !canMoveUp && styles.reorderTextDisabled]}>▲</Text>
          </Pressable>
          <Pressable onPress={() => onMove(1)} hitSlop={4} disabled={!canMoveDown} style={styles.reorderBtn}>
            <Text style={[styles.reorderText, !canMoveDown && styles.reorderTextDisabled]}>▼</Text>
          </Pressable>
        </View>
      ) : null}
      {onRemove ? <RemoveMark onPress={onRemove} /> : null}
    </View>
  );
}

function ChipPicker({
  options, selectedId, onSelect, emptyLabel,
}: {
  options: { id: string; label: string }[]; selectedId: string; onSelect: (id: string) => void; emptyLabel?: string;
}) {
  const styles = useStyles();
  if (options.length === 0) {
    return <Text style={styles.mutedText}>{emptyLabel ?? 'No options yet.'}</Text>;
  }
  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.chipRow}>
      {options.map((opt) => {
        const selected = selectedId === opt.id;
        return (
          <Pressable
            key={opt.id}
            onPress={() => onSelect(selected ? '' : opt.id)}
            style={[styles.chip, selected ? styles.chipSelected : null]}
          >
            <Text style={[styles.chipText, selected ? styles.chipTextSelected : null]} numberOfLines={1}>
              {opt.label || '(untitled)'}
            </Text>
          </Pressable>
        );
      })}
    </ScrollView>
  );
}

function SectorPicker({ value, onSelect }: { value: Sector | null; onSelect: (s: Sector | null) => void }) {
  const styles = useStyles();
  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.chipRow}>
      <Pressable
        onPress={() => onSelect(null)}
        style={[styles.sectorChip, value === null ? styles.sectorChipSelectedNone : null]}
      >
        <Text style={[styles.sectorChipText, value === null ? styles.chipTextSelected : null]}>None</Text>
      </Pressable>
      {SECTORS.map((s) => {
        const selected = value === s;
        return (
          <Pressable
            key={s}
            onPress={() => onSelect(selected ? null : s)}
            style={[styles.sectorChip, selected ? { backgroundColor: SECTOR_COLOR[s] } : null]}
          >
            <Text style={[styles.sectorChipText, selected ? styles.chipTextSelected : null]}>{SECTOR_LABELS[s]}</Text>
          </Pressable>
        );
      })}
    </ScrollView>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function TkneTool(_props: ToolProps) {
  const onOpenToolProp = _props.onOpenTool;
  const { t, c } = useTheme();
  const styles = useStyles();
  const { session } = useAuth();
  const userId = session?.user.id;

  const [screen, setScreen] = useState<'list' | 'detail'>('list');
  const [designs, setDesigns] = useState<DesignRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [busy, setBusy] = useState(false);

  // Groups (matches web's design_group_id + settings.designGroups organization).
  const [designGroups, setDesignGroups] = useState<DesignGroup[]>([]);
  const [groupShowInactive, setGroupShowInactive] = useState<Record<string, boolean>>({});
  const [newGroupName, setNewGroupName] = useState('');
  const [groupBusy, setGroupBusy] = useState(false);
  // List screen: which hexagons are expanded (all start collapsed), which
  // group label is being renamed inline, and whether the "+ Group" input is
  // open — optionally on behalf of a project that should join the new group.
  const [expandedDesigns, setExpandedDesigns] = useState<Record<string, boolean>>({});
  const [renamingGroupId, setRenamingGroupId] = useState<string | null>(null);
  const [renameText, setRenameText] = useState('');
  const [addingGroup, setAddingGroup] = useState<{ assignTo: DesignRow | null } | null>(null);
  // "+ Add project" inline form (name + group), and the hexagon whose title is
  // being edited in place (right-click → Rename).
  const [addingProject, setAddingProject] = useState(false);
  const [newProjectGroupId, setNewProjectGroupId] = useState<string | null>(null);
  const [renamingDesignId, setRenamingDesignId] = useState<string | null>(null);

  const [row, setRow] = useState<DesignRow | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeSection, setActiveSection] = useState<SectionKey>('destination');
  const [guideIndex, setGuideIndex] = useState<number>(-1);
  const [propsOpen, setPropsOpen] = useState<Record<string, boolean>>({});
  const [exporting, setExporting] = useState(false);
  // Which Directions row's due-date calendar is open. A plain item id targets
  // a top-level STEP; a composite `parentId:subId` targets a SUB-step, so one
  // modal serves both levels.
  const [dueDateFor, setDueDateFor] = useState<string | null>(null);
  // Which ORDER section's "⋯" feature menu is currently open (null = none).
  const [featMenuOpen, setFeatMenuOpen] = useState<string | null>(null);
  // Info popup: which SECTION_INFO key is currently showing (null = closed).
  // A couple of call sites (per-section "Basic" blocks) need a dynamic title,
  // so this can also hold a { key, title } override pair.
  const [infoOpenKey, setInfoOpenKey] = useState<string | null>(null);
  const [infoOpenTitle, setInfoOpenTitle] = useState<string | null>(null);
  const openInfo = (key: string, titleOverride?: string) => {
    setInfoOpenKey(key);
    setInfoOpenTitle(titleOverride ?? null);
  };
  const closeInfo = () => { setInfoOpenKey(null); setInfoOpenTitle(null); };
  const activeInfo = infoOpenKey && SECTION_INFO[infoOpenKey]
    ? { title: infoOpenTitle ?? SECTION_INFO[infoOpenKey].title, body: SECTION_INFO[infoOpenKey].body }
    : null;

  // Deep-link from a stage/substep tap on the ProjectPipeline card (SyQi
  // snippet or the TKNE list) straight to that step in the Directions
  // editor: scroll it into view and flash a highlight so it's unmistakable.
  const pendingFocusRef = useRef<{ stepId: string; substepId?: string } | null>(null);
  const [highlightedStepKey, setHighlightedStepKey] = useState<string | null>(null);
  const detailScrollRef = useRef<ScrollView | null>(null);
  const stepRowRefs = useRef<Record<string, View | null>>({});

  // Change-log / daily-records entries for this project (Revise → Logs).
  const [designLogs, setDesignLogs] = useState<{ id: string; kind: string; text: string; created_at: string }[]>([]);
  const [newLogNote, setNewLogNote] = useState('');
  // KYRS task lists (Directions "List" dropdown + one-way sync target).
  const [kyrsLists, setKyrsLists] = useState<{ id: string; name: string }[]>([]);
  // Live contents of the linked KYRS list, rendered inline in the Task section
  // (real `tasks` rows — the mirrored Directions steps plus any the user adds).
  const [linkedTasks, setLinkedTasks] = useState<LinkedTask[]>([]);
  const [newTaskText, setNewTaskText] = useState('');
  // KRNOS calendars (Calendar picker → embedded KrnosSchedule scope target).
  const [calendars, setCalendars] = useState<{ id: string; name: string; color: string }[]>([]);
  // Guards the reconciliation effect below against re-triggering itself when
  // it writes taskId back onto basic_stages (which is one of its own deps).
  const syncGuardRef = useRef<SyncGuard>({ running: false, pending: false });

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
  }, []);

  // ----- list loading -----
  const loadList = useCallback(async () => {
    if (!userId) return;
    const { data, error: err } = await supabase
      .from('designs')
      .select('*')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false });
    if (err) {
      setError(err.message);
    } else {
      setError(null);
      setDesigns((data ?? []).map(normalizeRow));
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  // ----- design groups (user_settings.design_groups — see settings-context.tsx) -----
  const loadDesignGroups = useCallback(async () => {
    if (!userId) return;
    const { data, error: err } = await supabase
      .from('user_settings')
      .select('design_groups')
      .eq('user_id', userId)
      .maybeSingle();
    if (err) {
      setError(err.message);
      return;
    }
    setDesignGroups(Array.isArray((data as any)?.design_groups) ? (data as any).design_groups : []);
  }, [userId]);

  useEffect(() => {
    void loadDesignGroups();
  }, [loadDesignGroups]);

  // ----- KYRS task lists (Directions "List" dropdown) -----
  // Loaded once when the detail screen is shown.
  useEffect(() => {
    if (!userId || screen !== 'detail') return;
    (async () => {
      const { data, error: err } = await supabase
        .from('task_lists')
        .select('id, name')
        .eq('user_id', userId)
        .order('created_at', { ascending: true });
      if (!err && data) setKyrsLists(data as { id: string; name: string }[]);
    })();
  }, [userId, screen]);

  const addKyrsList = async () => {
    if (!userId || !row) return;
    const nowIso = new Date().toISOString();
    const { data, error: err } = await supabase
      .from('task_lists')
      .insert({
        user_id: userId,
        name: row.project_title || 'Untitled',
        is_deletable: true,
        include_in_all_tasks_view: true,
        created_at: nowIso,
        updated_at: nowIso,
      })
      .select('id, name')
      .single();
    if (err || !data) {
      console.warn('[TKNE] failed to create KYRS list', err);
      return;
    }
    setKyrsLists((prev) => [...prev, data as { id: string; name: string }]);
    setField('sync_list_id', (data as { id: string; name: string }).id);
  };

  // ----- One-way sync: Directions steps → KYRS task list -----
  // Reconciles the KYRS task list referenced by row.sync_list_id so it mirrors
  // the current Directions (basic_stages) steps/substeps. The SyncGuard drain
  // loop absorbs the taskId writeback (which mutates basic_stages, one of this
  // effect's own deps) so it settles instead of looping forever.
  // Always-fresh handle to the current steps/list, so a re-run after a pending
  // pass writes the LATEST state (not the snapshot the effect closed over).
  const syncSnapshotRef = useRef<{ listId: string; steps: DesignCheckItem[]; designId: string | null; sector: string | null }>({ listId: '', steps: [], designId: null, sector: null });
  syncSnapshotRef.current = { listId: row?.sync_list_id ?? '', steps: row?.basic_stages ?? [], designId: row?.id ?? null, sector: row?.sector ?? null };

  useEffect(() => {
    if (!userId || !row?.sync_list_id) return;
    const timer = setTimeout(() => {
      // runSync is single-flighted by the guard: if a drain loop is already
      // running, just mark pending so it does one more pass with fresh state
      // when it finishes. This makes overlapping effect fires collapse into one
      // sequential drain — no concurrent DB passes, and the last edit always lands.
      if (syncGuardRef.current.running) { syncGuardRef.current.pending = true; return; }
      void (async () => {
        let passes = 0;
        do {
          const snap = syncSnapshotRef.current;
          if (!snap.listId) break;
          await syncDirectionsToKyrs(userId, snap.listId, snap.steps, updateCheckItem, updateSubstep, syncGuardRef.current, { designId: snap.designId, sector: snap.sector });
          passes++;
        } while (syncGuardRef.current.pending && passes < 5);
      })();
    }, 600);
    return () => clearTimeout(timer);
  }, [userId, row?.sync_list_id, row?.basic_stages, row?.sector]);

  // ----- Reverse sync: KYRS task done-toggle → Directions step status -----
  // Subscribes to the synced list's tasks. When a task's `done` flips in KYRS,
  // reflect it back onto the matching step/substep (by taskId). Loop-safe: we
  // only write if the incoming `done` differs from what the step already shows,
  // so the forward sync's own echo (which re-writes the same value) is a no-op.
  useEffect(() => {
    const listId = row?.sync_list_id;
    if (!userId || !listId) return;
    const channel = supabase
      .channel(`tkne-kyrs-${listId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'tasks', filter: `list_id=eq.${listId}` },
        (payload) => {
          const task = payload.new as { id: string; done: boolean };
          const steps = syncSnapshotRef.current.steps;
          for (const step of steps) {
            if (step.taskId === task.id) {
              const stepDone = step.status ? step.status === 'C' : !!step.checked;
              if (stepDone !== task.done) {
                // KYRS has only done/undone: done → Completed, undone → Pending.
                updateCheckItem('basic_stages', step.id, { status: task.done ? 'C' : 'P', checked: task.done });
              }
              return;
            }
            for (const sub of step.substeps ?? []) {
              if (sub.taskId === task.id) {
                if (!!sub.checked !== task.done) {
                  updateSubstep('basic_stages', step.id, sub.id, { checked: task.done });
                }
                return;
              }
            }
          }
        },
      )
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [userId, row?.sync_list_id]);

  // ----- Live linked-list contents (Task section inline list) -----
  // Loads the real `tasks` rows for the linked list and keeps them live via a
  // realtime subscription, so mirrored Directions steps AND manually-added
  // tasks both show up and stay current.
  const loadLinkedTasks = useCallback(async (listId: string) => {
    if (!userId || !listId) { setLinkedTasks([]); return; }
    const { data, error: err } = await supabase
      .from('tasks')
      .select('id, title, done, parent_task_id, order')
      .eq('user_id', userId)
      .eq('list_id', listId);
    if (!err && data) {
      const sorted = [...(data as LinkedTask[])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setLinkedTasks(sorted);
    }
  }, [userId]);

  useEffect(() => {
    const listId = row?.sync_list_id;
    if (!userId || !listId) { setLinkedTasks([]); return; }
    void loadLinkedTasks(listId);
    const channel = supabase
      .channel(`tkne-tasklist-${listId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks', filter: `list_id=eq.${listId}` },
        () => { void loadLinkedTasks(listId); })
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [userId, row?.sync_list_id, loadLinkedTasks]);

  const addLinkedTask = async () => {
    const title = newTaskText.trim();
    const listId = row?.sync_list_id;
    if (!title || !userId || !listId) return;
    setNewTaskText('');
    const nowIso = new Date().toISOString();
    const minOrder = linkedTasks.reduce((m, t) => Math.min(m, t.order ?? 0), 0);
    const { error: err } = await supabase.from('tasks').insert({
      user_id: userId, list_id: listId, title, done: false,
      parent_task_id: null, order: minOrder - 1, created_at: nowIso, updated_at: nowIso,
      ...(row?.id ? { design_id: row.id } : {}), ...(row?.sector ? { sector: row.sector } : {}),
    });
    if (err) setError(err.message);
    await loadLinkedTasks(listId);
  };

  const toggleLinkedTask = async (task: LinkedTask) => {
    if (!userId) return;
    setLinkedTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, done: !t.done } : t)));
    const { error: err } = await supabase.from('tasks')
      .update({ done: !task.done, updated_at: new Date().toISOString() })
      .eq('id', task.id).eq('user_id', userId);
    if (err && row?.sync_list_id) await loadLinkedTasks(row.sync_list_id);
  };

  const renameLinkedTask = async (task: LinkedTask, title: string) => {
    if (!userId) return;
    setLinkedTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, title } : t)));
    await supabase.from('tasks').update({ title, updated_at: new Date().toISOString() })
      .eq('id', task.id).eq('user_id', userId);
  };

  const removeLinkedTask = async (task: LinkedTask) => {
    if (!userId) return;
    setLinkedTasks((prev) => prev.filter((t) => t.id !== task.id));
    await supabase.from('tasks').delete().eq('id', task.id).eq('user_id', userId);
    if (row?.sync_list_id) await loadLinkedTasks(row.sync_list_id);
  };

  // Delete specific mirrored tasks by id (called when a Directions step is removed).
  const deleteLinkedTasks = useCallback(async (taskIds: string[]) => {
    if (!userId || taskIds.length === 0) return;
    await supabase.from('tasks').delete().in('id', taskIds).eq('user_id', userId);
  }, [userId]);

  // ----- KRNOS calendars (Calendar picker + inline month view) -----
  useEffect(() => {
    if (!userId || screen !== 'detail') return;
    (async () => {
      const { data, error: err } = await supabase
        .from('calendars')
        .select('id, name, color')
        .eq('user_id', userId)
        .order('created_at', { ascending: true });
      if (!err && data) setCalendars(data as { id: string; name: string; color: string }[]);
    })();
  }, [userId, screen]);

  // ----- Change-log / daily-records (Revise → Logs) -----
  const loadDesignLogs = useCallback(async () => {
    if (!userId || !row?.id) return;
    const { data, error: err } = await supabase
      .from('design_logs')
      .select('id, kind, text, created_at')
      .eq('design_id', row.id)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(300);
    if (!err && data) setDesignLogs(data as { id: string; kind: string; text: string; created_at: string }[]);
  }, [userId, row?.id]);

  useEffect(() => {
    if (!userId || !row?.id || screen !== 'detail') return;
    void loadDesignLogs();
  }, [userId, row?.id, screen, loadDesignLogs]);

  const addDesignLogNote = async () => {
    const text = newLogNote.trim();
    if (!text || !userId || !row?.id) return;
    setNewLogNote('');
    const { error: err } = await supabase.from('design_logs').insert({
      user_id: userId, design_id: row.id, kind: 'note', text,
    });
    if (err) setError(err.message);
    await loadDesignLogs();
  };

  const addCalendar = async () => {
    if (!userId || !row) return;
    const { data, error: err } = await supabase
      .from('calendars')
      .insert({ user_id: userId, name: row.project_title || 'Untitled', color: '#4a6d8c' })
      .select('id, name, color')
      .single();
    if (err || !data) { console.warn('[TKNE] failed to create calendar', err); return; }
    setCalendars((prev) => [...prev, data as { id: string; name: string; color: string }]);
    setField('sync_calendar_id', (data as { id: string }).id);
  };

  // Narrow update: only the design_groups column, never a full-row upsert.
  const saveDesignGroups = async (next: DesignGroup[]) => {
    setDesignGroups(next);
    const { error: err } = await supabase
      .from('user_settings')
      .update({ design_groups: next })
      .eq('user_id', userId!);
    if (err) {
      setError(err.message);
      await loadDesignGroups();
      return false;
    }
    return true;
  };

  const createDesignGroup = async () => {
    const name = newGroupName.trim();
    if (!name || !userId || groupBusy) return;
    setGroupBusy(true);
    const group = { id: `dg-${Date.now()}`, name };
    const assignTo = addingGroup?.assignTo ?? null;
    setNewGroupName('');
    setAddingGroup(null);
    const ok = await saveDesignGroups([...designGroups, group]);
    if (ok && assignTo) await setDesignGroupForItem(assignTo, group.id);
    setGroupBusy(false);
  };

  const commitRenameGroup = async () => {
    const id = renamingGroupId;
    const name = renameText.trim();
    setRenamingGroupId(null);
    if (!id || !name || !userId) return;
    await saveDesignGroups(designGroups.map((g) => (g.id === id ? { ...g, name } : g)));
  };

  // Deleting a group never deletes its projects — they fall back to Ungrouped.
  const deleteDesignGroup = (group: DesignGroup) => {
    const members = designs.filter((d) => d.design_group_id === group.id);
    Alert_alert(
      `Delete group "${group.name}"?`,
      members.length ? `Its ${members.length} project${members.length === 1 ? '' : 's'} will move to Ungrouped.` : undefined,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            if (!userId) return;
            setDesigns((prev) => prev.map((d) => (d.design_group_id === group.id ? { ...d, design_group_id: null } : d)));
            if (members.length) {
              const { error: err } = await supabase
                .from('designs')
                .update({ design_group_id: null, updated_at: nowIso() })
                .eq('user_id', userId)
                .eq('design_group_id', group.id);
              if (err) { setError(err.message); await loadList(); return; }
            }
            await saveDesignGroups(designGroups.filter((g) => g.id !== group.id));
          },
        },
      ],
    );
  };

  const openGroupHeaderMenu = (group: DesignGroup) => {
    Alert_alert(group.name, undefined, [
      { text: 'Rename', onPress: () => { setRenameText(group.name); setRenamingGroupId(group.id); } },
      { text: 'Add project here', onPress: () => startAddProject(group.id) },
      { text: 'Delete group', style: 'destructive', onPress: () => deleteDesignGroup(group) },
      { text: 'Cancel', style: 'cancel' },
    ]);
  };

  const setDesignGroupForItem = async (item: DesignRow, groupId: string | null) => {
    setDesigns((prev) => prev.map((d) => (d.id === item.id ? { ...d, design_group_id: groupId } : d)));
    const { error: err } = await supabase
      .from('designs')
      .update({ design_group_id: groupId, updated_at: nowIso() })
      .eq('id', item.id)
      .eq('user_id', userId!);
    if (err) {
      setError(err.message);
      await loadList();
    }
  };

  const openGroupPicker = (item: DesignRow) => {
    const options: { text: string; style?: 'cancel' | 'destructive'; onPress?: () => void }[] = [
      { text: 'Ungrouped', onPress: () => void setDesignGroupForItem(item, null) },
      ...designGroups.map((g) => ({ text: g.name, onPress: () => void setDesignGroupForItem(item, g.id) })),
      { text: '+ New group…', onPress: () => { setNewGroupName(''); setAddingGroup({ assignTo: item }); } },
      { text: 'Cancel', style: 'cancel' as const },
    ];
    Alert_alert('Assign to Group', item.project_title || 'Untitled Order', options);
  };

  const openDesignMenu = (item: DesignRow) => {
    const groupName = item.design_group_id
      ? designGroups.find((g) => g.id === item.design_group_id)?.name ?? null
      : null;
    Alert_alert(item.project_title || 'Untitled Order', groupName ? `Group: ${groupName}` : 'No group', [
      { text: 'Rename', onPress: () => setRenamingDesignId(item.id) },
      { text: groupName ? `Change Group (${groupName})` : 'Add to Group', onPress: () => openGroupPicker(item) },
      { text: item.is_active !== false ? 'Set Inactive' : 'Set Active', onPress: () => void toggleActive(item) },
      { text: 'Delete', style: 'destructive', onPress: () => deleteDesign(item) },
      { text: 'Cancel', style: 'cancel' },
    ]);
  };

  const groupBuckets = useMemo(() => {
    const buckets: Record<string, DesignRow[]> = {};
    for (const g of designGroups) buckets[g.id] = [];
    buckets.ungrouped = [];
    for (const d of designs) {
      const gid = d.design_group_id && buckets[d.design_group_id] ? d.design_group_id : 'ungrouped';
      buckets[gid].push(d);
    }
    return buckets;
  }, [designs, designGroups]);

  // ----- create / delete / toggle active -----
  // From the list's inline form this just drops a new hexagon into its
  // group column (open: false); it no longer jumps to the project page.
  const createDesign = async (groupId: string | null = null, open = true) => {
    const title = newTitle.trim();
    if (!userId || busy) return;
    setBusy(true);
    const payload = buildCreatePayload(userId);
    payload.project_title = title;
    if (groupId) payload.design_group_id = groupId;
    const { data, error: err } = await supabase.from('designs').insert(payload).select().single();
    if (err) {
      setError(err.message);
      setBusy(false);
      return;
    }
    setNewTitle('');
    await loadList();
    setBusy(false);
    if (data && open) openDesign(normalizeRow(data));
  };

  const submitNewProject = async () => {
    if (!newTitle.trim()) return;
    await createDesign(newProjectGroupId, false);
    setAddingProject(false);
  };

  const startAddProject = (groupId: string | null) => {
    setNewTitle('');
    setNewProjectGroupId(groupId);
    setAddingProject(true);
  };

  const renameDesign = async (item: DesignRow, title: string) => {
    setRenamingDesignId(null);
    const next = title.trim();
    if (!userId || next === (item.project_title || '')) return;
    setDesigns((prev) => prev.map((d) => (d.id === item.id ? { ...d, project_title: next } : d)));
    const { error: err } = await supabase
      .from('designs')
      .update({ project_title: next, updated_at: nowIso() })
      .eq('id', item.id)
      .eq('user_id', userId);
    if (err) {
      setError(err.message);
      await loadList();
    }
  };

  const deleteDesign = (item: DesignRow) => {
    Alert_alert('Delete order?', item.project_title || 'Untitled Order', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          setDesigns((prev) => prev.filter((d) => d.id !== item.id));
          if (row?.id === item.id) {
            writeNav('tkne', null);
            setRow(null);
            setScreen('list');
          }
          const { error: err } = await supabase.from('designs').delete().eq('id', item.id).eq('user_id', userId!);
          if (err) {
            setError(err.message);
            await loadList();
          }
        },
      },
    ]);
  };

  const toggleActive = async (item: DesignRow) => {
    setDesigns((prev) => prev.map((d) => (d.id === item.id ? { ...d, is_active: !d.is_active } : d)));
    const { error: err } = await supabase
      .from('designs')
      .update({ is_active: !item.is_active, updated_at: nowIso() })
      .eq('id', item.id)
      .eq('user_id', userId!);
    if (err) {
      setError(err.message);
      await loadList();
    }
  };

  // ----- open a design in the detail editor -----
  // `focus`, when given, deep-links straight to a Directions step (or one of
  // its sub-steps) — used by the ProjectPipeline card's stage/sub-box taps.
  const openDesign = async (item: DesignRow, focus?: { stepId: string; substepId?: string }) => {
    writeNav('tkne', { designId: item.id });
    setDetailLoading(true);
    setScreen('detail');
    setGuideIndex(-1);
    setActiveSection('destination');
    setPropsOpen({});
    pendingFocusRef.current = focus ?? null;
    const { data, error: err } = await supabase.from('designs').select('*').eq('id', item.id).single();
    if (err || !data) {
      setError(err?.message ?? 'Failed to load order.');
      writeNav('tkne', null);
      setScreen('list');
      setDetailLoading(false);
      return;
    }
    const loaded = normalizeRow(data);
    if (focus) {
      // Force the Directions section (and its basic block) open so the
      // target step is actually on screen, even if the user had collapsed it.
      loaded.section_toggles = { ...loaded.section_toggles, basic_directions: true, feat_directions_basic: true };
    }
    setRow(loaded);
    setDetailLoading(false);
  };

  const openDesignAtStep = (item: DesignRow, stepId: string, substepId?: string) =>
    openDesign(item, { stepId, substepId });

  // After a refresh, reopen the project that was open (see lib/navPersist).
  const restoredRef = useRef(false);
  useEffect(() => {
    if (!userId || restoredRef.current) return;
    restoredRef.current = true;
    void readNav<{ designId: string }>('tkne').then((saved) => {
      if (saved?.designId) void openDesign({ id: saved.designId } as DesignRow, consumeTkneFocus() ?? undefined);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Re-fetch the current row from `designs` — used after TKNE writes fields
  // server-side so the ORDER sections reflect the AI's changes.
  const reloadRow = useCallback(async () => {
    const id = row?.id;
    if (!id) return;
    const { data } = await supabase.from('designs').select('*').eq('id', id).single();
    if (data) setRow(normalizeRow(data));
  }, [row?.id]);

  // Consumes `pendingFocusRef` (set by openDesign's `focus` param) once the
  // target row has finished loading: scrolls the step/sub-step into view and
  // briefly highlights it. Runs on every row change, but no-ops instantly
  // once the pending focus has been consumed (ref is cleared immediately).
  useEffect(() => {
    if (screen !== 'detail' || detailLoading || !row) return;
    const focus = pendingFocusRef.current;
    if (!focus) return;
    pendingFocusRef.current = null;
    const key = focus.substepId ? `${focus.stepId}:${focus.substepId}` : focus.stepId;
    // Delay lets the just-forced-open accordion finish laying out before we measure.
    const timer = setTimeout(() => {
      // On web, prefer a fresh `document.getElementById` lookup over the
      // captured ref: `stepRowRefs.current[key]` can be a *stale* DOM node
      // if the row re-rendered between the click and this timeout (the
      // captured node is still non-null and still has `.scrollIntoView`,
      // so it fails SILENTLY — no error, no scroll — which is why this was
      // so hard to pin down). `nativeID` on the row View becomes a real DOM
      // `id` on react-native-web, so re-querying it here always gets the
      // node that's actually attached right now.
      const webId = `tkne-step-${key.split(':').join('-')}`;
      const webTarget = typeof document !== 'undefined' ? document.getElementById(webId) : null;
      const target = webTarget || stepRowRefs.current[key];
      if (target && typeof (target as any).scrollIntoView === 'function') {
        (target as any).scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        // Native branch only — `target` here is always the View ref (the web
        // lookup above returns an HTMLElement, which never reaches this path).
        const scrollHandle = findNodeHandle(detailScrollRef.current);
        const nativeTarget = stepRowRefs.current[key];
        const targetHandle = nativeTarget ? findNodeHandle(nativeTarget) : null;
        if (scrollHandle && targetHandle) {
          UIManager.measureLayout(
            targetHandle,
            scrollHandle,
            () => {},
            (_x, y) => {
              detailScrollRef.current?.scrollTo({ y: Math.max(0, y - 16), animated: true });
            },
          );
        }
      }
      setHighlightedStepKey(key);
      setTimeout(() => setHighlightedStepKey((cur) => (cur === key ? null : cur)), 1800);
    }, 200);
    return () => clearTimeout(timer);
  }, [screen, detailLoading, row]);

  // Same deep-link mechanism as `openDesign`'s `focus` param, but for the
  // in-page ProjectPipeline mounted on the detail screen itself: force the
  // Directions section open and queue the focus for the effect above to
  // consume (it re-fires on any `row` update, which `updateRow` triggers).
  const focusDirectionsStep = (stepId: string, substepId?: string) => {
    pendingFocusRef.current = { stepId, substepId };
    updateRow((prev) => ({
      ...prev,
      section_toggles: { ...prev.section_toggles, basic_directions: true, feat_directions_basic: true },
    }));
  };

  const goBack = async () => {
    writeNav('tkne', null);
    setScreen('list');
    setRow(null);
    await loadList();
  };

  // ----- export (mirrors web's exportDesignToWord section content, see
  // src/lib/orderExport.ts). Offers PDF or Word (.doc) like web. -----
  const runExport = async (fn: (r: OrderExportRow) => Promise<void>) => {
    if (!row || exporting) return;
    setExporting(true);
    try {
      await fn(row as unknown as OrderExportRow);
    } catch (e: any) {
      Alert_alert('Export failed', e?.message ?? 'Could not export this order.');
    } finally {
      setExporting(false);
    }
  };
  const exportOrder = () => {
    Alert_alert('Export', 'Choose a format', [
      { text: 'PDF', onPress: () => void runExport(exportOrderToPdf) },
      { text: 'Word (.doc)', onPress: () => void runExport(exportOrderToWord) },
      { text: 'Cancel', style: 'cancel' },
    ]);
  };

  // ----- autosave -----
  const scheduleSave = useCallback((next: DesignRow) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const { error: err } = await supabase
        .from('designs')
        .update(toUpdatePayload(next))
        .eq('id', next.id)
        .eq('user_id', userId!);
      if (err) setError(err.message);
    }, 800);
  }, [userId]);

  const updateRow = useCallback((updater: (prev: DesignRow) => DesignRow) => {
    setRow((prev) => {
      if (!prev) return prev;
      const next = updater(prev);
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  const toggleSection = (key: string) => {
    updateRow((prev) => ({ ...prev, section_toggles: { ...prev.section_toggles, [key]: !prev.section_toggles[key] } }));
  };

  const isOpenStrict = (key: string) => !!row?.section_toggles[key];
  const isOpenDefaultTrue = (key: string) => row?.section_toggles[key] !== false;
  // Toggle relative to the *effective* open state (default-true), so the first
  // tap always flips — plain toggleSection would no-op on an undefined key.
  const toggleBasicSection = (key: string) => {
    const open = row?.section_toggles[key] !== false;
    updateRow((prev) => ({ ...prev, section_toggles: { ...prev.section_toggles, [key]: !open } }));
  };

  // ----- field-level setters -----
  const setField = <K extends keyof DesignRow>(key: K, value: DesignRow[K]) => {
    updateRow((prev) => ({ ...prev, [key]: value }));
  };

  // Deity form-fill adapter (docs/DEITY_AGENT_BACKEND_DESIGN.md §4/§7): only
  // registered while a design is actually open in the detail editor, so
  // Hermes/TKNE's deity can only propose fills for the row the user is
  // looking at. Applying routes through `updateRow`, the exact same path
  // every manual edit on this screen uses (debounced autosave included).
  useEffect(() => {
    registerDeityForm('design', {
      getCurrentValues: () => (screen === 'detail' && row ? (row as unknown as Record<string, unknown>) : undefined),
      applyFill: (fields) => {
        updateRow((prev) => ({ ...prev, ...(fields as Partial<DesignRow>) }));
      },
    });
    return () => unregisterDeityForm('design');
  }, [screen, row, updateRow]);

  // Sector — drives ASTRA; single-select, immediate persist.
  const setSector = (next: Sector | null) => setField('sector', next);

  // Objectives
  const updateObjective = (id: string, patch: Partial<DesignObjective>) =>
    updateRow((prev) => ({ ...prev, objectives: prev.objectives.map((o) => (o.id === id ? { ...o, ...patch } : o)) }));
  const addObjective = () =>
    updateRow((prev) => ({ ...prev, objectives: [...prev.objectives, { id: generateId(), text: '', how: '', howStage: '', proof: '', proofStage: '' }] }));
  const removeObjective = (id: string) =>
    updateRow((prev) => ({ ...prev, objectives: prev.objectives.length > 1 ? prev.objectives.filter((o) => o.id !== id) : prev.objectives }));

  // Check items (definitionOfDone / mvp / basic*)
  const addCheckItem = (field: CheckField) =>
    updateRow((prev) => withArrayField<DesignCheckItem>(prev, field, (arr) => [...arr, { id: generateId(), text: '', checked: false }]));
  const updateCheckItem = (field: CheckField, id: string, patch: Partial<DesignCheckItem>) =>
    updateRow((prev) => withArrayField<DesignCheckItem>(prev, field, (arr) => arr.map((it) => (it.id === id ? { ...it, ...patch } : it))));
  const removeCheckItem = (field: CheckField, id: string) => {
    // Removing a Directions step also deletes its mirrored KYRS task (and its
    // substeps' tasks), so a step deletion propagates instead of leaving a
    // ghost row — the sync itself no longer sweeps orphans (see note there).
    if (field === 'basic_stages' && row?.sync_list_id) {
      const step = row.basic_stages.find((s) => s.id === id);
      const taskIds = [step?.taskId, ...(step?.substeps ?? []).map((s) => s.taskId)].filter(Boolean) as string[];
      if (taskIds.length) void deleteLinkedTasks(taskIds);
    }
    updateRow((prev) => withArrayField<DesignCheckItem>(prev, field, (arr) => arr.filter((it) => it.id !== id)));
  };
  const moveCheckItem = (field: CheckField, id: string, dir: -1 | 1) =>
    updateRow((prev) => withArrayField<DesignCheckItem>(prev, field, (arr) => {
      const idx = arr.findIndex((it) => it.id === id);
      const target = idx + dir;
      if (idx < 0 || target < 0 || target >= arr.length) return arr;
      const next = [...arr];
      const [moved] = next.splice(idx, 1);
      next.splice(target, 0, moved);
      return next;
    }));

  // Nested sub-steps: operate on the `substeps` array of a parent check item.
  // Sub-steps nest to any depth, so every mutation is addressed by a PATH of
  // ancestor ids (top-level step first). `mapAtPath` walks that path and hands
  // `fn` the `substeps` array of the item it lands on.
  const mapAtPath = (
    arr: DesignCheckItem[], path: string[], fn: (subs: DesignCheckItem[]) => DesignCheckItem[],
  ): DesignCheckItem[] =>
    arr.map((it) => {
      if (it.id !== path[0]) return it;
      if (path.length === 1) return { ...it, substeps: fn(it.substeps ?? []) };
      return { ...it, substeps: mapAtPath(it.substeps ?? [], path.slice(1), fn) };
    });

  /** Resolve the item a path points at (used for reading, e.g. its due date). */
  const findAtPath = (arr: DesignCheckItem[], path: string[]): DesignCheckItem | undefined => {
    let list = arr;
    let cur: DesignCheckItem | undefined;
    for (const id of path) {
      cur = list.find((x) => x.id === id);
      if (!cur) return undefined;
      list = cur.substeps ?? [];
    }
    return cur;
  };

  /** Every taskId at or below `item` — so deleting a branch cleans up all of
   *  its synced KYRS tasks, not just the one row being removed. */
  const collectTaskIds = (item: DesignCheckItem): string[] => [
    ...(item.taskId ? [item.taskId] : []),
    ...(item.substeps ?? []).flatMap(collectTaskIds),
  ];

  const addSubstepAt = (field: CheckField, path: string[]) =>
    updateRow((prev) => withArrayField<DesignCheckItem>(prev, field, (arr) =>
      mapAtPath(arr, path, (subs) => [...subs, { id: generateId(), text: '', checked: false }])));
  const updateSubstepAt = (field: CheckField, path: string[], subId: string, patch: Partial<DesignCheckItem>) =>
    updateRow((prev) => withArrayField<DesignCheckItem>(prev, field, (arr) =>
      mapAtPath(arr, path, (subs) => subs.map((s) => (s.id === subId ? { ...s, ...patch } : s)))));
  const removeSubstepAt = (field: CheckField, path: string[], subId: string) => {
    if (field === 'basic_stages' && row?.sync_list_id) {
      const sub = findAtPath(row.basic_stages, [...path, subId]);
      const ids = sub ? collectTaskIds(sub) : [];
      if (ids.length) void deleteLinkedTasks(ids);
    }
    updateRow((prev) => withArrayField<DesignCheckItem>(prev, field, (arr) =>
      mapAtPath(arr, path, (subs) => subs.filter((s) => s.id !== subId))));
  };
  const moveSubstepAt = (field: CheckField, path: string[], subId: string, dir: -1 | 1) =>
    updateRow((prev) => withArrayField<DesignCheckItem>(prev, field, (arr) =>
      mapAtPath(arr, path, (subs) => {
        const idx = subs.findIndex((s) => s.id === subId);
        const target = idx + dir;
        if (idx < 0 || target < 0 || target >= subs.length) return subs;
        const next = [...subs];
        const [moved] = next.splice(idx, 1);
        next.splice(target, 0, moved);
        return next;
      })));

  // Depth-1 wrapper kept for the KYRS task-sync call sites, which only ever
  // touch first-level sub-steps.
  const updateSubstep = (field: CheckField, parentId: string, subId: string, patch: Partial<DesignCheckItem>) =>
    updateSubstepAt(field, [parentId], subId, patch);

  // Every Directions sub-step, flattened, for the Execute-item and Metis-Order
  // dropdowns: label carries the "2.1" numbering for the menu, `text` is what
  // gets written into the field when picked.
  const substepOptions = useMemo<SubstepOption[]>(() => {
    const out: SubstepOption[] = [];
    // Numbering must match renderSubstepTree exactly (even depth → numeric,
    // odd depth → letter) or the menu labels disagree with the rows.
    const walk = (subs: DesignCheckItem[], path: string[], prefix: string, depth: number) => {
      subs.forEach((sub, j) => {
        const num = depth % 2 === 0 ? `${prefix}.${j + 1}` : `${prefix}${letterLabel(j)}`;
        out.push({ id: [...path, sub.id].join(':'), label: `${num}  ${sub.text || '(untitled)'}`, text: sub.text });
        walk(sub.substeps ?? [], [...path, sub.id], num, depth + 1);
      });
    };
    (row?.basic_stages ?? []).forEach((st, i) => walk(st.substeps ?? [], [st.id], `${i + 1}`, 0));
    return out;
  }, [row?.basic_stages]);

  // Status is a rich-text field (toolbar + formatted HTML). If its host
  // WebView/iframe fails to load, editing falls back to a plain TextInput
  // over the raw markup — visible degradation rather than a dead box.
  const [statusEditorFailed, setStatusEditorFailed] = useState(false);

  // Draggable height of the Status box under the diagram. Refs (not state) drive
  // the gesture because PanResponder is created once and would otherwise close
  // over the initial height forever.
  // The editor needs room for its toolbar, so the box starts taller than the
  // old plain textarea did and can't be dragged below the toolbar's height.
  const [statusHeight, setStatusHeight] = useState(300);
  const statusHeightRef = useRef(300);
  const statusDragStartRef = useRef(300);
  useEffect(() => { statusHeightRef.current = statusHeight; }, [statusHeight]);
  // The editor lives in its own document (WebView on native, iframe on web).
  // Once the pointer crosses into that document mid-drag it captures the move
  // events and the gesture dies halfway, so the editor is made
  // pointer-transparent for the duration of the drag.
  const [statusResizing, setStatusResizing] = useState(false);
  const statusResizer = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,
      onPanResponderGrant: () => {
        statusDragStartRef.current = statusHeightRef.current;
        setStatusResizing(true);
      },
      onPanResponderMove: (_e, g) => {
        // 240 is roughly the wrapped toolbar's own height — below that the
        // drag would hide the writing area entirely.
        setStatusHeight(Math.min(900, Math.max(240, statusDragStartRef.current + g.dy)));
      },
      onPanResponderRelease: () => setStatusResizing(false),
      onPanResponderTerminate: () => setStatusResizing(false),
      onPanResponderTerminationRequest: () => false,
    }),
  ).current;

  // Which row's notes popup is open, addressed the same way mutations are: the
  // array it lives in plus the path of ancestor ids down to it.
  const [notesFor, setNotesFor] = useState<{ field: CheckField; path: string[]; label: string } | null>(null);

  // `dueDateFor` is either a step id or a `parentId:subId` composite (sub-step).
  // These two resolve that key for reading the current value and writing a new
  // one, so the single DueDateModal drives both levels.
  const notesTarget = (): DesignCheckItem | undefined =>
    notesFor ? findAtPath(row?.[notesFor.field] ?? [], notesFor.path) : undefined;
  const setNotesOnTarget = (notes: string) => {
    if (!notesFor) return;
    const { field, path } = notesFor;
    if (path.length === 1) updateCheckItem(field, path[0], { notes });
    else updateSubstepAt(field, path.slice(0, -1), path[path.length - 1], { notes });
  };

  const dueDateForTarget = (): DesignCheckItem | undefined =>
    dueDateFor ? findAtPath(row?.basic_stages ?? [], dueDateFor.split(':')) : undefined;
  const setDueDateOnTarget = (patch: Partial<DesignCheckItem>) => {
    if (!dueDateFor) return;
    const path = dueDateFor.split(':');
    if (path.length === 1) updateCheckItem('basic_stages', path[0], patch);
    else updateSubstepAt('basic_stages', path.slice(0, -1), path[path.length - 1], patch);
  };

  // Constraint groups (challenges / constraints / risks)
  const addConstraint = (field: ConstraintField) =>
    updateRow((prev) => withArrayField<DesignConstraintItem>(prev, field, (arr) => [...arr, { id: generateId(), text: '', stageComponent: '' }]));
  const updateConstraint = (field: ConstraintField, id: string, patch: Partial<DesignConstraintItem>) =>
    updateRow((prev) => withArrayField<DesignConstraintItem>(prev, field, (arr) => arr.map((it) => (it.id === id ? { ...it, ...patch } : it))));
  const removeConstraint = (field: ConstraintField, id: string) =>
    updateRow((prev) => withArrayField<DesignConstraintItem>(prev, field, (arr) => arr.filter((it) => it.id !== id)));

  // Note items (processNegatives / indexItems / upgrades)
  const addNoteItem = (field: NoteField) =>
    updateRow((prev) => withArrayField<DesignNoteItem>(prev, field, (arr) => [...arr, { id: generateId(), text: '' }]));
  const updateNoteItem = (field: NoteField, id: string, text: string) =>
    updateRow((prev) => withArrayField<DesignNoteItem>(prev, field, (arr) => arr.map((it) => (it.id === id ? { ...it, text } : it))));
  const removeNoteItem = (field: NoteField, id: string) =>
    updateRow((prev) => withArrayField<DesignNoteItem>(prev, field, (arr) => arr.filter((it) => it.id !== id)));
  const pushNoteIfMissing = (field: NoteField, text: string) =>
    updateRow((prev) => {
      const list = prev[field] as DesignNoteItem[];
      if (!text || list.some((p) => p.text === text)) return prev;
      return withArrayField<DesignNoteItem>(prev, field, (arr) => [...arr, { id: generateId(), text }]);
    });

  // Essential / Component columns
  const updateColumnTitle = (field: ColumnsField, colId: string, title: string) =>
    updateRow((prev) => withArrayField<EssentialTableColumn>(prev, field, (cols) => cols.map((c) => (c.id === colId ? { ...c, title } : c))));
  const updateColumnCell = (field: ColumnsField, colId: string, itemId: string, patch: Partial<EssentialTableCell>) =>
    updateRow((prev) =>
      withArrayField<EssentialTableColumn>(prev, field, (cols) =>
        cols.map((c) => (c.id === colId ? { ...c, items: c.items.map((it) => (it.id === itemId ? { ...it, ...patch } : it)) } : c))
      )
    );
  const addColumnsRow = (field: ColumnsField) =>
    updateRow((prev) =>
      withArrayField<EssentialTableColumn>(prev, field, (cols) => cols.map((c) => ({ ...c, items: [...c.items, { id: generateId(), text: '', checked: false }] })))
    );
  const removeColumnsRow = (field: ColumnsField, rowIdx: number) =>
    updateRow((prev) =>
      withArrayField<EssentialTableColumn>(prev, field, (cols) => cols.map((c) => ({ ...c, items: c.items.filter((_, i) => i !== rowIdx) })))
    );
  const addColumn = (field: ColumnsField) =>
    updateRow((prev) =>
      withArrayField<EssentialTableColumn>(prev, field, (cols) => {
        const rowCount = cols[0]?.items.length ?? 0;
        return [...cols, { id: generateId(), title: '', items: Array.from({ length: rowCount }, () => ({ id: generateId(), text: '', checked: false })) }];
      })
    );
  const removeColumn = (field: ColumnsField, colId: string) =>
    updateRow((prev) => withArrayField<EssentialTableColumn>(prev, field, (cols) => cols.filter((c) => c.id !== colId)));
  // Seed the default P·P·P·P·P columns with one empty row when the table is empty.
  const seedColumns = (field: ColumnsField) =>
    updateRow((prev) =>
      withArrayField<EssentialTableColumn>(prev, field, (cols) =>
        cols.length > 0 ? cols : DEFAULT_ESSENTIAL_COLUMNS.map((title) => ({
          id: generateId(), title, items: [{ id: generateId(), text: '', checked: false }],
        }))
      )
    );

  // Stages
  const updateStage = (id: string, patch: Partial<DesignStage>) =>
    updateRow((prev) => ({ ...prev, stages: prev.stages.map((s) => (s.id === id ? { ...s, ...patch } : s)) }));
  const addStage = () =>
    updateRow((prev) => ({
      ...prev,
      stages: [...prev.stages, {
        id: generateId(), title: '', objectiveStage: '', objectiveProjectId: '',
        tasks: Array.from({ length: 4 }, () => ({ id: generateId(), date: '', task: '', before: false, after: false })),
      }],
    }));
  const removeStage = (id: string) =>
    updateRow((prev) => ({ ...prev, stages: prev.stages.filter((s) => s.id !== id) }));
  const updateStageTask = (stageId: string, taskId: string, patch: Partial<DesignStageTask>) =>
    updateRow((prev) => ({
      ...prev,
      stages: prev.stages.map((s) => (s.id === stageId ? { ...s, tasks: s.tasks.map((t) => (t.id === taskId ? { ...t, ...patch } : t)) } : s)),
    }));
  const addStageTask = (stageId: string) =>
    updateRow((prev) => ({
      ...prev,
      stages: prev.stages.map((s) => (s.id === stageId ? { ...s, tasks: [...s.tasks, { id: generateId(), date: '', task: '', before: false, after: false }] } : s)),
    }));
  const removeStageTask = (stageId: string, taskId: string) =>
    updateRow((prev) => ({
      ...prev,
      stages: prev.stages.map((s) => (s.id === stageId ? { ...s, tasks: s.tasks.filter((t) => t.id !== taskId) } : s)),
    }));

  // Implementation: design list
  const updateDesignListItem = (id: string, patch: Partial<DesignListItem>) =>
    updateRow((prev) => ({ ...prev, design_list: prev.design_list.map((it) => (it.id === id ? { ...it, ...patch } : it)) }));
  const addDesignListItem = () =>
    updateRow((prev) => ({ ...prev, design_list: [...prev.design_list, { id: generateId(), text: '', done: false }] }));
  const removeDesignListItem = (id: string) =>
    updateRow((prev) => ({ ...prev, design_list: prev.design_list.filter((it) => it.id !== id) }));

  // Storm table (editable) — storm_columns: string[], storm_rows: {id, cells:[{id,text}]}[].
  const seedStorm = () =>
    updateRow((prev) => {
      if (prev.storm_columns.length > 0) return prev;
      const columns = ['Column 1', 'Column 2', 'Column 3', 'Column 4'];
      const storm_rows = Array.from({ length: 4 }, () => ({
        id: generateId(),
        cells: columns.map(() => ({ id: generateId(), text: '' })),
      }));
      return { ...prev, storm_columns: columns, storm_rows };
    });
  const updateStormColumnTitle = (index: number, title: string) =>
    updateRow((prev) => {
      const cols = [...prev.storm_columns];
      cols[index] = title;
      return { ...prev, storm_columns: cols };
    });
  const updateStormCell = (rowIdx: number, cellIdx: number, text: string) =>
    updateRow((prev) => {
      const rows = prev.storm_rows.map((r, i) =>
        i === rowIdx ? { ...r, cells: r.cells.map((c, j) => (j === cellIdx ? { ...c, text } : c)) } : r,
      );
      return { ...prev, storm_rows: rows };
    });
  const addStormRow = () =>
    updateRow((prev) => ({
      ...prev,
      storm_rows: [...prev.storm_rows, { id: generateId(), cells: prev.storm_columns.map(() => ({ id: generateId(), text: '' })) }],
    }));
  const removeStormRow = (rowIdx: number) =>
    updateRow((prev) => ({ ...prev, storm_rows: prev.storm_rows.filter((_, i) => i !== rowIdx) }));
  const addStormColumn = () =>
    updateRow((prev) => ({
      ...prev,
      storm_columns: [...prev.storm_columns, `Column ${prev.storm_columns.length + 1}`],
      storm_rows: prev.storm_rows.map((r) => ({ ...r, cells: [...r.cells, { id: generateId(), text: '' }] })),
    }));
  const removeStormColumn = (colIdx: number) =>
    updateRow((prev) => ({
      ...prev,
      storm_columns: prev.storm_columns.filter((_, i) => i !== colIdx),
      storm_rows: prev.storm_rows.map((r) => ({ ...r, cells: r.cells.filter((_, i) => i !== colIdx) })),
    }));

  // Guide entries
  const addGuideEntry = () => {
    if (!row) return;
    const newIndex = row.guide_entries.length;
    updateRow((prev) => {
      const entry: DesignGuideEntry = {
        id: generateId(), date: nowIso().split('T')[0],
        sessionGoal: '', deliverable: '', essentials: '',
        forecastStep: 0, forecastSubStep: 0, workplanTask: 0, workplanSubTask: 0,
        implementationRows: Array.from({ length: 5 }, () => ({ id: generateId(), task: '', time: '' })),
        guide: '', result: '', resultText: '', indexUpdate: '', upgrades: '', techDebt: '', concerns: '', nextStep: '', other: '',
      };
      return { ...prev, guide_entries: [...prev.guide_entries, entry] };
    });
    setGuideIndex(newIndex);
  };
  const updateGuideEntry = (id: string, patch: Partial<DesignGuideEntry>) =>
    updateRow((prev) => ({ ...prev, guide_entries: prev.guide_entries.map((e) => (e.id === id ? { ...e, ...patch } : e)) }));
  const addGuideImplRow = (entryId: string) =>
    updateRow((prev) => ({
      ...prev,
      guide_entries: prev.guide_entries.map((e) => (e.id === entryId ? { ...e, implementationRows: [...e.implementationRows, { id: generateId(), task: '', time: '' }] } : e)),
    }));
  const updateGuideImplRow = (entryId: string, rowId: string, patch: Partial<GuideImplementationRow>) =>
    updateRow((prev) => ({
      ...prev,
      guide_entries: prev.guide_entries.map((e) =>
        e.id === entryId ? { ...e, implementationRows: e.implementationRows.map((r) => (r.id === rowId ? { ...r, ...patch } : r)) } : e
      ),
    }));
  const syncGuideToNotes = (entryId: string) => {
    const entry = row?.guide_entries.find((e) => e.id === entryId);
    if (!entry) return;
    if (entry.indexUpdate) pushNoteIfMissing('index_items', entry.indexUpdate);
    if (entry.upgrades) pushNoteIfMissing('upgrades', entry.upgrades);
    if (entry.result === 'unsuccessful' && entry.resultText) pushNoteIfMissing('process_negatives', entry.resultText);
  };

  // Forecast steps
  const addForecastStep = () =>
    updateRow((prev) => {
      const steps = [...prev.forecast_steps];
      const endIndex = steps.findIndex((s) => s.type === 'end');
      const newStep: ForecastStep = {
        id: generateId(), type: 'step', title: `Step ${steps.filter((s) => s.type === 'step').length + 1}`,
        description: '', concerns: [], points: [],
      };
      if (endIndex >= 0) steps.splice(endIndex, 0, newStep);
      else steps.push(newStep);
      return { ...prev, forecast_steps: steps };
    });
  const updateForecastStep = (id: string, patch: Partial<ForecastStep>) =>
    updateRow((prev) => ({ ...prev, forecast_steps: prev.forecast_steps.map((s) => (s.id === id ? { ...s, ...patch } : s)) }));
  const removeForecastStep = (id: string) =>
    updateRow((prev) => ({ ...prev, forecast_steps: prev.forecast_steps.filter((s) => s.id !== id) }));
  const moveForecastStep = (id: string, dir: -1 | 1) =>
    updateRow((prev) => {
      const steps = [...prev.forecast_steps];
      const idx = steps.findIndex((s) => s.id === id);
      const target = idx + dir;
      if (idx < 0 || target < 0 || target >= steps.length) return prev;
      if (steps[idx].type !== 'step' || steps[target].type !== 'step') return prev;
      const [moved] = steps.splice(idx, 1);
      steps.splice(target, 0, moved);
      return { ...prev, forecast_steps: steps };
    });

  const addForecastPoint = (stepId: string) =>
    updateRow((prev) => ({ ...prev, forecast_steps: prev.forecast_steps.map((s) => (s.id === stepId ? { ...s, points: [...s.points, { id: generateId(), label: '', description: '' }] } : s)) }));
  const updateForecastPoint = (stepId: string, pointId: string, patch: Partial<ForecastPoint>) =>
    updateRow((prev) => ({
      ...prev,
      forecast_steps: prev.forecast_steps.map((s) => {
        if (s.id !== stepId) return s;
        return {
          ...s,
          points: s.points.map((p) => {
            if (p.id !== pointId) return p;
            const next = { ...p, ...patch };
            if (patch.label !== undefined) next.label = patch.label.slice(0, 9);
            return next;
          }),
        };
      }),
    }));
  const removeForecastPoint = (stepId: string, pointId: string) =>
    updateRow((prev) => ({ ...prev, forecast_steps: prev.forecast_steps.map((s) => (s.id === stepId ? { ...s, points: s.points.filter((p) => p.id !== pointId) } : s)) }));

  const addForecastConcern = (stepId: string) =>
    updateRow((prev) => ({ ...prev, forecast_steps: prev.forecast_steps.map((s) => (s.id === stepId ? { ...s, concerns: [...s.concerns, { id: generateId(), title: 'Choice', options: [] }] } : s)) }));
  const updateForecastConcern = (stepId: string, concernId: string, title: string) =>
    updateRow((prev) => ({
      ...prev,
      forecast_steps: prev.forecast_steps.map((s) => (s.id === stepId ? { ...s, concerns: s.concerns.map((c) => (c.id === concernId ? { ...c, title } : c)) } : s)),
    }));
  const removeForecastConcern = (stepId: string, concernId: string) =>
    updateRow((prev) => ({
      ...prev,
      forecast_steps: prev.forecast_steps.map((s) => (s.id === stepId ? { ...s, concerns: s.concerns.filter((c) => c.id !== concernId) } : s)),
    }));

  const addForecastOption = (stepId: string, concernId: string) =>
    updateRow((prev) => ({
      ...prev,
      forecast_steps: prev.forecast_steps.map((s) => {
        if (s.id !== stepId) return s;
        return {
          ...s,
          concerns: s.concerns.map((c) =>
            c.id === concernId ? { ...c, options: [...c.options, { id: generateId(), title: `Option ${c.options.length + 1}`, rewards: [], exposures: [] }] } : c
          ),
        };
      }),
    }));
  const updateForecastOption = (stepId: string, concernId: string, optionId: string, title: string) =>
    updateRow((prev) => ({
      ...prev,
      forecast_steps: prev.forecast_steps.map((s) => {
        if (s.id !== stepId) return s;
        return { ...s, concerns: s.concerns.map((c) => (c.id === concernId ? { ...c, options: c.options.map((o) => (o.id === optionId ? { ...o, title } : o)) } : c)) };
      }),
    }));
  const removeForecastOption = (stepId: string, concernId: string, optionId: string) =>
    updateRow((prev) => ({
      ...prev,
      forecast_steps: prev.forecast_steps.map((s) => {
        if (s.id !== stepId) return s;
        return { ...s, concerns: s.concerns.map((c) => (c.id === concernId ? { ...c, options: c.options.filter((o) => o.id !== optionId) } : c)) };
      }),
    }));

  const addForecastRE = (stepId: string, concernId: string, optionId: string, kind: 'rewards' | 'exposures') =>
    updateRow((prev) => ({
      ...prev,
      forecast_steps: prev.forecast_steps.map((s) => {
        if (s.id !== stepId) return s;
        return {
          ...s,
          concerns: s.concerns.map((c) => {
            if (c.id !== concernId) return c;
            return {
              ...c,
              options: c.options.map((o) =>
                o.id === optionId ? { ...o, [kind]: [...o[kind], { id: generateId(), title: kind === 'rewards' ? 'Reward' : 'Exposure', description: '' }] } : o
              ),
            };
          }),
        };
      }),
    }));
  const updateForecastRE = (stepId: string, concernId: string, optionId: string, kind: 'rewards' | 'exposures', itemId: string, description: string) =>
    updateRow((prev) => ({
      ...prev,
      forecast_steps: prev.forecast_steps.map((s) => {
        if (s.id !== stepId) return s;
        return {
          ...s,
          concerns: s.concerns.map((c) => {
            if (c.id !== concernId) return c;
            return {
              ...c,
              options: c.options.map((o) => (o.id === optionId ? { ...o, [kind]: o[kind].map((item) => (item.id === itemId ? { ...item, description } : item)) } : o)),
            };
          }),
        };
      }),
    }));
  const removeForecastRE = (stepId: string, concernId: string, optionId: string, kind: 'rewards' | 'exposures', itemId: string) =>
    updateRow((prev) => ({
      ...prev,
      forecast_steps: prev.forecast_steps.map((s) => {
        if (s.id !== stepId) return s;
        return {
          ...s,
          concerns: s.concerns.map((c) => {
            if (c.id !== concernId) return c;
            return { ...c, options: c.options.map((o) => (o.id === optionId ? { ...o, [kind]: o[kind].filter((item) => item.id !== itemId) } : o)) };
          }),
        };
      }),
    }));

  // Basic steps
  const addBasicStep = () =>
    updateRow((prev) => ({ ...prev, basic_steps: [...(prev.basic_steps.length ? prev.basic_steps : ensureDefaultBasicSteps()), { id: generateId(), text: '', checked: false }] }));
  const updateBasicStep = (id: string, patch: Partial<BasicStep>, current: BasicStep[]) =>
    updateRow((prev) => ({ ...prev, basic_steps: current.map((s) => (s.id === id ? { ...s, ...patch } : s)) }));
  const removeBasicStep = (id: string, current: BasicStep[]) =>
    updateRow((prev) => ({ ...prev, basic_steps: current.filter((s) => s.id !== id) }));

  function ensureDefaultBasicSteps(): BasicStep[] {
    return Array.from({ length: 3 }, () => ({ id: generateId(), text: '', checked: false }));
  }

  // -------------------------------------------------------------------------
  // Render: LIST screen
  // -------------------------------------------------------------------------

  if (screen === 'list') {
    if (loading) {
      return (
        <View style={styles.center}>
          <ActivityIndicator color={c.blueText} />
        </View>
      );
    }
    return (
      <Atmos style={styles.root}>
        <View style={styles.listTopBar}>
          <GoldButton label="+ Add project" style={styles.listTopBtn} onPress={() => { if (!busy) startAddProject(null); }} />
          <GoldButton
            ghost
            label="+ Add group"
            style={styles.listTopBtn}
            onPress={() => { setNewGroupName(''); setAddingGroup({ assignTo: null }); }}
          />
        </View>
        {addingProject ? (
          <View style={styles.newProjectForm}>
<GlassFill />
            <Field
              value={newTitle}
              onChangeText={setNewTitle}
              onSubmitEditing={() => void submitNewProject()}
              placeholder="Project name…"
              autoFocus
            />
            <View style={styles.groupChipRow}>
              <Text style={styles.groupChipLabel}>Group:</Text>
              {[{ id: null as string | null, name: 'Ungrouped' }, ...designGroups].map((g, i) => {
                const on = newProjectGroupId === g.id;
                const tint = g.id ? groupTint(designGroups.findIndex((x) => x.id === g.id)) : UNGROUPED_TINT;
                return (
                  <Chip
                    key={g.id ?? 'ungrouped'}
                    label={g.name}
                    small
                    active={on}
                    color={tint.bg}
                    onPress={() => setNewProjectGroupId(g.id)}
                  />
                );
              })}
            </View>
            <View style={styles.groupAddRow}>
              <SmallButton label={busy ? 'Adding…' : 'Add'} onPress={() => void submitNewProject()} />
              <SmallButton label="Cancel" onPress={() => setAddingProject(false)} />
            </View>
          </View>
        ) : null}
        {addingGroup ? (
          <View style={styles.groupAddRow}>
            <Field
              style={{ flex: 1 }}
              value={newGroupName}
              onChangeText={setNewGroupName}
              onSubmitEditing={() => void createDesignGroup()}
              placeholder={addingGroup.assignTo ? `New group for "${addingGroup.assignTo.project_title || 'Untitled'}"…` : 'Group name…'}
              autoFocus
            />
            <SmallButton label="Create" onPress={() => void createDesignGroup()} />
            <SmallButton label="Cancel" onPress={() => setAddingGroup(null)} />
          </View>
        ) : null}
        {error ? <Text style={styles.error}>{error}</Text> : null}
        {designs.length === 0 && designGroups.length === 0 ? (
          <Text style={styles.empty}>No projects yet.</Text>
        ) : (
          // Groups are COLUMNS: label at the head, that group's project
          // hexagons stacked beneath. Expanded hexagons widen their column,
          // so both axes scroll.
          <ScrollView style={styles.groupScrollV}>
            <ScrollView horizontal contentContainerStyle={styles.groupColumns}>
              {[
                ...designGroups.map((g) => ({ group: g as DesignGroup | null, key: g.id })),
                ...(groupBuckets.ungrouped.length || designGroups.length === 0 ? [{ group: null, key: 'ungrouped' }] : []),
              ].map(({ group, key }) => {
                const tint = group ? groupTint(designGroups.indexOf(group)) : UNGROUPED_TINT;
                return (
                <View key={key} style={[styles.groupColumn, { backgroundColor: tint.bg }]}>
                  <LinearGradient
                    colors={['rgba(255,255,255,0.20)', 'rgba(255,255,255,0)', 'rgba(0,0,0,0.28)']}
                    style={StyleSheet.absoluteFill}
                    pointerEvents="none"
                  />
                  {group && renamingGroupId === group.id ? (
                    <TextInput
                      style={[styles.fieldInput, styles.groupRenameInput]}
                      value={renameText}
                      onChangeText={setRenameText}
                      onSubmitEditing={() => void commitRenameGroup()}
                      onBlur={() => void commitRenameGroup()}
                      autoFocus
                    />
                  ) : (
                    <Pressable
                      onPress={group ? () => openGroupHeaderMenu(group) : undefined}
                      disabled={!group}
                      style={styles.groupHeader}
                    >
                      <Text style={styles.groupHeaderText} numberOfLines={1}>
                        {group ? group.name.toUpperCase() : 'UNGROUPED'}
                      </Text>
                      {group ? <Text style={styles.groupHeaderDots}>⋯</Text> : null}
                    </Pressable>
                  )}
                  {groupBuckets[key].length === 0 ? (
                    <Text style={[styles.groupEmpty, { color: tint.ink }]}>Empty — right-click a project to move it here</Text>
                  ) : (
                    groupBuckets[key].map((item) => (
                      <ProjectPipeline
                        key={item.id}
                        variant="bare"
                        collapsed={!expandedDesigns[item.id]}
                        onToggleCollapsed={() => setExpandedDesigns((prev) => ({ ...prev, [item.id]: !prev[item.id] }))}
                        project={item}
                        onOpen={() => openDesign(item)}
                        onOpenStage={(stepId) => openDesignAtStep(item, stepId)}
                        onOpenSubstep={(stepId, subId) => openDesignAtStep(item, stepId, subId)}
                        onMenuPress={() => openDesignMenu(item)}
                        editingTitle={renamingDesignId === item.id}
                        onSubmitTitle={(t) => void renameDesign(item, t)}
                      />
                    ))
                  )}
                  <Pressable onPress={() => startAddProject(group ? group.id : null)} style={styles.groupAddProject}>
                    <Text style={[styles.groupAddProjectText, { color: tint.ink }]}>+ Project</Text>
                  </Pressable>
                </View>
                );
              })}
            </ScrollView>
          </ScrollView>
        )}
        <Text style={styles.hint}>Tap to open · double-tap to expand · right-click or long-press for options</Text>
      </Atmos>
    );
  }

  // -------------------------------------------------------------------------
  // Render: DETAIL screen
  // -------------------------------------------------------------------------

  if (detailLoading || !row) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={c.blueText} />
      </View>
    );
  }

  const objectiveOptions = row.objectives.map((o) => ({ id: o.id, label: o.text }));

  const renderGeneralInfo = () => (
    <View style={styles.advancedBody}>
      <LabeledInput label="Start Date" value={row.start_date} onChangeText={(v) => setField('start_date', v)} placeholder="YYYY-MM-DD" />
      <LabeledInput label="End Date" value={row.end_date} onChangeText={(v) => setField('end_date', v)} placeholder="YYYY-MM-DD" />
      <LabeledInput label="Problem" value={row.problem} onChangeText={(v) => setField('problem', v)} placeholder="Describe the problem…" multiline />
      <LabeledInput label="USER" value={row.user} onChangeText={(v) => setField('user', v)} placeholder="Who is the user…" multiline />

      <View style={styles.fieldBlock}>
        <View style={styles.labelRow}>
          <Text style={styles.fieldLabel}>Objectives</Text>
          <SmallButton label="+ Add" onPress={addObjective} />
        </View>
        {row.objectives.map((obj, i) => (
          <View key={obj.id} style={styles.objectiveRow}>
<GlassFill />
            <View style={styles.objectiveHeader}>
              <Text style={styles.indexLabel}>{i + 1}.</Text>
              {row.objectives.length > 1 ? <RemoveMark onPress={() => removeObjective(obj.id)} /> : null}
            </View>
            <TextInput style={styles.fieldInput} value={obj.text} onChangeText={(v) => updateObjective(obj.id, { text: v })} placeholder="Objective…" placeholderTextColor={c.slate} />
            <TextInput style={[styles.fieldInput, styles.smallGap]} value={obj.how} onChangeText={(v) => updateObjective(obj.id, { how: v })} placeholder="How — Stage (Task)" placeholderTextColor={c.slate} />
            <TextInput style={[styles.fieldInput, styles.smallGap]} value={obj.proof} onChangeText={(v) => updateObjective(obj.id, { proof: v })} placeholder="Proof — Stage deliverable" placeholderTextColor={c.slate} />
          </View>
        ))}
      </View>

      <LabeledInput label="One Core Feature" value={row.one_core_feature} onChangeText={(v) => setField('one_core_feature', v)} placeholder="Just the thing that solves the problem." multiline />
      <LabeledInput label="Success Looks Like" value={row.success_looks_like} onChangeText={(v) => setField('success_looks_like', v)} placeholder="How will you know it worked?" multiline />
      <LabeledInput label="Exclusions" value={row.exclusions} onChangeText={(v) => setField('exclusions', v)} placeholder="What you're deliberately leaving out." multiline />

      <View style={styles.fieldBlock}>
        <View style={styles.labelRow}>
          <Text style={styles.fieldLabel}>Definition of Done</Text>
          <SmallButton label="+ Add" onPress={() => addCheckItem('definition_of_done')} />
        </View>
        {row.definition_of_done.map((item, i) => (
          <CheckRow
            key={item.id}
            status={effectiveStatus(item)}
            text={item.text}
            index={i}
            onStatusChange={(st) => updateCheckItem('definition_of_done', item.id, { status: st, checked: st === 'C' })}
            onChangeText={(v) => updateCheckItem('definition_of_done', item.id, { text: v })}
            onRemove={() => removeCheckItem('definition_of_done', item.id)}
            hasNotes={!!item.notes}
            onPressIndex={() => setNotesFor({ field: 'definition_of_done', path: [item.id], label: `Definition of Done ${i + 1}` })}
            placeholder="Definition item…"
          />
        ))}
      </View>

      <View style={styles.fieldBlock}>
        <View style={styles.labelRow}>
          <Text style={styles.fieldLabel}>MVP</Text>
          <SmallButton label="+ Add" onPress={() => addCheckItem('mvp')} />
        </View>
        {row.mvp.map((item, i) => (
          <CheckRow
            key={item.id}
            status={effectiveStatus(item)}
            text={item.text}
            onStatusChange={(st) => updateCheckItem('mvp', item.id, { status: st, checked: st === 'C' })}
            onChangeText={(v) => updateCheckItem('mvp', item.id, { text: v })}
            onRemove={() => removeCheckItem('mvp', item.id)}
            placeholder={`MVP ${i + 1}`}
          />
        ))}
      </View>
    </View>
  );

  const CONSTRAINT_FIELDS: { field: ConstraintField; label: string }[] = [
    { field: 'challenges', label: 'Challenges' },
    { field: 'constraints', label: 'Constraints' },
    { field: 'risks', label: 'Risk (Recurring Cycles)' },
  ];

  const renderConstraints = () => (
    <View style={styles.advancedBody}>
      {CONSTRAINT_FIELDS.map(({ field, label }) => (
        <View key={field} style={styles.fieldBlock}>
          <View style={styles.labelRow}>
            <Text style={styles.fieldLabel}>{label}</Text>
            <SmallButton label="+ Add" onPress={() => addConstraint(field)} />
          </View>
          {row[field].map((item, i) => (
            <View key={item.id} style={styles.constraintRow}>
              <Text style={styles.indexLabel}>{i + 1}.</Text>
              <TextInput
                style={styles.constraintText}
                value={item.text}
                onChangeText={(v) => updateConstraint(field, item.id, { text: v })}
                placeholder={`${label} description…`}
                placeholderTextColor={c.slate}
              />
              <RemoveMark onPress={() => removeConstraint(field, item.id)} />
              <TextInput
                style={styles.constraintStage}
                value={item.stageComponent}
                onChangeText={(v) => updateConstraint(field, item.id, { stageComponent: v })}
                placeholder="Stage / Component"
                placeholderTextColor={c.slate}
              />
            </View>
          ))}
        </View>
      ))}
    </View>
  );

  const renderForecastPoint = (stepId: string, point: ForecastPoint) => (
    <View key={point.id} style={styles.pointRow}>
      <Text style={styles.bullet}>•</Text>
      <TextInput
        style={styles.pointLabel}
        value={point.label}
        onChangeText={(v) => updateForecastPoint(stepId, point.id, { label: v })}
        placeholder="Label"
        placeholderTextColor={c.slate}
        maxLength={9}
      />
      <Text style={styles.bullet}>:</Text>
      <TextInput
        style={styles.pointDescription}
        value={point.description}
        onChangeText={(v) => updateForecastPoint(stepId, point.id, { description: v })}
        placeholder="Description text…"
        placeholderTextColor={c.slate}
        multiline
      />
      <RemoveMark onPress={() => removeForecastPoint(stepId, point.id)} />
    </View>
  );

  const renderForecastRE = (stepId: string, concernId: string, optionId: string, kind: 'rewards' | 'exposures', item: ForecastRewardExposure) => (
    <View key={item.id} style={styles.reRow}>
      <View style={[styles.rePill, kind === 'rewards' ? styles.rewardPill : styles.exposurePill]}>
        <Text style={styles.rePillText}>{kind === 'rewards' ? 'Reward' : 'Exposure'}</Text>
      </View>
      <TextInput
        style={styles.reInput}
        value={item.description}
        onChangeText={(v) => updateForecastRE(stepId, concernId, optionId, kind, item.id, v)}
        placeholder="Description"
        placeholderTextColor={c.slate}
      />
      <RemoveMark onPress={() => removeForecastRE(stepId, concernId, optionId, kind, item.id)} />
    </View>
  );

  const renderForecastOption = (stepId: string, concernId: string, option: ForecastOption) => (
    <View key={option.id} style={styles.optionBlock}>
      <View style={styles.optionHeaderRow}>
        <TextInput
          style={styles.optionPill}
          value={option.title}
          onChangeText={(v) => updateForecastOption(stepId, concernId, option.id, v)}
          placeholder="Option"
          placeholderTextColor={c.textFaint}
        />
        <RemoveMark onPress={() => removeForecastOption(stepId, concernId, option.id)} />
      </View>
      {option.rewards.map((r) => renderForecastRE(stepId, concernId, option.id, 'rewards', r))}
      {option.exposures.map((r) => renderForecastRE(stepId, concernId, option.id, 'exposures', r))}
      <View style={styles.optionAddRow}>
        <SmallButton label="+ Reward" onPress={() => addForecastRE(stepId, concernId, option.id, 'rewards')} />
        <SmallButton label="+ Exposure" onPress={() => addForecastRE(stepId, concernId, option.id, 'exposures')} />
      </View>
    </View>
  );

  const renderForecastConcern = (stepId: string, concern: ForecastConcern) => (
    <View key={concern.id} style={styles.concernBlock}>
      <View style={styles.concernHeaderRow}>
        <TextInput
          style={styles.concernPill}
          value={concern.title}
          onChangeText={(v) => updateForecastConcern(stepId, concern.id, v)}
          placeholder="Choice"
          placeholderTextColor={c.textFaint}
        />
        <SmallButton label="+ Option" onPress={() => addForecastOption(stepId, concern.id)} />
        <RemoveMark onPress={() => removeForecastConcern(stepId, concern.id)} />
      </View>
      {concern.options.map((o) => renderForecastOption(stepId, concern.id, o))}
    </View>
  );

  const stepPillStyle = (step: ForecastStep) => [
    styles.stepPill,
    step.type === 'start' ? styles.stepPillStart : null,
    step.type === 'end' ? styles.stepPillEnd : null,
    step.type === 'step' ? styles.stepPillStep : null,
    step.checked ? styles.stepPillChecked : null,
  ];

  const renderForecast = () => {
    const steps = row.forecast_steps;
    return (
      <View style={styles.advancedBody}>
        {steps.map((step, stepIdx) => {
          const stepNumber = step.type === 'step' ? steps.filter((s, i) => s.type === 'step' && i <= stepIdx).length : undefined;
          return (
            <View key={step.id} style={styles.forecastStepBlock}>
              <View style={styles.forecastStepHeaderRow}>
                <TextInput
                  style={stepPillStyle(step)}
                  value={step.title}
                  onChangeText={(v) => updateForecastStep(step.id, { title: v })}
                  placeholder={step.type === 'start' ? 'START' : step.type === 'end' ? 'FINISH' : `Step ${stepNumber}`}
                  placeholderTextColor={c.textFaint}
                />
                <TextInput
                  style={styles.stepDescription}
                  value={step.description}
                  onChangeText={(v) => updateForecastStep(step.id, { description: v })}
                  placeholder="Description"
                  placeholderTextColor={c.slate}
                />
              </View>

              {step.points.length > 0 ? <View style={styles.pointsWrap}>{step.points.map((p) => renderForecastPoint(step.id, p))}</View> : null}
              {step.concerns.map((c) => renderForecastConcern(step.id, c))}

              <View style={styles.forecastActionsRow}>
                <SmallButton label="+ Point" onPress={() => addForecastPoint(step.id)} />
                <SmallButton label="+ Choice" onPress={() => addForecastConcern(step.id)} />
                {step.type === 'step' ? (
                  <>
                    <SmallButton label="↑" onPress={() => moveForecastStep(step.id, -1)} />
                    <SmallButton label="↓" onPress={() => moveForecastStep(step.id, 1)} />
                    <SmallButton label={propsOpen[step.id] ? 'Hide Props' : 'Properties'} onPress={() => setPropsOpen((p) => ({ ...p, [step.id]: !p[step.id] }))} />
                    <SmallButton label="Delete" danger onPress={() => removeForecastStep(step.id)} />
                  </>
                ) : null}
              </View>

              {step.type === 'step' && propsOpen[step.id] ? (
                <View style={styles.propsBox}>
<GlassFill />
                  <LabeledInput label="Status" value={step.status ?? ''} onChangeText={(v) => updateForecastStep(step.id, { status: v })} placeholder="Status…" />
                  <LabeledInput label="Stage" value={step.stage !== undefined ? String(step.stage) : ''} onChangeText={(v) => updateForecastStep(step.id, { stage: v ? parseInt(v, 10) || 0 : undefined })} placeholder="Stage number" keyboardType="number-pad" />
                  <LabeledInput label="Due date" value={step.date ?? ''} onChangeText={(v) => updateForecastStep(step.id, { date: v })} placeholder="YYYY-MM-DD" />
                  <LabeledInput
                    label="Drift override (0–8 segments, blank = auto)"
                    value={step.driftOverride !== undefined && step.driftOverride !== null ? String(step.driftOverride) : ''}
                    onChangeText={(v) => updateForecastStep(step.id, { driftOverride: v ? clampDriftOverride(parseInt(v, 10)) : null })}
                    placeholder="auto"
                    keyboardType="number-pad"
                  />
                  <LabeledInput label="Notes" value={step.notes ?? ''} onChangeText={(v) => updateForecastStep(step.id, { notes: v })} placeholder="Notes…" multiline />
                </View>
              ) : null}
            </View>
          );
        })}
        <SmallButton label="+ Add Step" onPress={addForecastStep} />
      </View>
    );
  };

  const renderDestination = () => (
    <View style={styles.advancedTable}>
<GlassFill />
      <SubHeaderBar title="General Information" tint={stageShade('destination', 0)} isOpen={isOpenStrict('generalInfo')} onToggle={() => toggleSection('generalInfo')} onInfoPress={() => openInfo('generalInfo')} />
      {isOpenStrict('generalInfo') ? renderGeneralInfo() : null}
      <SubHeaderBar title="Constraints" tint={stageShade('destination', 1)} isOpen={isOpenStrict('constraints')} onToggle={() => toggleSection('constraints')} onInfoPress={() => openInfo('constraints')} />
      {isOpenStrict('constraints') ? renderConstraints() : null}
      <SubHeaderBar title="FORECAST" tint={stageShade('destination', 2)} isOpen={isOpenStrict('forecast')} onToggle={() => toggleSection('forecast')} onInfoPress={() => openInfo('forecast')} />
      {isOpenStrict('forecast') ? renderForecast() : null}
    </View>
  );

  const renderColumnsTable = (field: ColumnsField) => {
    const columns = row[field] as EssentialTableColumn[];
    const rowCount = columns[0]?.items.length ?? 0;
    if (columns.length === 0) {
      return (
        <View style={styles.emptyTableWrap}>
          <Text style={styles.mutedText}>No table yet.</Text>
          <SmallButton label="+ Create table (People · Places · Processes · Products · Predictions)" onPress={() => seedColumns(field)} />
        </View>
      );
    }
    return (
      <View style={{ gap: 8 }}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          <View style={styles.tableGrid}>
            {/* Header row: editable column titles (+ delete). */}
            <View style={styles.tableHeaderRow}>
              <View style={styles.tableGutterCell} />
              {columns.map((col) => (
                <View key={col.id} style={styles.tableHeaderCell}>
                  <TextInput
                    style={styles.tableHeaderInput}
                    value={col.title}
                    onChangeText={(v) => updateColumnTitle(field, col.id, v)}
                    placeholder="Column"
                    placeholderTextColor={c.textFaint}
                  />
                  <Pressable onPress={() => removeColumn(field, col.id)} hitSlop={4} style={styles.columnDeleteBtn}>
                    <Text style={styles.columnDeleteMark}>✕</Text>
                  </Pressable>
                </View>
              ))}
            </View>
            {/* Body rows: one text cell per column. */}
            {Array.from({ length: rowCount }, (_, r) => (
              <View key={r} style={styles.tableBodyRow}>
                <Pressable onPress={() => removeColumnsRow(field, r)} hitSlop={4} style={styles.tableGutterCell}>
                  <Text style={styles.rowDeleteMark}>✕</Text>
                </Pressable>
                {columns.map((col) => {
                  const cell = col.items[r];
                  return (
                    <TextInput
                      key={cell ? cell.id : `${col.id}-${r}`}
                      style={styles.tableCellInput}
                      value={cell?.text ?? ''}
                      onChangeText={(v) => cell && updateColumnCell(field, col.id, cell.id, { text: v })}
                      placeholderTextColor={c.slate}
                      multiline
                    />
                  );
                })}
              </View>
            ))}
          </View>
        </ScrollView>
        <View style={styles.tableActionsRow}>
          <SmallButton label="+ Add Row" onPress={() => addColumnsRow(field)} />
          <SmallButton label="+ Add Column" onPress={() => addColumn(field)} />
        </View>
      </View>
    );
  };

  const renderEssentials = () => (
    <View style={styles.advancedTable}>
<GlassFill />
      <SubHeaderBar title="Essential Table" tint={stageShade('essentials', 0)} isOpen={isOpenStrict('essentialTable')} onToggle={() => toggleSection('essentialTable')} onInfoPress={() => openInfo('essentialTable')} />
      {isOpenStrict('essentialTable') ? <View style={styles.advancedBody}>{renderColumnsTable('essential_columns')}</View> : null}
    </View>
  );

  // Shared Directions body (List picker + basic_stages steps) used by BOTH the
  // Basic ORDER accordion and the Advanced Directions tab, so they're identical.
  const DIRECTIONS_SECTION = ORDER_SECTIONS.find((s) => s.key === 'directions')!;
  // Sub-steps render recursively so a sub-step can hold its own sub-steps to
  // any depth. `path` is the ancestor-id chain (top-level step first), `prefix`
  // the outline number ("2", then "2.1", then "2.1.3"), `depth` drives indent.
  const renderSubstepTree = (
    path: string[], subs: DesignCheckItem[], prefix: string, depth: number,
  ): React.ReactNode => subs.map((sub, si) => {
    const key = [...path, sub.id].join(':');
    // Even depth → numeric ("1.1"), odd depth → letter ("1.1A").
    const num = depth % 2 === 0 ? `${prefix}.${si + 1}` : `${prefix}${letterLabel(si)}`;
    const st = effectiveStatus(sub);
    const childPath = [...path, sub.id];
    return (
      <View key={sub.id}>
        <View
          nativeID={`tkne-step-${childPath.join('-')}`}
          ref={(el) => { stepRowRefs.current[key] = el; }}
          // Indent by DEPTH, so every sibling at a level shares the same left
          // edge (1.1A / 1.1B / 1.1C line up) while each nested level steps in.
          style={[
            styles.substepRow,
            depth > 0 ? { marginLeft: depth * 18 } : null,
            highlightedStepKey === key ? styles.substepRowHighlighted : null,
          ]}
        >
          <StepStatusSelect
            value={st}
            onChange={(v) => updateSubstepAt('basic_stages', path, sub.id, { status: v, checked: v === 'C' })}
          />
          <OutlineNumber
            label={num}
            hasNotes={!!sub.notes}
            onPress={() => setNotesFor({ field: 'basic_stages', path: childPath, label: num })}
            textStyle={styles.substepIndex}
          />
          <TextInput
            style={[styles.checkRowInput, {
              backgroundColor: STEP_STATUS_META[st].inputFill,
              color: STEP_STATUS_META[st].inputText,
            }]}
            value={sub.text}
            onChangeText={(v) => updateSubstepAt('basic_stages', path, sub.id, { text: v })}
            placeholder="A smaller sub-step…"
            placeholderTextColor={c.slate}
          />
          <Pressable
            style={[styles.dueChip, styles.dueChipSm, sub.dueDate ? styles.dueChipSet : null]}
            onPress={() => setDueDateFor(key)}
          >
            <Text style={[styles.dueChipText, sub.dueDate ? styles.dueChipTextSet : null]}>
              {sub.dueDate ? shortDateLabel(sub.dueDate) : 'Date'}
            </Text>
          </Pressable>
          <View style={styles.reorderCol}>
            <Pressable onPress={() => moveSubstepAt('basic_stages', path, sub.id, -1)} hitSlop={4} disabled={si === 0} style={styles.reorderBtn}>
              <Text style={[styles.reorderText, si === 0 && styles.reorderTextDisabled]}>▲</Text>
            </Pressable>
            <Pressable onPress={() => moveSubstepAt('basic_stages', path, sub.id, 1)} hitSlop={4} disabled={si === subs.length - 1} style={styles.reorderBtn}>
              <Text style={[styles.reorderText, si === subs.length - 1 && styles.reorderTextDisabled]}>▼</Text>
            </Pressable>
          </View>
          {/* Add-child lives ON the row: below the row it would stack up at the
              bottom of the whole subtree, one orphaned + per nesting level. */}
          <Pressable
            onPress={() => addSubstepAt('basic_stages', childPath)}
            style={styles.substepAddCircle}
            hitSlop={8}
            accessibilityLabel={`Add sub-step to ${num}`}
          >
            <Text style={styles.substepAddCircleText}>+</Text>
          </Pressable>
          <RemoveMark onPress={() => removeSubstepAt('basic_stages', path, sub.id)} />
        </View>
        {renderSubstepTree(childPath, sub.substeps ?? [], num, depth + 1)}
      </View>
    );
  });

  // "Run as protocol": open the THEMIS protocol linked to this step, or create
  // one from the step (title + sub-items as protocol steps) and link both ways.
  const runStepAsProtocol = async (item: DesignCheckItem) => {
    if (!userId || !row) return;
    const res = await ensureProtocolForStep({
      userId,
      designId: row.id,
      project: row.project_title,
      stageKey: item.id,
      title: item.text,
      subItems: (item.substeps ?? []).map((sub) => sub.text),
      existingProtocolId: item.protocolId,
    });
    if (!res.protocolId) {
      setError(res.error ?? 'Could not run as protocol');
      return;
    }
    if (item.protocolId !== res.protocolId) {
      updateCheckItem('basic_stages', item.id, { protocolId: res.protocolId });
    }
    if (res.error) setError(res.error);
    if (onOpenToolProp) {
      openThemisProtocol(res.protocolId);
      onOpenToolProp('themis');
    } else {
      Alert_alert('Protocol ready', 'Open THEMIS to run it.');
    }
  };

  const renderDirectionsSteps = () => (
    <>
      {row.basic_stages.length > 0 ? (
        row.basic_stages.map((item, i) => (
          <View key={item.id} nativeID={`tkne-step-${item.id}`} ref={(el) => { stepRowRefs.current[item.id] = el; }}>
            <View style={[
              styles.stepCell,
              { backgroundColor: STEP_STATUS_META[item.status ?? (item.checked ? 'C' : 'P')].cellFill, borderColor: STEP_STATUS_META[item.status ?? (item.checked ? 'C' : 'P')].color },
              highlightedStepKey === item.id ? styles.stepCellHighlighted : null,
            ]}>
              <View style={styles.checkRow}>
                <StepStatusSelect
                  value={item.status ?? (item.checked ? 'C' : 'P')}
                  onChange={(s) => updateCheckItem('basic_stages', item.id, { status: s, checked: s === 'C' })}
                />
                <OutlineNumber
                  label={`STEP ${i + 1}`}
                  hasNotes={!!item.notes}
                  onPress={() => setNotesFor({ field: 'basic_stages', path: [item.id], label: `Step ${i + 1}` })}
                  textStyle={styles.stepIndexLabel}
                />
                <TextInput
                  style={styles.stepCellInput}
                  value={item.text}
                  onChangeText={(v) => updateCheckItem('basic_stages', item.id, { text: v })}
                  placeholder={DIRECTIONS_SECTION.itemPlaceholder}
                  placeholderTextColor={c.slate}
                />
                <Pressable
                  style={[styles.dueChip, item.dueDate ? styles.dueChipSet : null]}
                  onPress={() => setDueDateFor(item.id)}
                >
                  <Text style={[styles.dueChipText, item.dueDate ? styles.dueChipTextSet : null]}>
                    {item.dueDate ? shortDateLabel(item.dueDate) : 'Date'}
                  </Text>
                </Pressable>
                <Pressable
                  style={[styles.dueChip, item.protocolId ? styles.dueChipSet : null]}
                  onPress={() => void runStepAsProtocol(item)}
                  accessibilityLabel="Run as protocol"
                >
                  <Text style={[styles.dueChipText, item.protocolId ? styles.dueChipTextSet : null]}>
                    {item.protocolId ? 'Open protocol' : 'Run as protocol'}
                  </Text>
                </Pressable>
                <View style={styles.reorderCol}>
                  <Pressable onPress={() => moveCheckItem('basic_stages', item.id, -1)} hitSlop={4} disabled={i === 0} style={styles.reorderBtn}>
                    <Text style={[styles.reorderText, i === 0 && styles.reorderTextDisabled]}>▲</Text>
                  </Pressable>
                  <Pressable onPress={() => moveCheckItem('basic_stages', item.id, 1)} hitSlop={4} disabled={i === row.basic_stages.length - 1} style={styles.reorderBtn}>
                    <Text style={[styles.reorderText, i === row.basic_stages.length - 1 && styles.reorderTextDisabled]}>▼</Text>
                  </Pressable>
                </View>
                <Pressable
                  onPress={() => addSubstepAt('basic_stages', [item.id])}
                  style={styles.substepAddCircle}
                  hitSlop={8}
                  accessibilityLabel="Add sub-step"
                >
                  <Text style={styles.substepAddCircleText}>+</Text>
                </Pressable>
                <RemoveMark onPress={() => removeCheckItem('basic_stages', item.id)} />
              </View>
              <View style={styles.substepGroup}>
                {renderSubstepTree([item.id], item.substeps ?? [], `${i + 1}`, 0)}
              </View>
            </View>
          </View>
        ))
      ) : (
        <Text style={styles.basicEmptyHint}>{DIRECTIONS_SECTION.emptyHint}</Text>
      )}
      <Pressable onPress={() => addCheckItem('basic_stages')} style={styles.substepAddBtn} hitSlop={6}>
        <Text style={styles.substepAddText}>+ Add step</Text>
      </Pressable>
    </>
  );

  // Advanced Directions = the same steps UI as Basic (one-for-one mirror).
  const renderStages = () => (
    <View style={styles.advancedBody}>
      {renderDirectionsSteps()}
      {renderCalendarNote()}
      {renderStorm()}
      {renderDesignListSection()}
      {renderTaskSection()}
    </View>
  );

  const renderStagesLEGACY = () => (
    <View style={styles.advancedTable}>
<GlassFill />
      {row.stages.map((stage, stageIdx) => (
        <View key={stage.id}>
          <SubHeaderBar
            title={`STAGE ${stageIdx + 1}: ${stage.title || '(Title)'}`}
            tint={stageShade('stages', stageIdx)}
            isOpen={isOpenDefaultTrue(`stage-${stage.id}`)}
            onToggle={() => toggleSection(`stage-${stage.id}`)}
          />
          {isOpenDefaultTrue(`stage-${stage.id}`) ? (
            <View style={styles.advancedBody}>
              <LabeledInput label="Stage Title" value={stage.title} onChangeText={(v) => updateStage(stage.id, { title: v })} placeholder="Stage title…" />
              <LabeledInput label="Objective Stage" value={stage.objectiveStage} onChangeText={(v) => updateStage(stage.id, { objectiveStage: v })} placeholder="Stage objective…" />
              <View style={styles.fieldBlock}>
                <Text style={styles.fieldLabel}>Objective Project</Text>
                <ChipPicker
                  options={objectiveOptions}
                  selectedId={stage.objectiveProjectId}
                  onSelect={(id) => updateStage(stage.id, { objectiveProjectId: id })}
                  emptyLabel="No objectives in Orient yet."
                />
              </View>

              <Text style={[styles.fieldLabel, styles.smallGap]}>Tasks</Text>
              {stage.tasks.map((task) => (
                <View key={task.id} style={styles.taskRow}>
                  <TextInput style={styles.taskDateInput} value={task.date} onChangeText={(v) => updateStageTask(stage.id, task.id, { date: v })} placeholder="YYYY-MM-DD" placeholderTextColor={c.slate} />
                  <TextInput style={styles.taskTextInput} value={task.task} onChangeText={(v) => updateStageTask(stage.id, task.id, { task: v })} placeholder="Task…" placeholderTextColor={c.slate} />
                  <RemoveMark onPress={() => removeStageTask(stage.id, task.id)} />
                </View>
              ))}
              {stage.tasks.map((task) => (
                <View key={`${task.id}-ba`} style={styles.taskBeforeAfterRow}>
                  <Pressable onPress={() => updateStageTask(stage.id, task.id, { before: !task.before })} style={[styles.checkbox, task.before ? styles.checkboxChecked : null]}>
                    {task.before ? <Text style={styles.checkboxMark}>✓</Text> : null}
                  </Pressable>
                  <TextInput style={styles.taskBeforeAfterInput} value={task.beforeText ?? ''} onChangeText={(v) => updateStageTask(stage.id, task.id, { beforeText: v })} placeholder="Before…" placeholderTextColor={c.slate} />
                  <Pressable onPress={() => updateStageTask(stage.id, task.id, { after: !task.after })} style={[styles.checkbox, task.after ? styles.checkboxChecked : null]}>
                    {task.after ? <Text style={styles.checkboxMark}>✓</Text> : null}
                  </Pressable>
                  <TextInput style={styles.taskBeforeAfterInput} value={task.afterText ?? ''} onChangeText={(v) => updateStageTask(stage.id, task.id, { afterText: v })} placeholder="After…" placeholderTextColor={c.slate} />
                </View>
              ))}
              <View style={styles.forecastActionsRow}>
                <SmallButton label="+ Add Task" onPress={() => addStageTask(stage.id)} />
                <SmallButton label="Delete Stage" danger onPress={() => Alert_alert('Delete this stage?', 'This permanently removes the stage and its tasks.', [{ text: 'Cancel', style: 'cancel' }, { text: 'Delete', style: 'destructive', onPress: () => removeStage(stage.id) }])} />
              </View>
            </View>
          ) : null}
        </View>
      ))}
      <SmallButton label="+ Add Stage" onPress={addStage} />
    </View>
  );

  const renderCalendarNote = () => (
    <View>
      <SubHeaderBar title="Calendar" tint={stageShade('implementation', 0)} isOpen={isOpenDefaultTrue('calendar')} onToggle={() => toggleSection('calendar')} onInfoPress={() => openInfo('calendar')} />
      {isOpenDefaultTrue('calendar') ? (
        <View style={styles.advancedBody}>
          <CalendarPicker
            calendars={calendars}
            selectedId={row.sync_calendar_id}
            onSelect={(id) => setField('sync_calendar_id', id)}
            onAddCalendar={() => void addCalendar()}
          />
          {!row.sync_calendar_id ? (
            <Text style={styles.mutedText}>Pick a calendar (or add one) to see and add this project&apos;s events. Events you add here mirror into that KRNOS calendar.</Text>
          ) : (
            // The real KRNOS schedule (week/day/month time-grid), scoped to the
            // linked calendar — identical to the standalone KRNOS tool.
            <KrnosSchedule calendarId={row.sync_calendar_id} embedded />
          )}
        </View>
      ) : null}
    </View>
  );

  const renderStorm = () => (
    <View>
      <SubHeaderBar title="Storm" tint={stageShade('implementation', 1)} isOpen={isOpenDefaultTrue('storm')} onToggle={() => toggleSection('storm')} onInfoPress={() => openInfo('storm')} />
      {isOpenDefaultTrue('storm') ? (
        <View style={styles.advancedBody}>
          {row.storm_columns.length === 0 ? (
            <View style={styles.emptyTableWrap}>
              <Text style={styles.mutedText}>No storm table yet.</Text>
              <SmallButton label="+ Create storm table (4 × 4)" onPress={seedStorm} />
            </View>
          ) : (
            <View style={{ gap: 8 }}>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.tableGrid}>
                  {/* Header row: editable column titles (+ delete). */}
                  <View style={styles.tableHeaderRow}>
                    <View style={styles.tableGutterCell} />
                    {row.storm_columns.map((col, colIdx) => (
                      <View key={colIdx} style={styles.tableHeaderCell}>
                        <TextInput
                          style={styles.tableHeaderInput}
                          value={col}
                          onChangeText={(v) => updateStormColumnTitle(colIdx, v)}
                          placeholder="Column"
                          placeholderTextColor={c.textFaint}
                        />
                        <Pressable onPress={() => removeStormColumn(colIdx)} hitSlop={4} style={styles.columnDeleteBtn}>
                          <Text style={styles.columnDeleteMark}>✕</Text>
                        </Pressable>
                      </View>
                    ))}
                  </View>
                  {/* Body rows: one editable text cell per column. */}
                  {row.storm_rows.map((r, rowIdx) => (
                    <View key={r.id} style={styles.tableBodyRow}>
                      <Pressable onPress={() => removeStormRow(rowIdx)} hitSlop={4} style={styles.tableGutterCell}>
                        <Text style={styles.rowDeleteMark}>✕</Text>
                      </Pressable>
                      {r.cells.map((cell, cellIdx) => (
                        <TextInput
                          key={cell.id}
                          style={styles.tableCellInput}
                          value={cell.text}
                          onChangeText={(v) => updateStormCell(rowIdx, cellIdx, v)}
                          placeholderTextColor={c.slate}
                          multiline
                        />
                      ))}
                    </View>
                  ))}
                </View>
              </ScrollView>
              <View style={styles.tableActionsRow}>
                <SmallButton label="+ Add Row" onPress={addStormRow} />
                <SmallButton label="+ Add Column" onPress={addStormColumn} />
              </View>
            </View>
          )}
        </View>
      ) : null}
    </View>
  );

  const renderDesignListSection = () => (
    <View>
      <SubHeaderBar title="List" tint={stageShade('implementation', 2)} isOpen={isOpenDefaultTrue('list')} onToggle={() => toggleSection('list')} onInfoPress={() => openInfo('list')} />
      {isOpenDefaultTrue('list') ? (
        <View style={styles.advancedBody}>
          {row.design_list.map((item) => (
            <CheckRow
              key={item.id}
              status={item.done ? 'C' : 'P'}
              text={item.text}
              onStatusChange={(st) => updateDesignListItem(item.id, { done: st === 'C' })}
              onChangeText={(v) => updateDesignListItem(item.id, { text: v })}
              onRemove={() => removeDesignListItem(item.id)}
              placeholder="To-do item…"
            />
          ))}
          <SmallButton label="+ Add Item" onPress={addDesignListItem} />
        </View>
      ) : null}
    </View>
  );

  const renderTaskSection = () => {
    // Top-level tasks first, each followed by its subtasks (parent_task_id).
    const roots = linkedTasks.filter((t) => !t.parent_task_id);
    const childrenOf = (pid: string) => linkedTasks.filter((t) => t.parent_task_id === pid);
    return (
      <View>
        <SubHeaderBar title="Task" tint={stageShade('implementation', 3)} isOpen={isOpenDefaultTrue('task')} onToggle={() => toggleSection('task')} onInfoPress={() => openInfo('task')} />
        {isOpenDefaultTrue('task') ? (
          <View style={styles.advancedBody}>
            {/* Pick which KYRS list this project's tasks live in (or make one). */}
            <ListPicker
              lists={kyrsLists}
              selectedId={row.sync_list_id}
              onSelect={(id) => setField('sync_list_id', id)}
              onAddList={() => void addKyrsList()}
            />
            {!row.sync_list_id ? (
              <Text style={styles.mutedText}>Pick a task list (or add one) to hold this project&apos;s tasks. Your Directions steps mirror into it automatically, and you can add your own tasks here too.</Text>
            ) : (
              <View style={{ gap: 6, marginTop: 8 }}>
                {roots.length === 0 ? (
                  <Text style={styles.mutedText}>No tasks yet — add one below, or add Directions steps and they&apos;ll appear here.</Text>
                ) : (
                  roots.map((t) => (
                    <React.Fragment key={t.id}>
                      <View style={styles.taskRow}>
                        <Pressable
                          onPress={() => void toggleLinkedTask(t)}
                          style={[styles.taskCheckbox, t.done ? styles.taskCheckboxOn : null]}
                        >
                          {t.done ? <Text style={styles.taskCheckMark}>✓</Text> : null}
                        </Pressable>
                        <TextInput
                          style={[styles.taskTitleInput, t.done ? styles.taskTitleDone : null]}
                          value={t.title}
                          onChangeText={(v) => void renameLinkedTask(t, v)}
                          placeholder="Task…"
                          placeholderTextColor={c.slate}
                        />
                        <Pressable onPress={() => void removeLinkedTask(t)} hitSlop={6}>
                          <Text style={styles.taskRemove}>✕</Text>
                        </Pressable>
                      </View>
                      {childrenOf(t.id).map((sub) => (
                        <View key={sub.id} style={[styles.taskRow, styles.taskRowSub]}>
                          <Pressable
                            onPress={() => void toggleLinkedTask(sub)}
                            style={[styles.taskCheckbox, sub.done ? styles.taskCheckboxOn : null]}
                          >
                            {sub.done ? <Text style={styles.taskCheckMark}>✓</Text> : null}
                          </Pressable>
                          <TextInput
                            style={[styles.taskTitleInput, sub.done ? styles.taskTitleDone : null]}
                            value={sub.title}
                            onChangeText={(v) => void renameLinkedTask(sub, v)}
                            placeholder="Subtask…"
                            placeholderTextColor={c.slate}
                          />
                          <Pressable onPress={() => void removeLinkedTask(sub)} hitSlop={6}>
                            <Text style={styles.taskRemove}>✕</Text>
                          </Pressable>
                        </View>
                      ))}
                    </React.Fragment>
                  ))
                )}
                <View style={styles.taskAddRow}>
                  <TextInput
                    style={styles.taskAddInput}
                    value={newTaskText}
                    onChangeText={setNewTaskText}
                    placeholder="New task…"
                    placeholderTextColor={c.slate}
                    onSubmitEditing={() => void addLinkedTask()}
                    returnKeyType="done"
                  />
                  <SmallButton label="+ Add" onPress={() => void addLinkedTask()} />
                </View>
              </View>
            )}
          </View>
        ) : null}
      </View>
    );
  };

  // Calendar/Storm/List/Task moved to the Directions tab (renderStages) —
  // they're planning/tracking aids for the steps, not execution output.
  // Execution now holds only the session Guide.
  const renderImplementation = () => (
    <View style={styles.advancedTable}>
<GlassFill />
      {renderGuide()}
    </View>
  );

  const RESULT_OPTIONS: { value: GuideResult; label: string }[] = [
    { value: '', label: 'None' },
    { value: 'success', label: 'Success' },
    { value: 'unsuccessful', label: 'Unsuccessful' },
    { value: 'not_applicable', label: 'N/A' },
  ];

  const renderGuide = () => {
    const entries = row.guide_entries;
    const idx = guideIndex < 0 ? entries.length - 1 : Math.min(guideIndex, entries.length - 1);
    const entry = entries[idx];

    if (entries.length === 0) {
      return (
        <View style={styles.advancedBody}>
          <Text style={styles.mutedText}>No session guides yet.</Text>
          <SmallButton label="+ New Session" onPress={addGuideEntry} />
        </View>
      );
    }

    // Directions sub-steps for the currently-selected step (drives the sub-step
    // dropdown — only shown when the chosen step actually has sub-steps).
    const dirSubSteps = row.basic_stages[entry.forecastStep - 1]?.substeps ?? [];

    return (
      <View>
        <View style={styles.guideNavBar}>
          <Text style={styles.guideNavTitle}>Session ORDER</Text>
          <View style={styles.guideNavControls}>
            <Pressable disabled={idx <= 0} onPress={() => setGuideIndex(Math.max(0, idx - 1))} hitSlop={6}>
              <Text style={styles.navArrow}>‹</Text>
            </Pressable>
            <TextInput style={styles.guideDateInput} value={entry.date} onChangeText={(v) => updateGuideEntry(entry.id, { date: v })} placeholder="YYYY-MM-DD" placeholderTextColor={c.textFaint} />
            <Pressable disabled={idx >= entries.length - 1} onPress={() => setGuideIndex(Math.min(entries.length - 1, idx + 1))} hitSlop={6}>
              <Text style={styles.navArrow}>›</Text>
            </Pressable>
            <Pressable onPress={addGuideEntry} hitSlop={6}>
              <Text style={styles.navArrow}>+</Text>
            </Pressable>
          </View>
        </View>

        <View style={styles.advancedBody}>
          {/* Orient */}
          <View style={styles.fieldBlock}>
            <OrderHeader word="Orient" rest="For this session I will complete" />
            <SubstepPickerInput
              value={entry.sessionGoal}
              onChangeText={(v) => updateGuideEntry(entry.id, { sessionGoal: v })}
              options={substepOptions}
            />
          </View>
          <View style={[styles.fieldBlock, styles.orderIndent]}>
            <Text style={styles.fieldLabel}>My deliverable will be</Text>
            <TextInput style={styles.fieldInput} value={entry.deliverable} onChangeText={(v) => updateGuideEntry(entry.id, { deliverable: v })} placeholderTextColor={c.slate} />
          </View>

          {/* Resources */}
          <View style={styles.fieldBlock}>
            <OrderHeader word="Resources" rest="To complete this I'll need" />
            <TextInput style={styles.fieldInput} value={entry.essentials} onChangeText={(v) => updateGuideEntry(entry.id, { essentials: v })} placeholderTextColor={c.slate} />
          </View>

          {/* Directions — pick a step (and sub-step) from the Directions section */}
          <View style={styles.fieldBlock}>
            <OrderHeader word="Directions" />
            <View style={[styles.pairRow, styles.orderIndent]}>
              <Text style={styles.mutedText}>I'm on step</Text>
              <StepDropdown
                placeholder="Step…"
                options={row.basic_stages.map((s) => s.text)}
                selectedIndex={entry.forecastStep}
                onSelect={(idx) => updateGuideEntry(entry.id, { forecastStep: idx, forecastSubStep: 0 })}
              />
              {dirSubSteps.length > 0 ? (
                <StepDropdown
                  placeholder="Sub-step…"
                  options={dirSubSteps.map((s) => s.text)}
                  selectedIndex={entry.forecastSubStep}
                  onSelect={(idx) => updateGuideEntry(entry.id, { forecastSubStep: idx })}
                />
              ) : null}
            </View>
          </View>

          {/* Implementation rows */}
          <View style={styles.fieldBlock}>
            <View style={styles.labelRow}>
              <Text style={styles.fieldLabel}>Implementation</Text>
              <SmallButton label="+ Add Row" onPress={() => addGuideImplRow(entry.id)} />
            </View>
            {entry.implementationRows.map((r, i) => (
              <View key={r.id} style={styles.implRow}>
                <Text style={styles.indexLabel}>{i + 1}.</Text>
                <TextInput style={styles.implTaskInput} value={r.task} onChangeText={(v) => updateGuideImplRow(entry.id, r.id, { task: v })} placeholder="Task…" placeholderTextColor={c.slate} />
                <TextInput style={styles.implTimeInput} value={r.time} onChangeText={(v) => updateGuideImplRow(entry.id, r.id, { time: v })} placeholder="HH:MM" placeholderTextColor={c.slate} />
              </View>
            ))}
          </View>

          {/* Execute */}
          <View style={styles.fieldBlock}>
            <OrderHeader word="Execute" />
            <TextInput style={[styles.fieldInput, styles.fieldInputMultiline]} value={entry.guide} onChangeText={(v) => updateGuideEntry(entry.id, { guide: v })} placeholder="Text field" placeholderTextColor={c.slate} multiline />
          </View>

          {/* Revise */}
          <View style={styles.fieldBlock}>
            <OrderHeader word="Revise" />
            <View style={styles.chipRow}>
              {RESULT_OPTIONS.map((opt) => (
                <Pressable key={opt.value || 'none'} onPress={() => updateGuideEntry(entry.id, { result: opt.value })} style={[styles.chip, entry.result === opt.value ? styles.chipSelected : null]}>
                  <Text style={[styles.chipText, entry.result === opt.value ? styles.chipTextSelected : null]}>{opt.label}</Text>
                </Pressable>
              ))}
            </View>
            <TextInput
              style={[styles.fieldInput, styles.smallGap]}
              value={entry.resultText}
              onChangeText={(v) => updateGuideEntry(entry.id, { resultText: v })}
              onBlur={() => syncGuideToNotes(entry.id)}
              placeholder={entry.result === 'unsuccessful' ? 'Auto-populates Process Negative…' : ''}
              placeholderTextColor={c.slate}
            />
          </View>

          <LabeledInput label="Index Update" value={entry.indexUpdate} onChangeText={(v) => updateGuideEntry(entry.id, { indexUpdate: v })} onBlur={() => syncGuideToNotes(entry.id)} placeholder="Auto-populates Index…" />
          <LabeledInput label="Upgrades" value={entry.upgrades} onChangeText={(v) => updateGuideEntry(entry.id, { upgrades: v })} onBlur={() => syncGuideToNotes(entry.id)} placeholder="Auto-populates Upgrades…" />
          <LabeledInput label="Tech Debt" value={entry.techDebt} onChangeText={(v) => updateGuideEntry(entry.id, { techDebt: v })} placeholder="Repeated mistakes & mitigation…" />
          <LabeledInput label="Concerns" value={entry.concerns} onChangeText={(v) => updateGuideEntry(entry.id, { concerns: v })} placeholder="Unknown variables needing attention…" />
          <LabeledInput label="Next Step / MEP" value={entry.nextStep} onChangeText={(v) => updateGuideEntry(entry.id, { nextStep: v })} placeholder="What's next & advance prep needed…" />
          <LabeledInput label="Other" value={entry.other} onChangeText={(v) => updateGuideEntry(entry.id, { other: v })} />
        </View>
      </View>
    );
  };

  const NOTE_SECTIONS: { field: NoteField; toggleKey: string; label: string }[] = [
    { field: 'process_negatives', toggleKey: 'processNeg', label: 'Process Negative' },
    { field: 'index_items', toggleKey: 'index', label: 'Index' },
    { field: 'upgrades', toggleKey: 'upgradesSection', label: 'Upgrades' },
  ];

  const renderNotes = () => (
    <View style={styles.advancedTable}>
<GlassFill />
      {NOTE_SECTIONS.map(({ field, toggleKey, label }, ni) => (
        <View key={field}>
          <SubHeaderBar title={label} tint={stageShade('notes', ni)} isOpen={isOpenDefaultTrue(toggleKey)} onToggle={() => toggleSection(toggleKey)} onInfoPress={() => openInfo(toggleKey)} />
          {isOpenDefaultTrue(toggleKey) ? (
            <View style={styles.advancedBody}>
              {row[field].map((item, i) => (
                <View key={item.id} style={styles.constraintRow}>
                  <Text style={styles.indexLabel}>{i + 1}.</Text>
                  <TextInput style={styles.constraintText} value={item.text} onChangeText={(v) => updateNoteItem(field, item.id, v)} placeholderTextColor={c.slate} />
                  <RemoveMark onPress={() => removeNoteItem(field, item.id)} />
                </View>
              ))}
              <SmallButton label="+ Add" onPress={() => addNoteItem(field)} />
            </View>
          ) : null}
        </View>
      ))}
      <SubHeaderBar title="Additional Notes" tint={stageShade('notes', NOTE_SECTIONS.length)} isOpen={isOpenDefaultTrue('additionalNotes')} onToggle={() => toggleSection('additionalNotes')} onInfoPress={() => openInfo('additionalNotes')} />
      {isOpenDefaultTrue('additionalNotes') ? (
        <View style={styles.advancedBody}>
          <TextInput
            style={[styles.fieldInput, styles.fieldInputMultiline]}
            value={row.additional_notes}
            onChangeText={(v) => setField('additional_notes', v)}
            placeholder="Additional notes…"
            placeholderTextColor={c.slate}
            multiline
          />
        </View>
      ) : null}
    </View>
  );

  const renderLogs = () => {
    const groups: { day: string; entries: typeof designLogs }[] = [];
    for (const entry of designLogs) {
      const day = entry.created_at.slice(0, 10);
      const last = groups[groups.length - 1];
      if (last && last.day === day) last.entries.push(entry);
      else groups.push({ day, entries: [entry] });
    }
    return advSubBlock('Logs', 'notes', 4, 'logsSection', () => (
      <View>
        <View style={styles.taskAddRow}>
          <TextInput
            style={styles.taskAddInput}
            value={newLogNote}
            onChangeText={setNewLogNote}
            placeholder="Log a note…"
            placeholderTextColor={c.slate}
            onSubmitEditing={() => void addDesignLogNote()}
            returnKeyType="done"
          />
          <SmallButton label="+ Log" onPress={() => void addDesignLogNote()} />
        </View>
        {groups.length === 0 ? (
          <Text style={styles.mutedText}>No changes logged yet.</Text>
        ) : (
          groups.map((g) => (
            <View key={g.day}>
              <Text style={styles.logDayHeader}>{g.day}</Text>
              {g.entries.map((entry) => (
                <View key={entry.id} style={styles.logRow}>
                  <Text style={styles.logTime}>{entry.created_at.slice(11, 16)}</Text>
                  <Text style={styles.logTag}>{entry.kind === 'change' ? 'TKNE' : 'note'}</Text>
                  <Text style={styles.logText}>{entry.text}</Text>
                </View>
              ))}
            </View>
          ))
        )}
      </View>
    ));
  };

  const SECTION_RENDERERS: Record<SectionKey, () => React.ReactNode> = {
    destination: renderDestination,
    essentials: renderEssentials,
    stages: renderStages,
    implementation: renderImplementation,
    guide: renderGuide,
    notes: renderNotes,
  };

  // "guide" is folded into the "implementation" tab (both now render together
  // under one "Execution" tab/section) — hide the separate "guide" tab from
  // the tab strip. Underlying SectionKey/section_toggles are untouched.
  const TAB_KEYS = SECTION_KEYS.filter((key) => key !== 'guide');
  const displaySection = activeSection === 'guide' ? 'implementation' : activeSection;

  const renderAdvanced = () => (
    <View>
      {/* One continuous segmented bar (not separate pills). Outer ends rounded
          via the clipping container; segments sit flush, split by hairlines. */}
      <View style={styles.sectionTabsBar}>
        {TAB_KEYS.map((key, idx) => {
          const active = displaySection === key;
          return (
            <Pressable
              key={key}
              onPress={() => setActiveSection(key)}
              style={[
                styles.sectionSeg,
                { backgroundColor: SECTION_COLORS[key], opacity: active ? 1 : 0.62 },
                idx > 0 && styles.sectionSegDivider,
              ]}
            >
              <Text style={[styles.sectionSegText, active && styles.sectionSegTextActive]} numberOfLines={1}>
                {SECTION_LABELS[key]}
              </Text>
            </Pressable>
          );
        })}
      </View>
      {/* No section-level header/toggle on mobile — the active tab already names
          the stage, and its content is always shown (sub-sections tap to expand). */}
      {SECTION_RENDERERS[displaySection]()}
    </View>
  );

  // ── Per-section advanced features ────────────────────────────────────────
  // One ORDER screen: each section shows its basic block plus any advanced
  // features the user has switched on via the section's "⋯" menu. Presence is
  // stored under `feat_<sectionKey>_<featKey>` (basic defaults on, advanced off);
  // each advanced block keeps its own SubHeaderBar collapse key underneath.
  const advSubBlock = (
    title: string, sectionKey: SectionKey, idx: number, collapseKey: string, body: () => React.ReactNode,
  ) => (
    <View>
      <SubHeaderBar title={title} tint={stageShade(sectionKey, idx)} isOpen={isOpenDefaultTrue(collapseKey)} onToggle={() => toggleSection(collapseKey)} onInfoPress={SECTION_INFO[collapseKey] ? () => openInfo(collapseKey) : undefined} />
      {isOpenDefaultTrue(collapseKey) ? <View style={styles.advancedBody}>{body()}</View> : null}
    </View>
  );

  type FeatureDef = { key: string; label: string; render: () => React.ReactNode };
  const ADVANCED_FEATURES: Record<string, FeatureDef[]> = {
    orient: [
      { key: 'generalInfo', label: 'General Information', render: () => advSubBlock('General Information', 'destination', 0, 'generalInfo', renderGeneralInfo) },
      { key: 'constraints', label: 'Constraints', render: () => advSubBlock('Constraints', 'destination', 1, 'constraints', renderConstraints) },
      { key: 'forecast', label: 'Forecast', render: () => advSubBlock('Forecast', 'destination', 2, 'forecast', renderForecast) },
    ],
    resources: [
      { key: 'essentialTable', label: 'Essential Table', render: () => advSubBlock('Essential Table', 'essentials', 0, 'essentialTable', () => renderColumnsTable('essential_columns')) },
    ],
    directions: [
      { key: 'calendar', label: 'Calendar', render: renderCalendarNote },
      { key: 'storm', label: 'Storm', render: renderStorm },
      { key: 'list', label: 'List', render: renderDesignListSection },
      { key: 'task', label: 'Task', render: renderTaskSection },
    ],
    execute: [
      { key: 'guide', label: 'Metis-Order', render: () => advSubBlock('Metis-Order', 'implementation', 4, 'guide', renderGuide) },
    ],
    revise: [
      { key: 'notesDetail', label: 'Notes (Process / Index / Upgrades)', render: renderNotes },
      { key: 'logs', label: 'Logs', render: renderLogs },
    ],
  };

  const renderFeatureMenu = (sectionKey: string) => (
    <View style={styles.featMenu}>
<GlassFill />
      <View style={styles.featMenuRow}>
        <Text style={styles.featMenuLabel}>Basic</Text>
        <Switch
          value={isOpenDefaultTrue(`feat_${sectionKey}_basic`)}
          onValueChange={() => toggleBasicSection(`feat_${sectionKey}_basic`)}
        />
      </View>
      {(ADVANCED_FEATURES[sectionKey] ?? []).map((f) => (
        <View key={f.key} style={styles.featMenuRow}>
          <Text style={styles.featMenuLabel}>{f.label}</Text>
          <Switch
            value={isOpenStrict(`feat_${sectionKey}_${f.key}`)}
            onValueChange={() => toggleSection(`feat_${sectionKey}_${f.key}`)}
          />
        </View>
      ))}
    </View>
  );

  // Basic view = the ORDER staircase. Each section is a colored bar (matching
  // the ORACLE ORDER palette) with its input rows nested beneath, rendered as
  // one bordered table. "Execute" carries both the implementation checklist and
  // the guide text (same underlying fields as Advanced — presentational only).
  const renderBasicView = () => (
    <View style={styles.basicTable}>
<GlassFill />
      {ORDER_SECTIONS.map((section, sectionIdx) => {
        const open = isOpenDefaultTrue(`basic_${section.key}`);
        return (
          <View key={section.key} style={[sectionIdx > 0 ? styles.basicSectionDivider : null, { backgroundColor: section.tint }]}>
            <Pressable
              onPress={() => toggleBasicSection(`basic_${section.key}`)}
              style={styles.basicSectionBar}
            >
              <View style={styles.basicSectionTitleRow}>
                <Text style={[styles.basicSectionBarText, { color: section.textColor }]}>{section.label}</Text>
                {SECTION_INFO[section.key] ? (
                  <InfoIcon onPress={() => openInfo(section.key)} tinted />
                ) : null}
              </View>
              <View style={styles.basicBarRight}>
                {section.checkField && section.key !== 'directions' && isOpenDefaultTrue(`feat_${section.key}_basic`) ? (
                  <Pressable onPress={() => addCheckItem(section.checkField!)} hitSlop={8} style={[styles.basicAddBtn, { borderColor: section.rim }]}>
                    <Text style={[styles.basicAddBtnText, { color: section.accent }]}>+</Text>
                  </Pressable>
                ) : null}
                {open ? (
                  <Pressable
                    onPress={() => setFeatMenuOpen((c) => (c === section.key ? null : section.key))}
                    hitSlop={8}
                    style={styles.basicMoreBtn}
                  >
                    <Text style={[styles.basicMoreText, { color: section.accent }]}>⋯</Text>
                  </Pressable>
                ) : null}
                <Text style={[styles.basicArrow, { color: section.accent }, open ? styles.basicArrowOpen : null]}>◀</Text>
              </View>
            </Pressable>
            {open ? (
              <View style={styles.basicSectionBody}>
                {featMenuOpen === section.key ? renderFeatureMenu(section.key) : null}
                {isOpenDefaultTrue(`feat_${section.key}_basic`) ? (
                  <View style={styles.basicBodyBlock}>
                    <SubHeaderBar
                      title="Basic"
                      tint={stageShade(SECTION_KEY_BY_ORDER[section.key], 0)}
                      isOpen={isOpenDefaultTrue(`basicBody_${section.key}`)}
                      onToggle={() => toggleSection(`basicBody_${section.key}`)}
                      onInfoPress={() => openInfo('basic', `Basic — ${section.label}`)}
                    />
                    {isOpenDefaultTrue(`basicBody_${section.key}`) ? (
                      <View style={styles.advancedBody}>
                        {section.key === 'orient' ? (
                          <>
                            <View style={styles.fieldBlock}>
                              <Text style={styles.fieldLabel}>Sector</Text>
                              <SectorPicker value={row.sector} onSelect={setSector} />
                            </View>
                            <LabeledInput
                              label="Drift gap (days)"
                              value={String(row.drift_gap_days)}
                              onChangeText={(v) => setField('drift_gap_days', v ? parseInt(v, 10) || 0 : 0)}
                              placeholder="2"
                              keyboardType="number-pad"
                            />
                          </>
                        ) : null}
                        {section.key === 'directions' ? renderDirectionsSteps() : null}
                        {section.checkField && section.key !== 'directions' ? (
                          row[section.checkField].length > 0 ? (
                            row[section.checkField].map((item, i) => (
                              <CheckRow
                                key={item.id}
                                status={effectiveStatus(item)}
                                text={item.text}
                                index={i}
                                placeholder={section.itemPlaceholder}
                                onStatusChange={(st) => updateCheckItem(section.checkField!, item.id, { status: st, checked: st === 'C' })}
                                onChangeText={(v) => updateCheckItem(section.checkField!, item.id, { text: v })}
                                onRemove={() => removeCheckItem(section.checkField!, item.id)}
                                onMove={(dir) => moveCheckItem(section.checkField!, item.id, dir)}
                                canMoveUp={i > 0}
                                canMoveDown={i < row[section.checkField!].length - 1}
                                substepOptions={section.key === 'execute' ? substepOptions : undefined}
                                hasNotes={!!item.notes}
                                onPressIndex={() => setNotesFor({ field: section.checkField!, path: [item.id], label: `${section.label} ${i + 1}` })}
                              />
                            ))
                          ) : (
                            <Text style={styles.basicEmptyHint}>{section.emptyHint}</Text>
                          )
                        ) : null}
                        {section.textField ? (
                          <TextInput
                            style={[styles.fieldInput, styles.fieldInputMultiline]}
                            value={row[section.textField]}
                            onChangeText={(v) => setField(section.textField!, v)}
                            placeholder={section.textPlaceholder}
                            placeholderTextColor={c.slate}
                            multiline
                          />
                        ) : null}
                      </View>
                    ) : null}
                  </View>
                ) : null}
                {(ADVANCED_FEATURES[section.key] ?? []).map((f) =>
                  isOpenStrict(`feat_${section.key}_${f.key}`) ? (
                    <View key={f.key} style={styles.featBlock}>{f.render()}</View>
                  ) : null,
                )}
              </View>
            ) : null}
          </View>
        );
      })}
    </View>
  );


  return (
    <Atmos>
    <ScrollView ref={detailScrollRef} style={styles.root} contentContainerStyle={styles.detailScroll}>
      <View style={styles.detailTopBar}>
        <SmallButton label="‹ Back" onPress={goBack} />
        <TextInput style={styles.titleInput} value={row.project_title} onChangeText={(v) => setField('project_title', v)} placeholder="New Project" placeholderTextColor={c.slate} />
      </View>
      <View style={styles.detailTopBar}>
        <Pressable
          onPress={() => toggleSection('tkne_shown')}
          style={[styles.tkneToggle, isOpenStrict('tkne_shown') ? styles.tkneToggleActive : null]}
        >
          <Text style={[styles.tkneToggleText, isOpenStrict('tkne_shown') ? styles.tkneToggleTextActive : null]}>TKNE</Text>
        </Pressable>
        <View style={styles.exportGroup}>
          {exporting ? <ActivityIndicator color={c.blueText} style={styles.exportSpinner} /> : null}
          <SmallButton label="Export" onPress={exportOrder} />
          <SmallButton label="Delete Order" danger onPress={() => deleteDesign(row)} />
        </View>
      </View>

      {error ? <Text style={styles.error}>{error}</Text> : null}

      {isOpenStrict('tkne_shown') ? (
        <TknePanel designId={row.id} viewMode={row.view_mode} onApplied={reloadRow} />
      ) : null}

      <View style={styles.pipelineCard}>
        <ProjectPipeline
          project={row}
          onOpen={() => {}}
          onOpenStage={(stepId) => focusDirectionsStep(stepId)}
          onOpenSubstep={(stepId, subId) => focusDirectionsStep(stepId, subId)}
        />
        <Text style={styles.statusLabel}>Status</Text>
        {statusEditorFailed ? (
          <TextInput
            style={[styles.fieldInput, styles.fieldInputMultiline, { height: statusHeight }]}
            value={row.status_update}
            onChangeText={(v) => setField('status_update', v)}
            placeholder="Where things stand right now…"
            placeholderTextColor={c.slate}
            multiline
          />
        ) : (
          <RichTextEditor
            key={row.id}
            value={row.status_update}
            onChange={(html) => setField('status_update', html)}
            onError={() => setStatusEditorFailed(true)}
            placeholder="Where things stand right now…"
            style={{ height: statusHeight, minHeight: 0 }}
            pointerEventsNone={statusResizing}
          />
        )}
        <View style={styles.statusResizeHandle} {...statusResizer.panHandlers}>
          <View style={styles.statusResizeGrip} />
          <View style={styles.statusResizeGrip} />
        </View>
      </View>

      {renderBasicView()}

      <DueDateModal
        visible={dueDateFor !== null}
        initial={dueDateForTarget()?.dueDate}
        onSelect={(ymd) => setDueDateOnTarget({ dueDate: ymd })}
        onClear={() => setDueDateOnTarget({ dueDate: undefined })}
        onClose={() => setDueDateFor(null)}
      />

      <NotesPopup
        visible={notesFor !== null}
        title={`Notes — ${notesFor?.label ?? ''}`}
        value={notesTarget()?.notes ?? ''}
        onChangeText={setNotesOnTarget}
        onClose={() => setNotesFor(null)}
      />

      <InfoPopup info={activeInfo} onClose={closeInfo} />
    </ScrollView>
    </Atmos>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------


const useStyles = makeThemedStyles(({ t, c, neu }) =>
  StyleSheet.create({
  root: { flex: 1, padding: 12 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  inputRow: { flexDirection: 'row', gap: 8, marginBottom: 10 },
  input: {
    flex: 1,
    backgroundColor: c.inputFill,
    borderRadius: radii.tile,
    borderWidth: 1.5,
    borderColor: c.inputRim,
    paddingHorizontal: 12,
    paddingVertical: 9,
    fontSize: 15,
    color: c.text,
  },
  addButton: {
    backgroundColor: c.lime,
    borderRadius: radii.tile,
    paddingHorizontal: 16,
    justifyContent: 'center',
  },
  addText: { fontWeight: '900', color: c.blueText },
  // Flat project list: one "+ Add project" button on top, then a compact button
  // per project (no groups / bubbles / inactive toggles).
  addProjectBtn: {
    backgroundColor: c.lime, borderRadius: radii.tile,
    paddingVertical: 12, alignItems: 'center', marginBottom: 12,
  },
  addProjectText: { fontWeight: '900', fontSize: 15, color: c.blueText },
  // One project pipeline card per row, stacked vertically (each is its own
  // hexagon-title + stage-circle chain — see ProjectPipeline).
  listTopBar: { flexDirection: 'row', gap: 10, marginBottom: 12 },
  listTopBtn: { flex: 1 },
  groupAddRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 10 },
  groupAddInput: { flex: 1, backgroundColor: t.navyRaised, borderWidth: 1, borderColor: c.slate },
  groupScrollV: { flex: 1 },
  groupColumns: { flexDirection: 'row', alignItems: 'flex-start', gap: 28, paddingVertical: 8, paddingHorizontal: 4 },
  groupColumn: { alignItems: 'flex-start', gap: 14, minWidth: 160, padding: 14, borderRadius: radii.panel, overflow: 'hidden', borderWidth: 1, borderColor: 'rgba(255,255,255,0.22)', boxShadow: `0 10px 30px ${t.glowSoft}` },
  groupAddProject: { paddingVertical: 4 },
  groupAddProjectText: { fontSize: 12, fontWeight: '800' },
  newProjectForm: { gap: 10, marginBottom: 12, padding: 14, borderRadius: radii.panel, overflow: 'hidden', borderWidth: 1, borderColor: t.navyLine, boxShadow: `0 10px 30px ${t.glowSoft}` },
  groupChipRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 6 },
  groupChipLabel: { fontSize: 12, fontWeight: '800', color: c.slate },
  groupChip: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 999, borderWidth: 2 },
  groupChipText: { fontSize: 12, fontWeight: '800' },
  groupHeader: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 6, paddingHorizontal: 12, maxWidth: 220, borderRadius: radii.pill, backgroundColor: t.navyRaised, borderWidth: 1, borderColor: t.navyLine },
  groupHeaderDots: { fontSize: 16, fontWeight: '800', color: t.ivory },
  groupRenameInput: { width: 180, backgroundColor: t.navyRaised, borderWidth: 1, borderColor: c.slate },
  groupEmpty: { fontSize: 11, fontStyle: 'italic', maxWidth: 140, opacity: 0.85 },
  error: { color: c.danger, marginBottom: 8, fontSize: 13 },
  list: { gap: 8, paddingBottom: 8 },
  empty: { color: c.slate, fontStyle: 'italic', textAlign: 'center', marginTop: 24 },
  hint: { fontSize: 11, color: c.slate, textAlign: 'center', marginTop: 6 },

  groupCreateRow: { flexDirection: 'row', gap: 8, marginBottom: 10 },
  groupCreateInput: {
    flex: 1,
    backgroundColor: c.inputFill,
    borderRadius: radii.tile,
    borderWidth: 1.5,
    borderColor: c.inputRim,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 13,
    color: c.text,
  },
  groupCreateButton: {
    backgroundColor: c.glassFill,
    borderRadius: radii.tile,
    paddingHorizontal: 14,
    justifyContent: 'center',
    ...neu.raisedSm,
  },
  groupCreateButtonText: { fontWeight: '800', fontSize: 12, color: c.blueText },
  groupList: { gap: 14, paddingBottom: 8 },
  groupSection: { gap: 8 },
  groupHeaderRow: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    backgroundColor: t.glass, borderWidth: 1, borderColor: t.lineFaint, paddingHorizontal: 12, paddingVertical: 8, borderRadius: radii.tile,
  },
  groupHeaderText: { fontFamily: DISPLAY_FONT, color: t.ivory, fontSize: 13, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 0.3 },
  groupHeaderToggle: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  groupHeaderToggleLabel: { color: t.ivorySoft, fontSize: 11 },
  groupHeaderSwitch: { transform: [{ scale: 0.8 }] },

  card: {
    backgroundColor: c.glassFill,
    borderRadius: radii.panel,
    padding: 12,
    gap: 4,
    ...neu.raised,
  },
  cardTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  cardTitle: { fontSize: 15, fontWeight: '700', color: c.text, flex: 1 },
  cardStatus: { fontSize: 12, color: c.slate },
  dot: { width: 12, height: 12, borderRadius: 6 },
  dotActive: { backgroundColor: t.onPace },
  dotInactive: { backgroundColor: c.slate },

  detailScroll: { paddingBottom: 40, gap: 10 },
  detailTopBar: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' },
  exportGroup: { flexDirection: 'row', alignItems: 'center', gap: 8, marginLeft: 'auto' },
  exportSpinner: { marginRight: 2 },
  titleInput: {
    flex: 1,
    fontSize: 18,
    fontWeight: '700',
    color: c.text,
    backgroundColor: c.inputFill,
    borderRadius: 14,
    paddingHorizontal: 10,
    paddingVertical: 7,
    ...neu.insetDeep,
  },
  modeToggle: {
    flexDirection: 'row',
    backgroundColor: c.glassFill,
    borderRadius: radii.pill,
    padding: 3,
    ...neu.raisedSm,
  },
  modeButton: { paddingHorizontal: 14, paddingVertical: 6, borderRadius: radii.pill },
  modeButtonActive: { backgroundColor: c.primary },
  modeButtonText: { fontSize: 13, fontWeight: '600', color: c.slate },
  modeButtonTextActive: { color: t.onGold },

  // TKNE AI toggle — sits right after the Basic/Advanced switch.
  tkneToggle: {
    paddingHorizontal: 14, paddingVertical: 8, borderRadius: radii.pill,
    backgroundColor: c.glassFill, ...neu.raisedSm,
  },
  tkneToggleActive: { backgroundColor: c.primary, ...neu.inset },
  tkneToggleText: { fontSize: 13, fontWeight: '900', letterSpacing: 0.5, color: c.primary },
  tkneToggleTextActive: { color: t.onGold },

  // Segmented tab bar — one rounded container; each segment carries its ORDER
  // stage color (set inline), inactive ones dimmed via opacity.
  sectionTabsBar: {
    flexDirection: 'row',
    borderRadius: radii.pill,
    overflow: 'hidden',
    marginBottom: 8,
    backgroundColor: c.glassFill,
    ...neu.raised,
  },
  sectionSeg: { flex: 1, paddingVertical: 10, paddingHorizontal: 2, alignItems: 'center', justifyContent: 'center' },
  sectionSegDivider: { borderLeftWidth: StyleSheet.hairlineWidth, borderLeftColor: t.navyLine },
  sectionSegText: { color: c.ink, fontSize: 11, fontWeight: '700' },
  sectionSegTextActive: { color: c.ink, fontWeight: '900' },

  sectionHeaderBar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    backgroundColor: t.glass, borderWidth: 1, borderColor: t.lineFaint, paddingHorizontal: 14, paddingVertical: 10, borderRadius: radii.tile,
    marginTop: 4,
  },
  sectionHeaderTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 8, flexShrink: 1 },
  sectionHeaderText: { color: t.ivory, fontSize: 15, fontWeight: '700' },

  // "i"-in-a-circle info icon, dropped next to a section/sub-section title.
  // Own Pressable + stopPropagation so it never triggers the bar's toggle.
  infoIconBtn: {
    width: 18, height: 18, borderRadius: 9, alignItems: 'center', justifyContent: 'center',
    borderWidth: 1, borderColor: t.navyLine,
  },
  infoIconBtnTinted: { borderColor: t.lineFaint },
  infoIconText: { color: t.ivory, fontSize: 11, fontWeight: '900', lineHeight: 13, fontStyle: 'italic' },
  infoIconTextTinted: { color: c.ink },

  // Info popup — content only, no modal chrome; a floating "×" pill closes it.
  infoBackdrop: { flex: 1, backgroundColor: c.overlay, alignItems: 'center', justifyContent: 'center', padding: 24 },
  infoCard: {
    width: '100%', maxWidth: 340, backgroundColor: t.navyRaised, borderRadius: radii.panel,
    padding: 18, paddingTop: 30, gap: 8, ...neu.raisedLg,
    overflow: 'hidden', borderWidth: 1, borderColor: t.navyLine, boxShadow: `0 10px 30px ${t.glowSoft}`,
  },
  infoCloseBtn: {
    position: 'absolute', top: 8, right: 8, width: 26, height: 26, borderRadius: 13,
    alignItems: 'center', justifyContent: 'center', backgroundColor: c.glassFill, ...neu.raisedSm,
  },
  infoCloseText: { fontSize: 16, fontWeight: '900', color: c.blueText, lineHeight: 18 },
  infoCardTitle: { fontSize: 16, fontWeight: '800', color: c.ink },
  infoCardBody: { fontSize: 13, lineHeight: 19, color: c.text },

  // One rounded table per Advanced section; the tinted SubHeaderBar rows sit
  // flush inside it (like the Basic-view ORDER table).
  advancedTable: {
    borderRadius: radii.panel, marginTop: 4,
    overflow: 'hidden', borderWidth: 1, borderColor: t.navyLine, boxShadow: `0 10px 30px ${t.glowSoft}`,
  },
  subHeaderBar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    backgroundColor: t.glass, borderWidth: 1, borderColor: t.lineFaint, paddingHorizontal: 14, paddingVertical: 12,
  },
  subHeaderTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 8, flexShrink: 1 },
  subHeaderText: { color: t.ivory, fontSize: 14, fontWeight: '800', letterSpacing: 0.3 },
  subHeaderTextTinted: { color: c.ink },
  subHeaderCaret: { fontSize: 14, fontWeight: '900', color: t.ivory },
  subHeaderCaretOpen: { transform: [{ rotate: '-90deg' }] },

  panel: {
    backgroundColor: c.glassFill,
    borderRadius: radii.panel,
    padding: 12,
    gap: 10,
    ...neu.raised,
  },
  // Expanded content INSIDE an advancedTable — flush (no rounding/border/shadow)
  // so it doesn't break the table's edges. Light translucent fill, hairline top.
  advancedBody: {
    backgroundColor: c.glassFill,
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 10,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: t.lineFaint,
  },
  subPanel: {
    backgroundColor: c.glassFill,
    borderRadius: radii.tile,
    padding: 10,
    gap: 8,
    marginTop: -1,
    ...neu.raisedSm,
  },

  fieldBlock: { gap: 4 },
  labelRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  fieldLabel: { fontSize: 12, fontWeight: '800', color: c.blueText, letterSpacing: 0.3 },
  // Metis-Order section header: big black word + normal-weight description.
  orderHeaderText: { marginBottom: 2 },
  orderWord: { fontSize: 20, fontWeight: '900', color: t.ivory },
  orderRest: { fontSize: 13, fontWeight: '600', color: c.slate },
  orderIndent: { marginLeft: 22 },
  // Step / sub-step dropdown box in the Metis-Order Directions field.
  stepDdBox: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: c.inputFill, borderRadius: 12, paddingHorizontal: 10, paddingVertical: 7,
    minWidth: 110, maxWidth: 180, ...neu.insetDeep,
  },
  stepDdText: { flex: 1, fontSize: 13, fontWeight: '700', color: c.ink },
  fieldInput: {
    backgroundColor: c.inputFill,
    borderRadius: 14,
    paddingHorizontal: 10,
    paddingVertical: 8,
    fontSize: 14,
    color: c.text,
    ...neu.insetDeep,
  },
  fieldInputMultiline: { minHeight: 60, textAlignVertical: 'top' },
  smallGap: { marginTop: 6 },
  mutedText: { fontSize: 12, color: c.slate, fontStyle: 'italic' },

  objectiveRow: { backgroundColor: c.glassFill, borderRadius: 8, padding: 8, gap: 4, marginBottom: 6, ...neu.raisedSm },
  objectiveHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },

  checkRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 4 },
  // Directions step rendered as one status-colored cell (the whole row is filled
  // with the current P/S/C color; the text input inside is transparent).
  stepCell: { borderWidth: 1, borderRadius: radii.tile, paddingHorizontal: 10, paddingVertical: 8, marginVertical: 2 },
  // Deep-link landing flash — a step/sub-step opened from a ProjectPipeline tap.
  stepCellHighlighted: { borderWidth: 3, borderColor: c.blueText },
  stepCellInput: { flex: 1, fontSize: 18, fontWeight: '800', color: t.ivory, paddingVertical: 2, paddingHorizontal: 2 },
  stepIndexLabel: { fontSize: 14, fontWeight: '700', fontStyle: 'italic', color: c.blueText, letterSpacing: 0.4 },
  checkbox: {
    width: 20, height: 20, borderRadius: 5,
    alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden', borderWidth: 1, borderColor: t.navyLine, boxShadow: `0 10px 30px ${t.glowSoft}`,
  },

  // Nested sub-steps under a Directions step — indented, lighter, smaller.
  substepGroup: { paddingLeft: 30, gap: 6, marginTop: 6 },
  substepRow: { flexDirection: 'row', alignItems: 'center', gap: 8, borderRadius: radii.tile },
  substepRowHighlighted: { borderWidth: 2, borderColor: c.blueText, backgroundColor: t.goldGlow },
  substepCheckbox: { width: 16, height: 16, borderRadius: 4, borderWidth: 1.5 },
  substepIndex: { fontSize: 11, color: c.slate, minWidth: 24, fontVariant: ['tabular-nums'] },
  substepAddBtn: { alignSelf: 'flex-start', paddingVertical: 3, paddingHorizontal: 2 },
  substepAddText: { fontSize: 12, fontWeight: '700', color: c.blueText },

  // Directions-step P/S/C status selector (Modal-hosted anchored dropdown).
  statusBox: {
    flexDirection: 'row', alignItems: 'center', gap: 2,
    paddingHorizontal: 7, paddingVertical: 4, borderRadius: 6, minWidth: 34, justifyContent: 'center',
  },
  statusBoxText: { fontSize: 13, fontWeight: '900', color: c.white },
  statusBoxCaret: { fontSize: 9, fontWeight: '900', color: 'rgba(255,255,255,0.85)' },
  // Transparent full-screen layer that catches tap-outside to close.
  statusOverlay: { flex: 1 },
  // The menu, absolutely placed under the box via measured coordinates.
  statusDropdown: {
    position: 'absolute',
    minWidth: 172, ...neu.raised,
    overflow: 'hidden', borderWidth: 1, borderColor: t.navyLine, boxShadow: `0 10px 30px ${t.glowSoft}`,
  },
  statusOption: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 12, paddingVertical: 9 },
  statusOptionActive: { backgroundColor: t.lineFaint },
  statusDot: { width: 22, height: 22, borderRadius: 6, alignItems: 'center', justifyContent: 'center' },
  statusDotText: { fontSize: 12, fontWeight: '900', color: c.white },
  statusOptionText: { fontSize: 14, fontWeight: '700', color: c.ink },

  // "List" picker (Directions section → KYRS task-list sync target).
  listBox: {
    flexDirection: 'row', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
    paddingHorizontal: 12, paddingVertical: 7, borderRadius: radii.pill,
    backgroundColor: t.navyRaised, ...neu.raisedSm,
    marginBottom: 10,
  },
  listBoxText: { fontSize: 13, fontWeight: '700', color: c.ink },
  listBoxCaret: { fontSize: 9, fontWeight: '900', color: c.slate },
  listDropdown: {
    position: 'absolute',
    minWidth: 200, ...neu.raised,
    overflow: 'hidden', borderWidth: 1, borderColor: t.navyLine, boxShadow: `0 10px 30px ${t.glowSoft}`,
  },
  listOption: { paddingHorizontal: 12, paddingVertical: 9 },
  listOptionText: { fontSize: 14, fontWeight: '700', color: c.ink },
  listOptionAddText: { fontSize: 14, fontWeight: '700', color: c.blueText },

  // Directions-step due-date chip + reorder controls.
  dueChip: {
    paddingHorizontal: 8, paddingVertical: 4, borderRadius: radii.pill,
    backgroundColor: c.glassFill, ...neu.raisedSm,
    minWidth: 42, alignItems: 'center',
  },
  statusResizeHandle: {
    alignSelf: 'center', marginTop: 2, paddingVertical: 7, paddingHorizontal: 26,
    alignItems: 'center', justifyContent: 'center', gap: 3,
    // Web-only affordance; ignored on native.
    cursor: 'ns-resize' as any,
  },
  statusResizeGrip: { width: 30, height: 3, borderRadius: 2, backgroundColor: c.inputRim },
  pipelineCard: { marginBottom: 16 },
  statusLabel: {
    fontFamily: DISPLAY_FONT, fontSize: 13, fontWeight: '800',
    textTransform: 'uppercase', letterSpacing: 0.5,
    // ProjectPipeline already carries a 16px bottom margin (the list screen
    // relies on it), so no marginTop here or the gap doubles.
    color: c.blueText, marginBottom: 6, marginLeft: 2,
  },
  outlineNumNoted: { fontWeight: '800', textDecorationLine: 'underline' },
  notesInput: {
    // neu.insetDeep supplies borderWidth/borderColor — spread it FIRST so the
    // explicit rim below wins instead of being silently overwritten.
    ...neu.insetDeep,
    backgroundColor: c.inputFill, borderRadius: radii.tile,
    borderWidth: 1.5, borderColor: c.inputRim,
    paddingHorizontal: 12, paddingVertical: 10, minHeight: 140,
    fontSize: 14, color: c.blueText, textAlignVertical: 'top',
  },
  substepAddCircle: {
    width: 22, height: 22, borderRadius: 11,
    alignItems: 'center', justifyContent: 'center',
    backgroundColor: t.navyRaised,
    ...neu.raisedSm,
  },
  substepAddCircleText: { fontSize: 15, lineHeight: 17, fontWeight: '800', color: c.blueText },
  pickerWrap: { flex: 1, position: 'relative', justifyContent: 'center' },
  pickerField: { flex: 1, paddingLeft: 40 },
  // Wide, full-height hit area — the caret was a ~13px glyph and near-impossible
  // to hit; the button now spans the whole left gutter of the field.
  pickerCaretBtn: {
    position: 'absolute', left: 0, top: 0, bottom: 0, width: 38,
    alignItems: 'center', justifyContent: 'center',
  },
  pickerCaretText: { fontSize: 26, lineHeight: 28, fontWeight: '800', color: c.slate },
  pickerDropdown: {
    position: 'absolute', backgroundColor: t.navyRaised, borderRadius: radii.tile,
    paddingVertical: 4, minWidth: 220, ...neu.raisedLg,
    overflow: 'hidden', borderWidth: 1, borderColor: t.navyLine, boxShadow: `0 10px 30px ${t.glowSoft}`,
  },
  pickerScroll: { flexGrow: 0 },
  pickerOption: { paddingHorizontal: 12, paddingVertical: 9 },
  pickerOptionText: { fontSize: 13, color: c.blueText },
  pickerOptionOwn: { fontSize: 13, fontWeight: '800', color: c.slate },
  pickerEmpty: { fontSize: 12, color: c.slate, paddingHorizontal: 12, paddingVertical: 9, fontStyle: 'italic' },
  dueChipSm: { paddingHorizontal: 7, paddingVertical: 2 },
  dueChipSet: { backgroundColor: c.lime, borderColor: c.blue },
  dueChipText: { fontSize: 11, fontWeight: '700', color: c.slate },
  dueChipTextSet: { color: c.blueText, fontWeight: '800' },
  reorderCol: { justifyContent: 'center' },
  reorderBtn: { paddingHorizontal: 2, paddingVertical: 1 },
  reorderText: { fontSize: 11, color: c.blueText, fontWeight: '900', lineHeight: 13 },
  reorderTextDisabled: { color: t.lineFaint },

  // Due-date calendar modal.
  dueBackdrop: { flex: 1, backgroundColor: c.overlay, alignItems: 'center', justifyContent: 'center', padding: 24 },
  dueCard: {
    width: '100%', maxWidth: 320, backgroundColor: t.navyRaised, borderRadius: radii.panel,
    padding: 16, gap: 10, ...neu.raisedLg,
    overflow: 'hidden', borderWidth: 1, borderColor: t.navyLine, boxShadow: `0 10px 30px ${t.glowSoft}`,
  },
  calHeaderRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  calNavBtn: { width: 30, height: 30, borderRadius: 15, alignItems: 'center', justifyContent: 'center', backgroundColor: c.glassFill, ...neu.raisedSm },
  calNavText: { fontSize: 18, fontWeight: '900', color: c.blueText },
  calMonthLabel: { fontSize: 14, fontWeight: '800', color: c.ink },
  calWeekdayRow: { flexDirection: 'row' },
  calWeekdayText: { flex: 1, textAlign: 'center', fontSize: 10, fontWeight: '700', color: c.slate },
  calGrid: { flexDirection: 'row', flexWrap: 'wrap' },
  calCell: { width: `${100 / 7}%`, aspectRatio: 1, alignItems: 'center', justifyContent: 'center' },
  calDayCircle: { width: 30, height: 30, borderRadius: 15, alignItems: 'center', justifyContent: 'center' },
  calDayToday: { borderWidth: 1, borderColor: c.blue },
  calDaySelected: { backgroundColor: c.blue },
  calDayText: { fontSize: 12, fontWeight: '600', color: c.text },
  calDayTextSelected: { color: t.onGold, fontWeight: '900' },
  dueFooter: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 4 },
  dueClearText: { fontSize: 13, fontWeight: '700', color: c.danger },
  dueDoneText: { fontSize: 13, fontWeight: '800', color: c.blueText },
  checkboxChecked: { backgroundColor: c.lime, borderColor: c.blue },
  checkboxMark: { fontSize: 12, fontWeight: '900', color: c.blueText },
  indexLabel: { fontSize: 12, color: c.slate, minWidth: 20 },
  checkRowInput: { flex: 1, fontSize: 14, color: c.text, backgroundColor: c.inputFill, borderRadius: 14, paddingHorizontal: 8, paddingVertical: 6, ...neu.insetDeep },
  removeButton: { paddingHorizontal: 4 },
  removeMark: { fontSize: 18, color: c.danger, fontWeight: '700' },

  constraintRow: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 4 },
  constraintText: { flex: 1, fontSize: 14, color: c.text, backgroundColor: c.inputFill, borderRadius: 14, paddingHorizontal: 8, paddingVertical: 6, ...neu.insetDeep },
  constraintStage: {
    width: 110, fontSize: 12, color: c.text, backgroundColor: c.inputFill,
    borderRadius: 14, paddingHorizontal: 6, paddingVertical: 6, ...neu.insetDeep,
  },

  smallButton: {
    backgroundColor: c.glassFill, borderRadius: 8, paddingHorizontal: 10, paddingVertical: 6, ...neu.raisedSm,
  },
  smallButtonText: { fontSize: 12, fontWeight: '700', color: c.blueText },
  dangerText: { color: c.danger },

  chipRow: { flexDirection: 'row', gap: 6, flexWrap: 'wrap', paddingVertical: 4 },
  chip: {
    backgroundColor: c.glassFill, borderRadius: radii.pill, paddingHorizontal: 12, paddingVertical: 6, ...neu.raisedSm,
  },
  chipSelected: { backgroundColor: c.primary, ...neu.inset },
  chipText: { fontSize: 12, color: c.text, fontWeight: '600' },
  chipTextSelected: { color: c.white },

  sectorChip: {
    backgroundColor: c.glassFill, borderRadius: radii.pill, paddingHorizontal: 12, paddingVertical: 6, ...neu.raisedSm,
  },
  sectorChipSelectedNone: { backgroundColor: c.slate },
  sectorChipText: { fontSize: 12, color: c.text, fontWeight: '600' },
  sectorTag: {
    borderRadius: radii.pill, paddingHorizontal: 8, paddingVertical: 2, marginLeft: 4,
  },
  sectorTagText: { fontSize: 10, fontWeight: '700', color: c.white },

  stepPill: {
    width: 96, borderRadius: 8, paddingHorizontal: 8, paddingVertical: 8,
    fontSize: 13, fontWeight: '800', color: c.white,
  },
  stepPillStart: { backgroundColor: c.inkSoft },
  stepPillEnd: { backgroundColor: c.ink },
  stepPillStep: { backgroundColor: t.lapsed },
  stepPillChecked: { opacity: 0.6 },
  forecastStepBlock: { marginBottom: 14, gap: 6 },
  forecastStepHeaderRow: { flexDirection: 'row', gap: 8, alignItems: 'flex-start' },
  stepDescription: {
    flex: 1, backgroundColor: c.inputFill,
    borderRadius: 14, paddingHorizontal: 10, paddingVertical: 8,
    fontSize: 13, color: c.text, ...neu.insetDeep,
  },
  forecastActionsRow: { flexDirection: 'row', gap: 6, flexWrap: 'wrap', marginTop: 4 },
  propsBox: { backgroundColor: c.glassFill, borderRadius: 8, padding: 8, gap: 8, marginTop: 4, ...neu.raisedSm },

  pointsWrap: { marginLeft: 12, gap: 4 },
  pointRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 4 },
  bullet: { color: c.text, marginTop: 6 },
  pointLabel: {
    width: 70, fontSize: 13, fontWeight: '700', color: c.text,
    borderRadius: 14, paddingHorizontal: 8, paddingVertical: 4, ...neu.insetDeep,
    overflow: 'hidden', borderWidth: 1, borderColor: t.navyLine, boxShadow: `0 10px 30px ${t.glowSoft}`,
  },
  pointDescription: { flex: 1, fontSize: 13, color: c.text, backgroundColor: c.inputFill, borderRadius: 14, paddingHorizontal: 8, paddingVertical: 4, ...neu.insetDeep },

  concernBlock: { marginLeft: 12, marginTop: 6, gap: 6, borderLeftWidth: 2, borderLeftColor: '#facc15', paddingLeft: 8 },
  concernHeaderRow: { flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  concernPill: {
    backgroundColor: '#facc15', borderRadius: 6, paddingHorizontal: 8, paddingVertical: 6,
    fontSize: 12, fontWeight: '700', color: '#1f2937', minWidth: 90,
  },
  optionBlock: { marginLeft: 10, marginTop: 4, gap: 4 },
  optionHeaderRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  optionPill: {
    backgroundColor: c.card, borderRadius: 6, paddingHorizontal: 8, paddingVertical: 6,
    fontSize: 12, fontWeight: '600', color: '#1f2937', minWidth: 90,
  },
  optionAddRow: { flexDirection: 'row', gap: 6, marginLeft: 6, marginTop: 2 },
  reRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginLeft: 10, marginTop: 2 },
  rePill: { borderRadius: 6, paddingHorizontal: 8, paddingVertical: 4 },
  rewardPill: { backgroundColor: t.onPace },
  exposurePill: { backgroundColor: t.lapsed },
  rePillText: { color: c.white, fontSize: 11, fontWeight: '700' },
  reInput: { flex: 1, fontSize: 13, color: c.text, backgroundColor: c.inputFill, borderRadius: 14, paddingHorizontal: 8, paddingVertical: 4, ...neu.insetDeep },

  // Real gridded table: shared cell borders form the gridlines. Container draws
  // the top+left edge; each cell draws its right+bottom edge.
  tableGrid: {
    borderTopWidth: 1, borderLeftWidth: 1, borderColor: c.inputRim,
    borderRadius: 8, overflow: 'hidden',
  },
  tableHeaderRow: { flexDirection: 'row' },
  tableBodyRow: { flexDirection: 'row' },
  tableGutterCell: {
    width: 26, minHeight: 34, alignItems: 'center', justifyContent: 'center',
    borderRightWidth: 1, borderBottomWidth: 1, borderColor: c.inputRim,
    backgroundColor: c.glassFill,
  },
  tableHeaderCell: {
    width: 148, flexDirection: 'row', alignItems: 'center',
    backgroundColor: t.navyRaised, borderRightWidth: 1, borderBottomWidth: 1, borderColor: c.inputRim,
    paddingLeft: 6, paddingRight: 2,
  },
  tableHeaderInput: { flex: 1, color: t.ivory, fontWeight: '800', fontSize: 12, paddingVertical: 8 },
  columnDeleteBtn: { padding: 3 },
  columnDeleteMark: { color: '#ffd4d0', fontWeight: '900', fontSize: 13 },
  tableCellInput: {
    width: 148, minHeight: 34, fontSize: 13, color: c.text,
    backgroundColor: c.inputFill,
    borderRightWidth: 1, borderBottomWidth: 1, borderColor: c.inputRim,
    paddingHorizontal: 8, paddingVertical: 7,
  },
  rowDeleteMark: { color: c.danger, fontWeight: '900', fontSize: 12 },
  emptyTableWrap: { gap: 8, alignItems: 'flex-start' },
  tableActionsRow: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },

  taskRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  taskDateInput: { width: 100, fontSize: 12, color: c.text, backgroundColor: c.inputFill, borderRadius: 14, paddingHorizontal: 6, paddingVertical: 6, ...neu.insetDeep },
  taskTextInput: { flex: 1, fontSize: 13, color: c.text, backgroundColor: c.inputFill, borderRadius: 14, paddingHorizontal: 8, paddingVertical: 6, ...neu.insetDeep },
  taskBeforeAfterRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 2 },
  taskBeforeAfterInput: { flex: 1, fontSize: 12, color: c.text, backgroundColor: c.inputFill, borderRadius: 14, paddingHorizontal: 8, paddingVertical: 4, ...neu.insetDeep },

  stormHeaderRow: { flexDirection: 'row' },
  stormHeaderCell: { width: 110, backgroundColor: t.navyRaised, color: t.ivory, fontWeight: '700', fontSize: 11, padding: 6 },
  stormBodyRow: { flexDirection: 'row' },
  stormBodyCell: { width: 110, fontSize: 12, color: c.text, padding: 6, borderBottomWidth: 1, borderBottomColor: c.glassRim },

  // Linked KYRS task list (Task section inline list)
  taskRowSub: { marginLeft: 22 },
  taskCheckbox: {
    width: 22, height: 22, borderRadius: 6, borderWidth: 2, borderColor: c.blue,
    alignItems: 'center', justifyContent: 'center', backgroundColor: c.inputFill,
  },
  taskCheckboxOn: { backgroundColor: c.lime, borderColor: c.blue },
  taskCheckMark: { fontSize: 13, fontWeight: '900', color: c.blueText },
  taskTitleInput: { flex: 1, fontSize: 14, color: c.text, backgroundColor: c.inputFill, borderRadius: 12, paddingHorizontal: 10, paddingVertical: 7, ...neu.insetDeep },
  taskTitleDone: { color: c.slate, textDecorationLine: 'line-through' },
  taskRemove: { fontSize: 14, fontWeight: '800', color: c.slate, paddingHorizontal: 2 },
  taskAddRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 4 },
  taskAddInput: { flex: 1, fontSize: 14, color: c.text, backgroundColor: c.inputFill, borderRadius: 12, paddingHorizontal: 10, paddingVertical: 8, ...neu.insetDeep },


  guideNavBar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    backgroundColor: t.glass, borderWidth: 1, borderColor: t.lineFaint, paddingHorizontal: 12, paddingVertical: 8, borderRadius: radii.tile,
  },
  guideNavTitle: { color: t.ivory, fontWeight: '700', fontSize: 13, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  guideNavControls: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  navArrow: { color: t.ivory, fontSize: 18, fontWeight: '900', paddingHorizontal: 4 },
  guideDateInput: {
    width: 110, color: c.white, fontSize: 12, backgroundColor: t.lineFaint,
    borderRadius: 6, paddingHorizontal: 8, paddingVertical: 4,
  },
  pairRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  pairInput: {
    width: 56, textAlign: 'center', backgroundColor: c.inputFill, borderRadius: 14,
    paddingVertical: 6, fontSize: 14, color: c.text, ...neu.insetDeep,
  },
  implRow: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 3 },
  implTaskInput: { flex: 1, fontSize: 13, color: c.text, backgroundColor: c.inputFill, borderRadius: 14, paddingHorizontal: 8, paddingVertical: 4, ...neu.insetDeep },
  implTimeInput: { width: 70, fontSize: 12, color: c.text, backgroundColor: c.inputFill, borderRadius: 14, paddingHorizontal: 6, paddingVertical: 4, ...neu.insetDeep },

  // One frosted-glass frame; translucent color-tinted bands sit flush inside,
  // separated by 1pt dividers in the same glassStroke color as the frame border
  // (overflow:'hidden' clips the top/bottom bands to the rounded corners). Each
  // section collapses to its bar (accordion). The stage color survives as a
  // translucent wash, so the O-R-D-E-R coding reads without the old solid-paint
  // / thick-black-border look.
  basicTable: {
    borderRadius: radii.panel,
    overflow: 'hidden', borderWidth: 1, borderColor: t.navyLine, boxShadow: `0 10px 30px ${t.glowSoft}`,
  },
  basicSectionDivider: { borderTopWidth: 1, borderTopColor: c.glassStroke },
  basicSectionBar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 14, paddingVertical: 13, gap: 10,
  },
  basicSectionTitleRow: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8 },
  basicSectionBarText: { fontFamily: DISPLAY_FONT, fontSize: 16, fontWeight: '800', letterSpacing: 0.6, textTransform: 'uppercase' },
  basicBarRight: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  // Wraps the "Basic" SubHeaderBar + its fields inside each ORDER band body,
  // matching how advSubBlock nests its own SubHeaderBar + advancedBody.
  basicBodyBlock: { marginBottom: 4 },
  basicArrow: { fontSize: 16, fontWeight: '900' },
  basicArrowOpen: { transform: [{ rotate: '-90deg' }] },
  basicAddBtn: {
    width: 26, height: 26, borderRadius: 13, alignItems: 'center', justifyContent: 'center',
    backgroundColor: c.glassFill, ...neu.raisedSm,
  },
  basicAddBtnText: { fontSize: 18, fontWeight: '900', lineHeight: 20 },
  basicMoreBtn: { paddingHorizontal: 2, alignItems: 'center', justifyContent: 'center' },
  basicMoreText: { fontSize: 20, fontWeight: '900', lineHeight: 20 },
  basicSectionBody: {
    padding: 12, gap: 8, backgroundColor: c.glassFill,
    borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: t.lineFaint,
  },
  // "⋯" feature switch menu — a small card of on/off rows for the section.
  featMenu: {
    paddingHorizontal: 12, paddingVertical: 4, marginBottom: 4, ...neu.raisedSm,
    overflow: 'hidden', borderWidth: 1, borderColor: t.navyLine, boxShadow: `0 10px 30px ${t.glowSoft}`,
  },
  featMenuRow: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 6,
    borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: t.lineFaint,
  },
  featMenuLabel: { fontSize: 13, fontWeight: '600', color: c.text },
  featBlock: { marginTop: 4 },
  basicEmptyHint: { fontSize: 12, color: c.slate, fontStyle: 'italic', paddingVertical: 4 },
  basicStepRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 6 },
  basicStepInput: { flex: 1, fontSize: 14, color: c.text, backgroundColor: c.inputFill, borderRadius: 14, paddingHorizontal: 8, paddingVertical: 6, ...neu.insetDeep },

  // Logs (Revise → Logs): daily-grouped change/note feed.
  logDayHeader: { fontSize: 12, fontWeight: '800', color: c.blueText, marginTop: 10, marginBottom: 4 },
  logRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 8, paddingVertical: 4 },
  logTime: { fontSize: 11, color: c.slate, width: 40 },
  logTag: {
    fontSize: 10, fontWeight: '800', color: c.blueText, backgroundColor: c.inputFill,
    borderRadius: 8, paddingHorizontal: 6, paddingVertical: 1, overflow: 'hidden',
  },
  logText: { flex: 1, fontSize: 13, color: c.text },
}),
);
